<template>
  <v-container class="inventur-office-container">
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Inventur-Auswertung (Office)</span>
        <div>
          <v-btn color="primary" class="mr-2" @click="exportExcelEinzelliste">
            <v-icon left>mdi-file-excel</v-icon>
            Einzelauflistung exportieren
          </v-btn>
          <v-btn color="primary" class="mr-2" @click="exportExcelKompaktliste">
            <v-icon left>mdi-file-excel</v-icon>
            Kompaktliste exportieren
          </v-btn>
          <v-btn
            color="success"
            class="mr-2"
            @click="exportSap"
            :disabled="!selectedArchivId"
            title="Finaler Export der Zählwerte zur Übernahme nach SAP (mit SHA-256 Audit-Anker)"
          >
            <v-icon left>mdi-database-export</v-icon>
            SAP-Export
          </v-btn>
          <v-btn
            v-if="!isViewingArchiv"
            color="warning"
            variant="tonal"
            @click="openArchivDialog"
          >
            <v-icon left>mdi-archive-arrow-down</v-icon>
            Inventur archivieren
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Archiv-Selektor -->
        <div class="d-flex align-center mb-4" style="gap: 12px;">
          <v-icon :color="isViewingArchiv ? 'warning' : 'primary'">
            {{ isViewingArchiv ? 'mdi-archive-eye' : 'mdi-clipboard-list-outline' }}
          </v-icon>
          <v-select
            v-model="selectedArchivId"
            :items="archivOptions"
            item-title="title"
            item-value="value"
            label="Anzeige"
            density="comfortable"
            variant="outlined"
            hide-details
            style="max-width: 480px;"
          />
          <v-btn
            v-if="!isViewingArchiv"
            size="small"
            variant="tonal"
            color="primary"
            @click="openInventurNameDialog"
            :title="aktuelleMeta.name
              ? `Bezeichnung der laufenden Inventur ändern (aktuell: ${aktuelleMeta.name})`
              : 'Bezeichnung für die laufende Inventur vergeben'"
          >
            <v-icon start>mdi-rename-box</v-icon>
            {{ aktuelleMeta.name ? 'Umbenennen' : 'Bezeichnung vergeben' }}
          </v-btn>
          <v-btn
            v-if="isViewingArchiv"
            size="small"
            variant="text"
            @click="selectedArchivId = 0"
          >
            <v-icon start>mdi-close</v-icon>
            Zurück zur aktuellen Inventur
          </v-btn>
          <v-btn
            v-if="isViewingArchiv"
            size="small"
            color="warning"
            variant="tonal"
            @click="openKorrekturDialog"
          >
            <v-icon start>mdi-file-document-edit-outline</v-icon>
            Korrektur erfassen
          </v-btn>
        </div>

        <v-alert
          v-if="isViewingArchiv"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          icon="mdi-archive-eye"
        >
          <strong>Archiv-Ansicht:</strong> Sie betrachten einen archivierten Inventur-Lauf.
          Die Daten sind <u>schreibgeschützt</u>; Bearbeiten, Import und erneutes Archivieren
          sind in dieser Ansicht deaktiviert.
        </v-alert>
        <!-- Tabs: Einzelauflistung vs Kompaktliste -->
        <v-tabs v-model="activeTab" class="mb-6">
          <v-tab value="einzelliste">Einzelauflistung</v-tab>
          <v-tab value="kompaktliste">Kompaktliste</v-tab>
          <v-tab value="paletten">Paletten</v-tab>
          <v-tab v-if="!isViewingArchiv" value="import">Artikel-Import</v-tab>
          <v-tab v-if="!isViewingArchiv" value="ean-lookup">EAN-Lookup</v-tab>
          <v-tab v-if="isViewingArchiv" value="korrekturen">
            Korrekturen
            <v-chip v-if="korrekturen.length > 0" size="x-small" color="warning" class="ml-2">
              {{ korrekturen.length }}
            </v-chip>
          </v-tab>
          <v-tab value="log">Audit-Log</v-tab>
        </v-tabs>

        <!-- TAB: Einzelauflistung -->
        <v-window v-model="activeTab">
          <v-window-item value="einzelliste">
            <v-card variant="outlined" class="mb-4">
              <v-card-text>
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                  icon="mdi-information-outline"
                >
                  <strong>Hinweis:</strong> Es werden nur Paletten mit Status
                  <em>abgeschlossen</em> oder <em>archiv</em> angezeigt.
                  Paletten in laufender Erfassung
                  <span v-if="paletenInErfassungCount > 0">
                    (aktuell <strong>{{ paletenInErfassungCount }}</strong>)
                  </span>
                  sind hier <u>nicht</u> enthalten.
                </v-alert>
                <!-- Filter -->
                <v-row class="mb-4">
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="filterEinzelliste.suche"
                      label="Suche nach Paletten-Nr., CMMF oder Artikel"
                      prepend-icon="mdi-magnify"
                      @input="filterEinzelliste.suche = filterEinzelliste.suche"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="filterEinzelliste.onlyAbweichungen"
                      :items="[
                        { title: 'Alle Artikel', value: false },
                        { title: 'Nur Abweichungen', value: true }
                      ]"
                      label="Filter"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn-toggle
                      v-model="einzellisteAnsicht"
                      mandatory
                      density="compact"
                      color="primary"
                      variant="outlined"
                    >
                      <v-btn value="einzeln" size="small">
                        <v-icon start>mdi-format-list-bulleted</v-icon>
                        Einzeln
                      </v-btn>
                      <v-btn value="zusammengefasst" size="small">
                        <v-icon start>mdi-group</v-icon>
                        Zusammengefasst
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>

                <!-- Tabelle: Einzelauflistung -->
                <v-data-table
                  :headers="einzellisteAnsicht === 'zusammengefasst' ? headersEinzellisteAggregiert : headersEinzelliste"
                  :items="einzellisteAnsicht === 'zusammengefasst' ? aggregatedEinzelliste : filteredEinzelliste"
                  :loading="loading.einzelliste"
                  class="elevation-0"
                  density="compact"
                  fixed-header
                  height="600px"
                >
                  <template v-slot:[`item.paletten_nr`]="{ item }">
                    <a
                      href="#"
                      class="text-primary text-decoration-none font-weight-medium"
                      title="Palette öffnen"
                      @click.stop.prevent="openPaletteDetail({ id: item.palette_id, paletten_nr: item.paletten_nr })"
                    >
                      {{ item.paletten_nr }}
                    </a>
                  </template>
                  <template v-slot:[`item.paletten_nrs`]="{ item }">
                    <template v-if="item.paletten_links && item.paletten_links.length">
                      <template v-for="(p, idx) in item.paletten_links" :key="p.id || p.nr">
                        <a
                          href="#"
                          class="text-primary text-decoration-none font-weight-medium"
                          title="Palette öffnen"
                          @click.stop.prevent="openPaletteDetail({ id: p.id, paletten_nr: p.nr })"
                        >{{ p.nr }}</a><span v-if="idx < item.paletten_links.length - 1">, </span>
                      </template>
                    </template>
                    <span v-else>{{ item.paletten_nrs || '–' }}</span>
                  </template>
                  <template v-slot:[`item.scan_zeitpunkt`]="{ item }">
                    {{ item.scan_zeitpunkt ? formatDateTime(item.scan_zeitpunkt) : '–' }}
                  </template>
                  <template v-slot:[`item.kumulative_differenz`]="{ item }">
                    <v-chip
                      :color="item.kumulative_differenz === 0 ? 'green' : item.kumulative_differenz > 0 ? 'orange' : 'red'"
                      :text-color="item.kumulative_differenz === 0 ? 'white' : 'white'"
                    >
                      {{ item.kumulative_differenz > 0 ? '+' : '' }}{{ item.kumulative_differenz }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: Kompaktliste -->
          <v-window-item value="kompaktliste">
            <v-card variant="outlined" class="mb-4">
              <v-card-text>
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                  icon="mdi-information-outline"
                >
                  <strong>Hinweis:</strong> In dieser Auswertung sind nur Paletten mit Status
                  <em>abgeschlossen</em> oder <em>archiv</em> berücksichtigt.
                  Paletten in laufender Erfassung
                  <span v-if="paletenInErfassungCount > 0">
                    (aktuell <strong>{{ paletenInErfassungCount }}</strong>)
                  </span>
                  werden bewusst <u>nicht</u> in IST/Differenz eingerechnet, um Inventur-Zwischenstände nicht zu verfälschen.
                </v-alert>
                <!-- Filter -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="filterKompaktliste.suche"
                      label="Suche nach CMMF, EAN oder Artikel"
                      prepend-icon="mdi-magnify"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="filterKompaktliste.onlyAbweichungen"
                      :items="[
                        { title: 'Alle Artikel', value: false },
                        { title: 'Nur Abweichungen', value: true }
                      ]"
                      label="Filter"
                    />
                  </v-col>
                </v-row>

                <!-- Statistik -->
                <v-row class="mb-4">
                  <v-col cols="12" md="3">
                    <v-card variant="outlined">
                      <v-card-text class="text-center">
                        <div class="info-label">Unique Artikel</div>
                        <div class="info-value">{{ filteredKompaktliste.length }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card variant="outlined">
                      <v-card-text class="text-center">
                        <div class="info-label">Gesamt IST</div>
                        <div class="info-value">{{ kompaktGesamtIst }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card variant="outlined">
                      <v-card-text class="text-center">
                        <div class="info-label">Gesamt SOLL</div>
                        <div class="info-value">{{ kompaktGesamtSoll }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card variant="outlined">
                      <v-card-text class="text-center">
                        <div class="info-label">Gesamt Diff.</div>
                        <div class="info-value" :class="kompaktGesamtDiff >= 0 ? 'green--text' : 'red--text'">
                          {{ kompaktGesamtDiff > 0 ? '+' : '' }}{{ kompaktGesamtDiff }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Plausibilitäts-Vorschau (SAP-Export-Logik) -->
                <v-card variant="outlined" class="mb-4 plausi-card">
                  <v-card-title class="text-subtitle-1 d-flex align-center">
                    <v-icon class="mr-2" color="info">mdi-shield-search</v-icon>
                    Plausibilitäts-Vorschau (Stand für SAP-Export)
                  </v-card-title>
                  <v-card-text>
                    <v-alert
                      v-if="kompaktPlausi.hatOffenePaletten"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mb-3"
                      icon="mdi-alert"
                    >
                      <strong>Achtung:</strong> Es sind noch
                      <strong>{{ kompaktPlausi.offenePaletten }}</strong> Palette(n) in laufender Erfassung.
                      Deren Scans sind in dieser Vorschau <u>nicht</u> berücksichtigt.
                      Vor dem SAP-Export müssen alle Paletten den Status <em>abgeschlossen</em> haben.
                    </v-alert>

                    <v-row dense>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-neutral">
                          <div class="plausi-label">Katalog-Artikel gesamt</div>
                          <div class="plausi-value">{{ kompaktPlausi.total }}</div>
                          <div class="plausi-hint">Quelle: SAP-Soll-Import</div>
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-ok">
                          <div class="plausi-label">Exakt gezählt</div>
                          <div class="plausi-value">{{ kompaktPlausi.exakt }}</div>
                          <div class="plausi-hint">IST = SOLL</div>
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-danger">
                          <div class="plausi-label">Nicht gescannt</div>
                          <div class="plausi-value">{{ kompaktPlausi.ohneScan }}</div>
                          <div class="plausi-hint">
                            werden mit <strong>IST = 0</strong> ausgewertet<br>
                            → Ausbuchung: <strong>{{ kompaktPlausi.summeAusbuchung }} Stk</strong>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-warn">
                          <div class="plausi-label">Fehlbestand</div>
                          <div class="plausi-value">{{ kompaktPlausi.fehlbestand }}</div>
                          <div class="plausi-hint">
                            teilweise gezählt (IST &lt; SOLL)<br>
                            → fehlend: <strong>{{ kompaktPlausi.summeFehlbestand }} Stk</strong>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-warn">
                          <div class="plausi-label">Übermenge</div>
                          <div class="plausi-value">{{ kompaktPlausi.uebermenge }}</div>
                          <div class="plausi-hint">
                            IST &gt; SOLL<br>
                            → Mehrbestand: <strong>+{{ kompaktPlausi.summeUebermenge }} Stk</strong>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="6" md="3">
                        <div class="plausi-tile plausi-tile-info">
                          <div class="plausi-label">Neu (außerhalb Soll)</div>
                          <div class="plausi-value">{{ kompaktPlausi.neuArtikel }}</div>
                          <div class="plausi-hint">SOLL = 0, aber gescannt</div>
                        </div>
                      </v-col>
                    </v-row>

                    <v-alert
                      type="info"
                      variant="tonal"
                      density="compact"
                      class="mt-3 mb-0"
                      icon="mdi-information-outline"
                    >
                      <strong>So liest SAP das später:</strong>
                      Jeder Katalog-Artikel landet im Export. Nicht gescannte Artikel werden mit
                      <code>IST = 0</code> und Differenz <code>−SOLL</code> übergeben (Fehlbestand-Ausbuchung).
                      Geprüft: <code>LEFT JOIN inventur_katalog → inventur_positionen</code> mit
                      <code>COALESCE(SUM(istbestand),0)</code>.
                    </v-alert>
                  </v-card-text>
                </v-card>

                <!-- Tabelle: Kompaktliste -->
                <v-data-table
                  :headers="headersKompaktliste"
                  :items="filteredKompaktliste"
                  :loading="loading.kompaktliste"
                  v-model:expanded="expandedKompaktliste"
                  item-value="cmmf_nummer"
                  show-expand
                  class="elevation-0"
                  density="compact"
                  fixed-header
                  height="600px"
                >
                  <template v-slot:[`item.gesamt_differenz`]="{ item }">
                    <v-chip
                      :color="item.gesamt_differenz === 0 ? 'green' : item.gesamt_differenz > 0 ? 'orange' : 'red'"
                      :text-color="item.gesamt_differenz === 0 ? 'white' : 'white'"
                    >
                      {{ item.gesamt_differenz > 0 ? '+' : '' }}{{ item.gesamt_differenz }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.data-table-expand`]="{ item, internalItem, toggleExpand, isExpanded }">
                    <v-btn
                      icon="mdi-chevron-down"
                      size="small"
                      variant="text"
                      :class="{ 'rotate-180': isExpanded(internalItem) }"
                      @click.stop="toggleExpand(internalItem); loadArtikelPaletten(item.cmmf_nummer)"
                    />
                  </template>
                  <template v-slot:expanded-row="{ columns, item }">
                    <tr>
                      <td :colspan="columns.length" class="pa-4" style="background: rgba(255,255,255,0.03);">
                        <div v-if="loadingArtikelPaletten[item.cmmf_nummer]">
                          <v-progress-circular indeterminate size="20" /> Paletten werden geladen...
                        </div>
                        <div v-else-if="!kompaktArtikelPaletten[item.cmmf_nummer] || kompaktArtikelPaletten[item.cmmf_nummer].length === 0">
                          <em>Keine Paletten gefunden</em>
                        </div>
                        <v-table v-else density="compact">
                          <thead>
                            <tr>
                              <th>Paletten-Nr.</th>
                              <th>Status</th>
                              <th>Erfasst von</th>
                              <th>Datum</th>
                              <th class="text-right">Soll</th>
                              <th class="text-right">IST</th>
                              <th class="text-right">Differenz (kumuliert)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="p in kompaktArtikelPaletten[item.cmmf_nummer]"
                              :key="p.palette_id"
                              :class="p.status === 'erfassung' ? 'palette-row-erfassung' : ''"
                            >
                              <td>
                                <a
                                  href="#"
                                  class="text-primary text-decoration-none font-weight-medium"
                                  title="Palette öffnen"
                                  @click.stop.prevent="openPaletteDetail({ id: p.palette_id, paletten_nr: p.paletten_nr })"
                                >{{ p.paletten_nr }}</a>
                              </td>
                              <td>
                                <v-chip
                                  size="x-small"
                                  :color="p.status === 'erfassung' ? 'blue' : p.status === 'abgeschlossen' ? 'green' : 'grey'"
                                  variant="flat"
                                >
                                  {{ p.status }}
                                </v-chip>
                              </td>
                              <td>{{ p.erstellt_von_name }}</td>
                              <td>{{ formatDateTime(p.erfassung_datum) }}</td>
                              <td class="text-right">{{ p.sollbestand }}</td>
                              <td class="text-right">{{ p.istbestand }}</td>
                              <td class="text-right">{{ Number(p.kumulative_differenz) > 0 ? '+' : '' }}{{ p.kumulative_differenz }}</td>
                            </tr>
                          </tbody>
                        </v-table>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: Paletten (Office-Verwaltung) -->
          <v-window-item value="paletten">
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>Paletten-Übersicht</span>
                <div class="d-flex align-center" style="gap:8px;">
                  <v-btn size="small" color="primary" variant="elevated" @click="openEndloslisteDialog">
                    <v-icon start>mdi-printer-outline</v-icon>
                    Endlosliste drucken
                  </v-btn>
                  <v-btn size="small" variant="text" @click="fetchPaletten" :loading="loading.paletten">
                    <v-icon start>mdi-refresh</v-icon>
                    Aktualisieren
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersPaletten"
                  :items="paletten"
                  :loading="loading.paletten"
                  density="compact"
                  fixed-header
                  height="600px"
                  hover
                  @click:row="(_e, ctx) => openPaletteDetail(ctx && ctx.item)"
                >
                  <template v-slot:[`item.paletten_nr`]="{ item }">
                    <a href="#" class="text-primary text-decoration-none" @click.stop.prevent="openPaletteDetail(item)">
                      {{ item.paletten_nr }}
                    </a>
                  </template>
                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip
                      :color="item.status === 'abgeschlossen' ? 'green' : item.status === 'erfassung' ? 'blue' : 'grey'"
                      size="small"
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.erfassung_datum`]="{ item }">
                    {{ formatDateTime(item.erfassung_datum) }}
                  </template>
                  <template v-slot:[`item.abgeschlossen_am`]="{ item }">
                    {{ item.abgeschlossen_am ? formatDateTime(item.abgeschlossen_am) : '–' }}
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      size="small"
                      color="primary"
                      variant="tonal"
                      class="mr-2"
                      @click.stop="openPaletteDetail(item)"
                    >
                      <v-icon start size="16">mdi-eye-outline</v-icon>
                      Inhalt
                    </v-btn>
                    <v-btn
                      v-if="item.status === 'abgeschlossen' && !isViewingArchiv"
                      size="small"
                      color="warning"
                      variant="tonal"
                      :loading="reopeningPaletteId === item.id"
                      @click.stop="reopenPalette(item)"
                    >
                      <v-icon start size="16">mdi-lock-open-variant</v-icon>
                      Wieder öffnen
                    </v-btn>
                    <span v-else-if="isViewingArchiv" class="text-caption text-grey">Archiviert</span>
                    <span v-else class="text-caption text-grey">In Bearbeitung</span>
                    <v-btn
                      size="small"
                      color="info"
                      variant="tonal"
                      class="ml-2"
                      @click.stop="openPalettenscheinDialog(item)"
                    >
                      <v-icon start size="16">mdi-printer</v-icon>
                      Drucken
                    </v-btn>
                    <v-btn
                      v-if="!isViewingArchiv && Number(item.anzahl_positionen || 0) === 0"
                      size="small"
                      color="error"
                      variant="tonal"
                      class="ml-2"
                      :loading="deletingPaletteId === item.id"
                      @click.stop="deletePalette(item)"
                    >
                      <v-icon start size="16">mdi-delete</v-icon>
                      Löschen
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: Import -->
          <v-window-item value="import">
            <!-- Artikel-Katalog verwalten (initial geschlossen, ueber Button einblenden) -->
            <div class="d-flex align-center mb-3" style="gap:8px;">
              <v-btn
                color="primary"
                variant="elevated"
                @click="toggleKatalogVerwaltung"
              >
                <v-icon start>
                  {{ katalogVerwaltungVisible ? 'mdi-eye-off-outline' : 'mdi-database-search' }}
                </v-icon>
                {{ katalogVerwaltungVisible ? 'Artikel-Katalog ausblenden' : 'Artikel-Katalog öffnen' }}
              </v-btn>
              <span class="text-caption text-medium-emphasis">
                Wird erst auf Klick geladen – schont den Tab-Wechsel bei großem Katalog.
              </span>
            </div>
            <v-card v-if="katalogVerwaltungVisible" variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>Artikel-Katalog verwalten</span>
                <div class="d-flex align-center" style="gap:8px;">
                  <v-btn
                    size="small"
                    color="success"
                    @click="openKatalogEdit(null)"
                  >
                    <v-icon start>mdi-plus</v-icon>
                    Neuer Artikel
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="text"
                    @click="loadKatalogVerwaltung"
                    :loading="loading.katalogVerwaltung"
                  >
                    <v-icon start>mdi-refresh</v-icon>
                    Aktualisieren
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                  Bearbeitet bestehende Artikel im Inventur-Katalog
                  (EAN nachpflegen, Stammdaten korrigieren) oder legt neue Artikel an,
                  die nicht im SAP-Import enthalten waren.
                  Jede Änderung wird im Audit-Log mit Hash-Chain dokumentiert.
                </v-alert>

                <v-row dense class="mb-2">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="katalogVerwaltungFilter"
                      label="Suche (CMMF, EAN, Kurztext, Werk, Lagerort)"
                      density="comfortable"
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-magnify"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-checkbox
                      v-model="katalogVerwaltungNurOhneEan"
                      label="Nur Artikel ohne EAN anzeigen"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="katalogVerwaltungHeaders"
                  :items="katalogVerwaltungGefiltert"
                  :items-per-page="25"
                  :loading="loading.katalogVerwaltung"
                  density="compact"
                  fixed-header
                  height="500px"
                  no-data-text="Keine Artikel vorhanden."
                >
                  <template v-slot:[`item.gtin_ean`]="{ item }">
                    <span v-if="item.gtin_ean">{{ item.gtin_ean }}</span>
                    <span v-else class="text-warning">
                      <v-icon size="small" color="warning">mdi-barcode-off</v-icon>
                      –
                    </span>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      @click="openKatalogEdit(item)"
                      title="Bearbeiten"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

            <!-- EAN-Übernahme per Kurztext-Match (Duplikat-Erkennung im Inventur-Katalog) -->
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>EAN-Codes per Kurztext-Match ergänzen</span>
              </v-card-title>
              <v-card-text>
                <p class="mb-3">
                  Findet Artikel im Inventur-Katalog <strong>ohne EAN</strong>, deren
                  Kurztext exakt einem anderen Inventur-Artikel mit EAN entspricht –
                  auch wenn die CMMF-Nummern <em>nicht</em> übereinstimmen.
                  Vor der Übernahme werden alle Kandidaten zur Auswahl angezeigt.
                </p>
                <v-btn
                  color="primary"
                  :loading="loading.kurztextSync"
                  @click="openKurztextPreview"
                >
                  <v-icon start>mdi-text-search</v-icon>
                  Kurztext-Match prüfen …
                </v-btn>
                <v-alert
                  v-if="kurztextSyncResult.show"
                  :type="kurztextSyncResult.type"
                  class="mt-4"
                >
                  <strong>{{ kurztextSyncResult.title }}</strong><br>
                  Aktualisierte Zeilen: {{ kurztextSyncResult.aktualisiert }}<br>
                  Übersprungen: {{ kurztextSyncResult.uebersprungen }}<br>
                  Ohne EAN nachher: {{ kurztextSyncResult.nachher }} (von gesamt {{ kurztextSyncResult.gesamt }})
                </v-alert>
              </v-card-text>
            </v-card>

            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>EAN-Codes aus Refurbish-Katalog ergänzen</span>
              </v-card-title>
              <v-card-text>
                <p class="mb-3">
                  Übernimmt fehlende EAN-Codes aus <code>refurbish_artikel_katalog</code> in den Inventur-Katalog
                  (Match per CMMF-Nummer). Vorhandene EANs werden nicht überschrieben.
                </p>
                <v-btn
                  color="primary"
                  :loading="loading.eanSync"
                  @click="openEanPreview"
                >
                  <v-icon start>mdi-magnify</v-icon>
                  EAN-Übernahme prüfen …
                </v-btn>
                <v-alert
                  v-if="eanSyncResult.show"
                  :type="eanSyncResult.type"
                  class="mt-4"
                >
                  <strong>{{ eanSyncResult.title }}</strong><br>
                  Aktualisierte Zeilen: {{ eanSyncResult.aktualisiert }}<br>
                  Übersprungen: {{ eanSyncResult.uebersprungen }}<br>
                  Ohne EAN nachher: {{ eanSyncResult.nachher }} (von gesamt {{ eanSyncResult.gesamt }})
                </v-alert>
              </v-card-text>
            </v-card>

            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>EAN-Codes aus archivierten Inventuren übernehmen</span>
              </v-card-title>
              <v-card-text>
                <p class="mb-3">
                  Übernimmt fehlende EAN-Codes aus bereits <strong>archivierten Inventuren</strong>
                  (Match per CMMF-Nummer, tolerant gegen führende Nullen). So bleiben einmal
                  gepflegte EANs in einer neu gestarteten Inventur erhalten. Vorhandene EANs
                  werden nicht überschrieben.
                </p>
                <div class="d-flex align-center flex-wrap mb-3" style="gap:12px;">
                  <v-select
                    v-model="archivEanSourceId"
                    :items="archivEanSourceOptions"
                    item-title="title"
                    item-value="value"
                    label="Quelle"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    style="max-width: 480px;"
                  />
                </div>
                <v-btn
                  color="primary"
                  :loading="loading.archivEanSync"
                  :disabled="archivList.length === 0"
                  @click="openArchivEanPreview"
                >
                  <v-icon start>mdi-archive-search</v-icon>
                  EAN-Übernahme prüfen …
                </v-btn>
                <span v-if="archivList.length === 0" class="ml-3 text-caption text-medium-emphasis">
                  Keine archivierten Inventuren vorhanden.
                </span>
                <v-alert
                  v-if="archivEanSyncResult.show"
                  :type="archivEanSyncResult.type"
                  class="mt-4"
                >
                  <strong>{{ archivEanSyncResult.title }}</strong><br>
                  Aktualisierte Zeilen: {{ archivEanSyncResult.aktualisiert }}<br>
                  Übersprungen: {{ archivEanSyncResult.uebersprungen }}<br>
                  Ohne EAN nachher: {{ archivEanSyncResult.nachher }} (von gesamt {{ archivEanSyncResult.gesamt }})
                </v-alert>
              </v-card-text>
            </v-card>

            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>Artikel-Katalog importieren (aus Excel kopieren &amp; einfügen)</span>
                <div class="d-flex align-center text-caption" style="gap:12px;">
                  <span v-if="katalogStats.gesamt">
                    <v-icon size="small" class="mr-1">mdi-database</v-icon>
                    {{ katalogStats.gesamt }} Einträge
                  </span>
                  <span v-if="katalogStats.gesamt" class="text-success">
                    <v-icon size="small" class="mr-1" color="success">mdi-barcode-scan</v-icon>
                    {{ katalogStats.mit_ean }} mit EAN
                  </span>
                  <span v-if="katalogStats.gesamt" :class="katalogStats.ohne_ean > 0 ? 'text-warning' : 'text-medium-emphasis'">
                    <v-icon size="small" class="mr-1" :color="katalogStats.ohne_ean > 0 ? 'warning' : undefined">mdi-barcode-off</v-icon>
                    {{ katalogStats.ohne_ean }} ohne EAN
                  </span>
                  <v-btn
                    v-if="katalogStats.ohne_kurztext > 0"
                    size="small"
                    color="warning"
                    variant="tonal"
                    @click="openKurztextNachpflege"
                    :loading="loading.kurztextNachpflege"
                  >
                    <v-icon start>mdi-text-box-edit-outline</v-icon>
                    {{ katalogStats.ohne_kurztext }} ohne Kurztext
                  </v-btn>
                  <span v-if="katalogStats.letzte_aktualisierung" class="text-medium-emphasis">
                    zuletzt {{ formatDateTime(katalogStats.letzte_aktualisierung) }}
                  </span>
                  <v-btn size="small" variant="text" @click="fetchKatalogStats" :loading="loading.katalogStats">
                    <v-icon start>mdi-refresh</v-icon>
                    Status
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  Markieren Sie in Excel die Spalten
                  <strong>CMMF | EAN | Kurztext | Werk | Lagerort | Sollbestand</strong>
                  (mit oder ohne Kopfzeile) und fügen Sie die Daten unten ein.
                  Die Trennung erfolgt automatisch (Tab oder Semikolon).
                  <strong>Kurztext ist optional</strong> – fehlende Texte können danach im Dialog nachgepflegt werden.
                </v-alert>

                <v-checkbox
                  v-model="importHasHeader"
                  label="Erste Zeile ist eine Kopfzeile (überspringen)"
                  density="compact"
                  hide-details
                  class="mb-1"
                />
                <v-checkbox
                  v-model="importHasEan"
                  label="Daten enthalten eine EAN-Spalte (zwischen CMMF und Kurztext)"
                  density="compact"
                  hide-details
                  class="mb-2"
                  @update:model-value="parseImportPaste"
                />

                <v-textarea
                  v-model="importPasteText"
                  label="Daten hier einfügen (Strg+V)"
                  rows="10"
                  variant="outlined"
                  hide-details
                  class="mb-4"
                  placeholder="CMMF<TAB>EAN<TAB>Kurztext<TAB>Werk<TAB>Lagerort<TAB>Sollbestand"
                  @input="parseImportPaste"
                  @paste="onImportPaste"
                />

                <div class="d-flex align-center flex-wrap mb-4" style="gap:8px;">
                  <v-btn
                    color="success"
                    :loading="loading.import"
                    :disabled="parsedImportRows.length === 0 || importProgress.active"
                    @click="startImport"
                  >
                    <v-icon start>mdi-database-import</v-icon>
                    {{ parsedImportRows.length.toLocaleString('de-DE') }} Zeile(n) importieren
                  </v-btn>
                  <v-btn
                    variant="text"
                    @click="clearImportPaste"
                    :disabled="!importPasteText || importProgress.active"
                  >
                    <v-icon start>mdi-close</v-icon>
                    Leeren
                  </v-btn>
                  <v-spacer />
                  <span v-if="parsedImportPreview.length" class="text-caption text-medium-emphasis">
                    Vorschau: erste {{ parsedImportPreview.length }} von {{ parsedImportRows.length.toLocaleString('de-DE') }} Zeilen
                  </span>
                </div>

                <div v-if="importProgress.active" class="mb-4">
                  <div class="d-flex justify-space-between text-caption mb-1">
                    <span>{{ importProgress.label }}</span>
                    <span>{{ importProgress.percent }}%</span>
                  </div>
                  <v-progress-linear
                    :model-value="importProgress.percent"
                    color="success"
                    height="10"
                    rounded
                    striped
                  />
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ importProgress.done.toLocaleString('de-DE') }} /
                    {{ importProgress.total.toLocaleString('de-DE') }} Zeilen
                    <span v-if="importProgress.chunkTotal">
                      · Chunk {{ importProgress.chunkIndex }}/{{ importProgress.chunkTotal }}
                    </span>
                  </div>
                </div>

                <!-- Vorschau-Tabelle -->
                <v-data-table
                  v-if="parsedImportPreview.length"
                  :headers="headersImportPreview"
                  :items="parsedImportPreview"
                  density="compact"
                  hide-default-footer
                  class="mb-4"
                />

                <!-- Import-Ergebnis -->
                <v-alert
                  v-if="importResult.show"
                  :type="importResult.type"
                  class="mt-2"
                >
                  <strong>{{ importResult.title }}</strong><br>
                  Gesamt: {{ importResult.total }} | Erfolg: {{ importResult.success }} | Fehler: {{ importResult.errors }}
                  <div v-if="importResult.sollChanges != null" class="mt-2">
                    Soll-Änderungen: <strong>{{ importResult.sollChanges }}</strong>
                    <span v-if="importResult.sollDiffSha">
                      · Diff-SHA256: <code>{{ importResult.sollDiffSha }}</code>
                    </span>
                    <div v-if="importResult.sollDiffBeleg || importResult.sollDiffBelegId">
                      Beleg:
                      <code>{{ importResult.sollDiffBeleg || ('DB #' + importResult.sollDiffBelegId) }}</code>
                      <span v-if="importResult.sollDiffBelegStatus" class="text-medium-emphasis">
                        ({{ importResult.sollDiffBelegStatus }})
                      </span>
                      <v-btn
                        size="x-small"
                        variant="text"
                        class="ml-1"
                        @click="downloadSollDiff(importResult)"
                      >
                        Download
                      </v-btn>
                    </div>
                    <div v-else-if="importResult.sollChanges === 0" class="text-medium-emphasis">
                      Keine Soll-Änderung gegenüber Katalog → kein Beleg (Werte waren schon aktuell).
                    </div>
                    <div v-else-if="importResult.sollDiffBelegError" class="text-error">
                      Beleg-Fehler: {{ importResult.sollDiffBelegError }}
                    </div>
                  </div>
                  <div v-if="importResult.ohneKurztext > 0" class="mt-2">
                    <v-btn
                      size="small"
                      color="warning"
                      variant="tonal"
                      @click="openKurztextNachpflege"
                      :loading="loading.kurztextNachpflege"
                    >
                      <v-icon start>mdi-text-box-edit-outline</v-icon>
                      {{ importResult.ohneKurztext }} Artikel ohne Kurztext nachpflegen
                    </v-btn>
                  </div>
                  <ul v-if="importResult.messages.length > 0" class="mt-2">
                    <li v-for="msg in importResult.messages" :key="msg">{{ msg }}</li>
                  </ul>
                </v-alert>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: Korrekturen (nur Archiv-Ansicht) -->
          <v-window-item value="korrekturen">
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>Korrekturbuchungen</span>
                <div class="d-flex" style="gap:8px;">
                  <v-btn
                    size="small"
                    variant="tonal"
                    :disabled="korrekturen.length === 0"
                    @click="exportKorrekturen"
                  >
                    <v-icon start>mdi-download</v-icon>
                    Export CSV
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    color="primary"
                    :disabled="korrekturen.length === 0"
                    :loading="loading.korrekturenPrint"
                    @click="printKorrekturen"
                  >
                    <v-icon start>mdi-printer</v-icon>
                    Drucken
                  </v-btn>
                  <v-btn color="warning" size="small" @click="openKorrekturDialog">
                    <v-icon start>mdi-plus</v-icon>
                    Neue Korrektur
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-alert type="info" variant="tonal" density="compact" class="mb-4" icon="mdi-information-outline">
                  <strong>GoBD-Hinweis:</strong> Der archivierte Stichtagsbestand bleibt unverändert.
                  Korrekturen werden als separate, nachvollziehbare Buchungen erfasst und über die
                  Hash-Chain im Audit-Log gegen Manipulation gesichert.
                </v-alert>
                <v-data-table
                  :items="korrekturen"
                  :headers="[
                    { title: 'Datum', key: 'erstellt_am', sortable: true },
                    { title: 'CMMF', key: 'cmmf_nummer' },
                    { title: 'Kurztext', key: 'kurztext' },
                    { title: 'Werk/Lager', key: 'werk_lager' },
                    { title: 'Alt', key: 'bestand_alt', align: 'end' },
                    { title: 'Neu', key: 'bestand_neu', align: 'end' },
                    { title: 'Δ', key: 'differenz', align: 'end' },
                    { title: 'Kategorie', key: 'grund_kategorie' },
                    { title: 'Grund', key: 'grund_text' },
                    { title: 'Erfasst von', key: 'erstellt_von_name' }
                  ]"
                  :items-per-page="50"
                  density="compact"
                  no-data-text="Keine Korrekturen vorhanden."
                >
                  <template v-slot:[`item.erstellt_am`]="{ item }">
                    {{ item.erstellt_am ? new Date(item.erstellt_am).toLocaleString('de-DE') : '' }}
                  </template>
                  <template v-slot:[`item.werk_lager`]="{ item }">
                    {{ [item.werk, item.lagerort].filter(Boolean).join(' / ') || '–' }}
                  </template>
                  <template v-slot:[`item.differenz`]="{ item }">
                    <span :class="Number(item.differenz) > 0 ? 'text-success' : 'text-error'">
                      {{ Number(item.differenz) > 0 ? '+' : '' }}{{ item.differenz }}
                    </span>
                  </template>
                  <template v-slot:[`item.grund_kategorie`]="{ item }">
                    {{ (grundKategorien.find(g => g.value === item.grund_kategorie) || {}).title || item.grund_kategorie }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: EAN-Lookup -->
          <v-window-item value="ean-lookup">
            <!-- Block 1: EAN-Übernahme aus Lookup-Tabelle in den Inventur-Katalog -->
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>EAN-Codes aus Lookup-Tabelle ergänzen</span>
              </v-card-title>
              <v-card-text>
                <p class="mb-3">
                  Übernimmt fehlende EAN-Codes aus <code>inventur_ean_lookup</code> in den Inventur-Katalog
                  (Match per CMMF-Nummer). Vorhandene EANs werden nicht überschrieben,
                  und es werden auch keine neuen Artikel angelegt – der CMMF muss bereits im Katalog stehen.
                </p>
                <v-btn
                  color="primary"
                  :loading="loading.eanLookupSync"
                  @click="openEanLookupPreview"
                >
                  <v-icon start>mdi-magnify</v-icon>
                  EAN-Übernahme prüfen …
                </v-btn>
                <v-alert
                  v-if="eanLookupSyncResult.show"
                  :type="eanLookupSyncResult.type"
                  class="mt-4"
                >
                  <strong>{{ eanLookupSyncResult.title }}</strong><br>
                  Aktualisierte Zeilen: {{ eanLookupSyncResult.aktualisiert }}<br>
                  Übersprungen: {{ eanLookupSyncResult.uebersprungen }}<br>
                  Ohne EAN nachher: {{ eanLookupSyncResult.nachher }} (von gesamt {{ eanLookupSyncResult.gesamt }})
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Block 2: Lookup-Tabelle pflegen (Paste-Import) -->
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>EAN-Lookup-Tabelle</span>
                <div class="d-flex align-center text-caption" style="gap:12px;">
                  <span v-if="eanLookupStats.gesamt">
                    <v-icon size="small" class="mr-1">mdi-database</v-icon>
                    {{ eanLookupStats.gesamt }} Einträge
                  </span>
                  <span v-if="eanLookupStats.letzte_aktualisierung" class="text-medium-emphasis">
                    zuletzt {{ formatDateTime(eanLookupStats.letzte_aktualisierung) }}
                  </span>
                  <v-btn size="small" variant="text" @click="fetchEanLookupStats" :loading="loading.eanLookupStats">
                    <v-icon start>mdi-refresh</v-icon>
                    Status
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  Hier kann eine zentrale <strong>EAN ↔ CMMF ↔ Bezeichnung</strong>-Liste
                  per Excel-Paste gepflegt werden. Wenn beim Scannen ein EAN nicht im
                  Inventur-Katalog gefunden wird, schaut das System hier nach und
                  schlägt den passenden Artikel über den CMMF-Code vor.
                  <br><br>
                  <strong>Spaltenreihenfolge in Excel:</strong>
                  <code>EAN&nbsp;|&nbsp;CMMF&nbsp;|&nbsp;Bezeichnung</code>
                  (Trenner Tab oder Semikolon).
                </v-alert>

                <v-checkbox
                  v-model="eanLookupHasHeader"
                  label="Erste Zeile ist eine Kopfzeile (überspringen)"
                  density="compact"
                  hide-details
                  class="mb-1"
                  @update:model-value="parseEanLookupPaste"
                />
                <v-checkbox
                  v-model="eanLookupReplaceAll"
                  label="Bestehende Tabelle vor Import komplett ersetzen (Vorsicht!)"
                  density="compact"
                  color="warning"
                  hide-details
                  class="mb-2"
                />

                <v-textarea
                  v-model="eanLookupPasteText"
                  label="Daten hier einfügen (Strg+V)"
                  rows="10"
                  variant="outlined"
                  hide-details
                  class="mb-4"
                  placeholder="4210101392812&#9;000000001110020257&#9;DA1510F1 TRAVEL IRON"
                  @input="parseEanLookupPaste"
                  @paste="onEanLookupPaste"
                />

                <div class="d-flex align-center flex-wrap mb-4" style="gap:8px;">
                  <v-btn
                    color="success"
                    :loading="loading.eanLookupImport"
                    :disabled="parsedEanLookupRows.length === 0"
                    @click="startEanLookupImport"
                  >
                    <v-icon start>mdi-database-import</v-icon>
                    {{ parsedEanLookupRows.length }} Zeile(n) importieren
                  </v-btn>
                  <v-btn
                    variant="text"
                    @click="clearEanLookupPaste"
                    :disabled="!eanLookupPasteText"
                  >
                    <v-icon start>mdi-close</v-icon>
                    Eingabe leeren
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    size="small"
                    variant="text"
                    color="error"
                    @click="clearEanLookupTable"
                    :loading="loading.eanLookupClear"
                    :disabled="!eanLookupStats.gesamt"
                  >
                    <v-icon start>mdi-delete-sweep-outline</v-icon>
                    Tabelle leeren
                  </v-btn>
                </div>

                <v-data-table
                  v-if="parsedEanLookupPreview.length"
                  :headers="headersEanLookupPreview"
                  :items="parsedEanLookupPreview"
                  density="compact"
                  hide-default-footer
                  class="mb-4"
                />

                <v-alert
                  v-if="eanLookupResult.show"
                  :type="eanLookupResult.type"
                  class="mt-2"
                >
                  <strong>{{ eanLookupResult.title }}</strong><br>
                  Gesamt: {{ eanLookupResult.total }} | Neu: {{ eanLookupResult.eingefuegt }} |
                  Aktualisiert: {{ eanLookupResult.aktualisiert }} |
                  Übersprungen: {{ eanLookupResult.uebersprungen }}
                  <span v-if="eanLookupResult.geloescht"> | Gelöscht: {{ eanLookupResult.geloescht }}</span>
                  <ul v-if="eanLookupResult.fehler && eanLookupResult.fehler.length" class="mt-2">
                    <li v-for="msg in eanLookupResult.fehler" :key="msg">{{ msg }}</li>
                  </ul>
                </v-alert>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- TAB: Audit-Log -->
          <v-window-item value="log">
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center flex-wrap" style="gap:8px;">
                <span>Audit-Log</span>
                <div class="d-flex align-center" style="gap:8px;">
                  <v-btn
                    size="small"
                    color="success"
                    variant="elevated"
                    @click="verifyAuditChain"
                    :loading="loading.verifyChain"
                    :disabled="!auditLog.length"
                    title="GoBD: SHA-256 Hash-Chain neu berechnen und mit gespeicherten Hashes vergleichen"
                  >
                    <v-icon start>mdi-shield-check-outline</v-icon>
                    Hash-Chain prüfen
                  </v-btn>
                  <v-btn
                    size="small"
                    color="secondary"
                    variant="elevated"
                    @click="exportAuditLogCsv"
                    :disabled="!auditLog.length"
                    title="Audit-Log inkl. Hash-Chain als CSV (für Betriebsprüfung)"
                  >
                    <v-icon start>mdi-file-delimited-outline</v-icon>
                    CSV-Export
                  </v-btn>
                  <v-btn
                    size="small"
                    color="primary"
                    variant="elevated"
                    @click="printAuditLog"
                    :loading="loading.auditPrint"
                    :disabled="!auditLog.length"
                  >
                    <v-icon start>mdi-printer-outline</v-icon>
                    Drucken
                  </v-btn>
                  <v-btn size="small" variant="text" @click="fetchLog" :loading="loading.log">
                    <v-icon start>mdi-refresh</v-icon>
                    Aktualisieren
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersLog"
                  :items="auditLog"
                  :loading="loading.log"
                  class="elevation-0"
                  density="compact"
                  fixed-header
                  height="600px"
                >
                  <template v-slot:[`item.created_at`]="{ item }">
                    {{ formatDateTime(item.created_at) }}
                  </template>
                  <template v-slot:[`item.record_hash`]="{ item }">
                    <span class="audit-hash" :title="item.record_hash || ''">{{ item.record_hash || '—' }}</span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <!-- Dialog: Hash-Chain-Verify-Ergebnis -->
    <v-dialog v-model="verifyChainDialog" max-width="640">
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon :color="verifyChainResult?.valid ? 'success' : 'error'">
            {{ verifyChainResult?.valid ? 'mdi-shield-check' : 'mdi-shield-alert' }}
          </v-icon>
          Hash-Chain-Prüfung
        </v-card-title>
        <v-card-text v-if="verifyChainResult">
          <v-alert
            :type="verifyChainResult.valid ? 'success' : 'error'"
            variant="tonal"
            class="mb-3"
          >
            <strong v-if="verifyChainResult.valid">Audit-Log unverändert.</strong>
            <strong v-else>ACHTUNG: Hash-Chain gebrochen!</strong>
          </v-alert>
          <v-list density="compact">
            <v-list-item>
              <v-list-item-title>Gesamt geprüft</v-list-item-title>
              <template #append>{{ verifyChainResult.total }}</template>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Hash gültig</v-list-item-title>
              <template #append>
                <span class="text-success">{{ verifyChainResult.ok }}</span>
              </template>
            </v-list-item>
            <v-list-item v-if="verifyChainResult.legacy">
              <v-list-item-title>Legacy (vor Verify-Feature)</v-list-item-title>
              <template #append>
                <span class="text-grey">{{ verifyChainResult.legacy }}</span>
              </template>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>Bruchstellen</v-list-item-title>
              <template #append>
                <span :class="verifyChainResult.broken ? 'text-error' : 'text-grey'">
                  {{ verifyChainResult.broken }}
                </span>
              </template>
            </v-list-item>
          </v-list>
          <v-divider class="my-2" v-if="verifyChainResult.breaks?.length" />
          <div v-if="verifyChainResult.breaks?.length">
            <div class="text-subtitle-2 mb-1">Erste Bruchstellen:</div>
            <v-list density="compact" class="bg-grey-lighten-4">
              <v-list-item v-for="b in verifyChainResult.breaks" :key="b.id">
                <v-list-item-title>
                  Eintrag #{{ b.id }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ b.reason }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="verifyChainDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Palettenschein nachdrucken -->
    <v-dialog v-model="palettenscheinDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-printer</v-icon>
          Palettenschein drucken
        </v-card-title>
        <v-card-text>
          <p class="mb-4">Gepackt von, Gescannt von und Kontrolle (optional) eintragen.</p>
          <v-autocomplete
            v-model="palettenscheinData.ansager"
            :items="userOptions"
            :loading="userOptionsLoading"
            label="Gepackt von"
            class="mb-2"
            hide-details
            clearable
            prepend-inner-icon="mdi-account"
            no-data-text="Keine Mitarbeiter gefunden"
          />
          <v-text-field
            v-model="palettenscheinData.schreiber"
            label="Gescannt von"
            class="mb-2"
            hide-details
            prepend-inner-icon="mdi-account-check"
            hint="Automatisch ermittelt aus den Scans dieser Palette – bei Bedarf editierbar"
            persistent-hint
          />
          <v-autocomplete
            v-model="palettenscheinData.kontrolle"
            :items="userOptions"
            :loading="userOptionsLoading"
            label="Kontrolle (optional)"
            hide-details
            clearable
            prepend-inner-icon="mdi-account-search"
            no-data-text="Keine Mitarbeiter gefunden"
            class="mt-4"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="palettenscheinDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="executePrintPalettenschein">
            <v-icon start>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: SAP-Export-Bestätigung (Plausibilitäts-Vorschau) -->
    <v-dialog v-model="sapExportConfirmDialog" max-width="760" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-alert-decagram-outline</v-icon>
          SAP-Export – Plausibilitätsprüfung
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Bitte den endgültigen Zählstand vor der Übergabe an SAP nochmals prüfen.
            Der Export erzeugt einen <strong>GoBD-Audit-Anker (SHA-256)</strong> und kann
            danach nicht mehr unbemerkt verändert werden.
          </p>

          <v-alert
            v-if="kompaktPlausi.hatOffenePaletten"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
            icon="mdi-close-octagon"
          >
            <strong>Export blockiert:</strong>
            Es sind noch <strong>{{ kompaktPlausi.offenePaletten }}</strong> Palette(n) in laufender
            Erfassung. Bitte zuerst alle Paletten abschließen.
          </v-alert>

          <v-table density="compact" class="mb-3">
            <tbody>
              <tr>
                <td><strong>Katalog-Artikel insgesamt</strong></td>
                <td>{{ kompaktPlausi.total }}</td>
              </tr>
              <tr>
                <td><strong>Exakt gezählt (IST = SOLL)</strong></td>
                <td class="text-success"><strong>{{ kompaktPlausi.exakt }}</strong></td>
              </tr>
              <tr class="bg-red-lighten-5">
                <td>
                  <strong>Nicht gescannt</strong>
                  <div class="text-caption text-medium-emphasis">
                    werden mit IST = 0 übergeben → SAP bucht den Soll-Bestand aus
                  </div>
                </td>
                <td>
                  <strong>{{ kompaktPlausi.ohneScan }}</strong> Artikel
                  <div class="text-caption">
                    Ausbuchung gesamt: <strong>{{ kompaktPlausi.summeAusbuchung }} Stk</strong>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Teilweise gezählt (Fehlbestand)</strong>
                  <div class="text-caption text-medium-emphasis">IST &lt; SOLL</div>
                </td>
                <td>
                  {{ kompaktPlausi.fehlbestand }} Artikel
                  <div class="text-caption">
                    fehlend: <strong>{{ kompaktPlausi.summeFehlbestand }} Stk</strong>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Übermenge / Neu</strong>
                  <div class="text-caption text-medium-emphasis">IST &gt; SOLL bzw. nicht im Soll-Katalog</div>
                </td>
                <td>
                  {{ kompaktPlausi.uebermenge }} Artikel ({{ kompaktPlausi.neuArtikel }} davon neu)
                  <div class="text-caption">
                    Mehrbestand: <strong>+{{ kompaktPlausi.summeUebermenge }} Stk</strong>
                  </div>
                </td>
              </tr>
              <tr>
                <td><strong>Σ Gesamt-SOLL</strong></td>
                <td>{{ kompaktGesamtSoll }} Stk</td>
              </tr>
              <tr>
                <td><strong>Σ Gesamt-IST</strong></td>
                <td>{{ kompaktGesamtIst }} Stk</td>
              </tr>
              <tr>
                <td><strong>Σ Gesamt-Differenz</strong></td>
                <td>
                  <span :class="kompaktGesamtDiff >= 0 ? 'text-success' : 'text-error'">
                    <strong>{{ kompaktGesamtDiff > 0 ? '+' : '' }}{{ kompaktGesamtDiff }}</strong> Stk
                  </span>
                </td>
              </tr>
            </tbody>
          </v-table>

          <v-text-field
            v-model="sapExportConfirm.referenz"
            label="Externe Referenz-ID für SAP (optional, z. B. SAP-INV-2026-001)"
            density="compact"
            variant="outlined"
            prepend-inner-icon="mdi-identifier"
            class="mb-2"
          />

          <v-checkbox
            v-model="sapExportConfirm.bestaetigt"
            color="warning"
            density="compact"
            hide-details
          >
            <template #label>
              <span>
                Ich habe die obigen Zahlen geprüft und bestätige, dass nicht gescannte Artikel
                bewusst als Fehlbestand <strong>ausgebucht</strong> werden sollen.
              </span>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="sapExportConfirmDialog = false" :disabled="sapExportConfirm.busy">
            Abbrechen
          </v-btn>
          <v-spacer />
          <v-btn
            color="success"
            :disabled="!sapExportConfirm.bestaetigt || kompaktPlausi.hatOffenePaletten || sapExportConfirm.busy"
            :loading="sapExportConfirm.busy"
            @click="performSapExport"
          >
            <v-icon start>mdi-database-export</v-icon>
            SAP-Export jetzt erzeugen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: SAP-Export-Ergebnis (Medienbruch-Anker) -->
    <v-dialog v-model="sapExportDialog" max-width="720" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" color="success">mdi-database-export</v-icon>
          SAP-Export abgeschlossen
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Die CSV-Datei wurde erzeugt und der Export wurde im Audit-Log dokumentiert.
            Bitte den unten gezeigten SHA-256 auf das SAP-Übergabeprotokoll notieren
            bzw. ausdrucken und vom SAP-Verantwortlichen gegenzeichnen lassen.
          </p>
          <v-table density="compact">
            <tbody>
              <tr><td><strong>Datei</strong></td><td>{{ sapExportResult.filename }}</td></tr>
              <tr><td><strong>Zeitpunkt</strong></td><td>{{ sapExportResult.zeit }}</td></tr>
              <tr><td><strong>Referenz-ID</strong></td><td>{{ sapExportResult.referenz || '(keine angegeben)' }}</td></tr>
              <tr><td><strong>Zeilen (Artikel)</strong></td><td>{{ sapExportResult.rows }}</td></tr>
              <tr><td><strong>Gesamt SOLL</strong></td><td>{{ sapExportResult.sumSoll }}</td></tr>
              <tr><td><strong>Gesamt IST</strong></td><td>{{ sapExportResult.sumIst }}</td></tr>
              <tr><td><strong>Gesamt Differenz</strong></td><td>{{ (Number(sapExportResult.sumIst) - Number(sapExportResult.sumSoll)) || 0 }}</td></tr>
              <tr>
                <td><strong>SHA-256 der CSV</strong></td>
                <td style="font-family:monospace; word-break:break-all;">{{ sapExportResult.csvHash }}</td>
              </tr>
              <tr>
                <td><strong>Audit-Anker (Log #{{ sapExportResult.anchorId }})</strong></td>
                <td style="font-family:monospace; word-break:break-all;">{{ sapExportResult.anchorHash }}</td>
              </tr>
            </tbody>
          </v-table>
          <p class="mt-3 text-caption text-medium-emphasis">
            Stimmt der oben gezeigte Audit-Anker bei einer späteren Prüfung mit dem
            <code>record_hash</code> in <code>inventur_log</code> überein, ist die
            Unverändertheit der gesamten Kette bis zu diesem Export bewiesen.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="copySapHash">
            <v-icon start>mdi-content-copy</v-icon>
            Hash kopieren
          </v-btn>
          <v-spacer />
          <v-btn color="primary" @click="sapExportDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Korrekturen-CSV-Export (SHA-256 Verifikation) -->
    <v-dialog v-model="korrekturenExportDialog" max-width="780" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" color="success">mdi-file-check-outline</v-icon>
          Korrekturen-CSV exportiert
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Die CSV-Datei wurde erzeugt und der Export wurde im Audit-Log
            dokumentiert. Mit dem unten gezeigten SHA-256 lässt sich jederzeit
            prüfen, dass eine später vorgelegte Datei <strong>byte-genau</strong>
            der hier exportierten entspricht.
          </p>
          <v-table density="compact">
            <tbody>
              <tr><td><strong>Datei</strong></td><td>{{ korrekturenExportResult.filename }}</td></tr>
              <tr><td><strong>Zeitpunkt</strong></td><td>{{ korrekturenExportResult.zeit }}</td></tr>
              <tr><td><strong>Zeilen</strong></td><td>{{ korrekturenExportResult.rows }}</td></tr>
              <tr><td><strong>Summe Δ</strong></td><td>{{ Number(korrekturenExportResult.sumDiff) >= 0 ? '+' : '' }}{{ korrekturenExportResult.sumDiff }}</td></tr>
              <tr>
                <td><strong>SHA-256 der CSV</strong></td>
                <td style="font-family:monospace; word-break:break-all;">{{ korrekturenExportResult.csvHash }}</td>
              </tr>
              <tr>
                <td><strong>Audit-Anker (Log #{{ korrekturenExportResult.anchorId }})</strong></td>
                <td style="font-family:monospace; word-break:break-all;">{{ korrekturenExportResult.anchorHash }}</td>
              </tr>
            </tbody>
          </v-table>

          <v-alert type="info" variant="tonal" density="compact" class="mt-4" border="start">
            <div class="text-subtitle-2 mb-1">So prüfen Sie den Hash gegen die Datei:</div>
            <div class="mb-2">
              <strong>Windows (PowerShell):</strong>
              <code style="display:block; padding:4px 8px; background:rgba(0,0,0,0.25); border-radius:4px; margin-top:4px;">Get-FileHash -Algorithm SHA256 "{{ korrekturenExportResult.filename }}"</code>
              <v-btn size="x-small" variant="text" class="mt-1" @click="copyVerifyCommand('pwsh')">
                <v-icon start size="small">mdi-content-copy</v-icon>Befehl kopieren
              </v-btn>
            </div>
            <div>
              <strong>Linux / macOS:</strong>
              <code style="display:block; padding:4px 8px; background:rgba(0,0,0,0.25); border-radius:4px; margin-top:4px;">sha256sum "{{ korrekturenExportResult.filename }}"</code>
              <v-btn size="x-small" variant="text" class="mt-1" @click="copyVerifyCommand('unix')">
                <v-icon start size="small">mdi-content-copy</v-icon>Befehl kopieren
              </v-btn>
            </div>
            <div class="text-caption mt-3">
              Das ausgegebene Ergebnis muss exakt mit dem oben gezeigten SHA-256
              übereinstimmen. Stimmt es nicht, wurde die Datei verändert.
            </div>
          </v-alert>

          <p class="mt-3 text-caption text-medium-emphasis">
            Der Audit-Anker (Log #{{ korrekturenExportResult.anchorId }}) verweist
            auf den Eintrag „korrekturen_export" in <code>inventur_log</code>, in
            dessen Beschreibung derselbe SHA-256 hinterlegt ist. Damit ist der
            Export revisionssicher mit der globalen Hash-Chain verknüpft.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="copyKorrekturenHash">
            <v-icon start>mdi-content-copy</v-icon>
            Hash kopieren
          </v-btn>
          <v-spacer />
          <v-btn color="primary" @click="korrekturenExportDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Zählergebnis drucken (GoBD) -->
    <v-dialog v-model="endloslisteDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-format-list-numbered</v-icon>
          Zählergebnis drucken
        </v-card-title>
        <v-card-text>
          <p class="mb-4 text-body-2">
            Druckt eine fortlaufende, nummerierte Liste aller Positionen.
            Der Druck wird im Audit-Log dokumentiert.
          </p>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="endloslisteFilter.druckJahr"
                label="Inventur-Jahr (Druck-Header)"
                type="number"
                hint="Jahr, das oben im Druck erscheint"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="endloslisteFilter.jahr"
                label="Filter: Paletten-Anlegejahr (optional)"
                type="number"
                hint="Leer = alle Jahre. Filtert auf inventur_paletten.jahr"
                persistent-hint
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="endloslisteFilter.status"
                :items="endloslisteStatusOptions"
                label="Paletten-Status"
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="endloslisteDialog = false" :disabled="loading.endlosliste">
            Abbrechen
          </v-btn>
          <v-btn color="primary" @click="executePrintEndlosliste" :loading="loading.endlosliste">
            <v-icon start>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== EAN-Übernahme aus Refurbish: Vorschau-Dialog ===== -->
    <v-dialog v-model="eanPreviewDialog" max-width="1100" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon>mdi-magnify</v-icon>
          EAN-Übernahme aus Refurbish-Katalog – Vorschau
        </v-card-title>
        <v-card-text>
          <div v-if="loading.eanSync" class="d-flex align-center" style="gap:8px;">
            <v-progress-circular indeterminate size="20" />
            Daten werden ermittelt …
          </div>

          <div v-else>
            <v-alert
              :type="eanPreview.items.length === 0 ? 'info' : 'success'"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div>
                Inventur-Katalog: <strong>{{ eanPreview.katalog_gesamt }}</strong> Artikel,
                davon <strong>{{ eanPreview.ohne_ean_gesamt }}</strong> ohne EAN.
              </div>
              <div>
                Treffer im Refurbish-Katalog: <strong>{{ eanPreview.treffer_anzahl }}</strong>
                ({{ eanPreview.eindeutig }} eindeutig, {{ eanPreview.mehrdeutig }} mehrdeutig).
              </div>
            </v-alert>

            <div
              v-if="eanPreview.items.length"
              class="d-flex align-center mb-2"
              style="gap:12px;"
            >
              <v-checkbox
                :model-value="eanPreviewAllSelected"
                :indeterminate="eanPreviewSomeSelected && !eanPreviewAllSelected"
                hide-details
                density="compact"
                label="Alle auswählen"
                @update:model-value="toggleEanSelectAll"
              />
              <span class="text-caption text-medium-emphasis">
                {{ eanPreviewSelectedCount }} von {{ eanPreview.items.length }} ausgewählt
              </span>
            </div>

            <v-table
              v-if="eanPreview.items.length"
              density="compact"
              class="ean-preview-table"
            >
              <thead>
                <tr>
                  <th style="width:40px;"></th>
                  <th>CMMF</th>
                  <th>Kurztext (Inventur)</th>
                  <th>Werk</th>
                  <th>Lagerort</th>
                  <th>EAN-Auswahl (Refurbish)</th>
                  <th>Refurbish-Bezeichnung</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in eanPreview.items" :key="item.cmmf_nummer">
                  <td>
                    <v-checkbox
                      v-model="eanSelected[item.cmmf_nummer]"
                      hide-details
                      density="compact"
                    />
                  </td>
                  <td><strong>{{ item.cmmf_nummer }}</strong></td>
                  <td>{{ item.kurztext }}</td>
                  <td>{{ item.werk }}</td>
                  <td>{{ item.lagerort }}</td>
                  <td>
                    <template v-if="item.kandidaten.length === 1">
                      <span class="font-weight-medium">{{ item.kandidaten[0].ean }}</span>
                    </template>
                    <template v-else>
                      <v-select
                        v-model="eanChoice[item.cmmf_nummer]"
                        :items="item.kandidaten.map(k => ({ title: k.ean + (k.code_commercial ? ' · ' + k.code_commercial : ''), value: k.ean }))"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width:240px;"
                      />
                      <span class="text-caption text-warning">
                        {{ item.kandidaten.length }} Kandidaten
                      </span>
                    </template>
                  </td>
                  <td class="text-caption">
                    {{ getSelectedCandidateDescription(item) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="eanPreviewDialog = false" :disabled="loading.eanSync">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            :disabled="eanPreviewSelectedCount === 0 || loading.eanSync"
            :loading="loading.eanSync"
            @click="applyEanSelection"
          >
            <v-icon start>mdi-check</v-icon>
            {{ eanPreviewSelectedCount }} Zuordnung(en) übernehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== EAN-Übernahme aus Lookup-Tabelle: Vorschau-Dialog ===== -->
    <v-dialog v-model="eanLookupPreviewDialog" max-width="1100" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon>mdi-magnify</v-icon>
          EAN-Übernahme aus Lookup-Tabelle – Vorschau
        </v-card-title>
        <v-card-text>
          <div v-if="loading.eanLookupSync" class="d-flex align-center" style="gap:8px;">
            <v-progress-circular indeterminate size="20" />
            Daten werden ermittelt …
          </div>

          <div v-else>
            <v-alert
              :type="eanLookupPreview.items.length === 0 ? 'info' : 'success'"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div>
                Inventur-Katalog: <strong>{{ eanLookupPreview.katalog_gesamt }}</strong> Artikel,
                davon <strong>{{ eanLookupPreview.ohne_ean_gesamt }}</strong> ohne EAN.
              </div>
              <div>
                Treffer in der Lookup-Tabelle: <strong>{{ eanLookupPreview.treffer_anzahl }}</strong>
                ({{ eanLookupPreview.eindeutig }} eindeutig, {{ eanLookupPreview.mehrdeutig }} mehrdeutig).
              </div>
            </v-alert>

            <div
              v-if="eanLookupPreview.items.length"
              class="d-flex align-center mb-2"
              style="gap:12px;"
            >
              <v-checkbox
                :model-value="eanLookupPreviewAllSelected"
                :indeterminate="eanLookupPreviewSomeSelected && !eanLookupPreviewAllSelected"
                hide-details
                density="compact"
                label="Alle auswählen"
                @update:model-value="toggleEanLookupSelectAll"
              />
              <span class="text-caption text-medium-emphasis">
                {{ eanLookupPreviewSelectedCount }} von {{ eanLookupPreview.items.length }} ausgewählt
              </span>
            </div>

            <v-table
              v-if="eanLookupPreview.items.length"
              density="compact"
              class="ean-preview-table"
            >
              <thead>
                <tr>
                  <th style="width:40px;"></th>
                  <th>CMMF</th>
                  <th>Kurztext (Inventur)</th>
                  <th>Werk</th>
                  <th>Lagerort</th>
                  <th>EAN-Auswahl (Lookup)</th>
                  <th>Lookup-Bezeichnung</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in eanLookupPreview.items" :key="item.cmmf_nummer">
                  <td>
                    <v-checkbox
                      v-model="eanLookupSelected[item.cmmf_nummer]"
                      hide-details
                      density="compact"
                    />
                  </td>
                  <td><strong>{{ item.cmmf_nummer }}</strong></td>
                  <td>{{ item.kurztext }}</td>
                  <td>{{ item.werk }}</td>
                  <td>{{ item.lagerort }}</td>
                  <td>
                    <template v-if="item.kandidaten.length === 1">
                      <span class="font-weight-medium">{{ item.kandidaten[0].ean }}</span>
                    </template>
                    <template v-else>
                      <v-select
                        v-model="eanLookupChoice[item.cmmf_nummer]"
                        :items="item.kandidaten.map(k => ({ title: k.ean + (k.bezeichnung ? ' · ' + k.bezeichnung : ''), value: k.ean }))"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width:240px;"
                      />
                      <span class="text-caption text-warning">
                        {{ item.kandidaten.length }} Kandidaten
                      </span>
                    </template>
                  </td>
                  <td class="text-caption">
                    {{ getSelectedLookupCandidateDescription(item) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="eanLookupPreviewDialog = false" :disabled="loading.eanLookupSync">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            :disabled="eanLookupPreviewSelectedCount === 0 || loading.eanLookupSync"
            :loading="loading.eanLookupSync"
            @click="applyEanLookupSelection"
          >
            <v-icon start>mdi-check</v-icon>
            {{ eanLookupPreviewSelectedCount }} Zuordnung(en) übernehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== EAN-Übernahme aus Inventur-Archiv: Vorschau-Dialog ===== -->
    <v-dialog v-model="archivEanPreviewDialog" max-width="1200" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon>mdi-archive-search</v-icon>
          EAN-Übernahme aus archivierten Inventuren – Vorschau
        </v-card-title>
        <v-card-text>
          <div v-if="loading.archivEanSync" class="d-flex align-center" style="gap:8px;">
            <v-progress-circular indeterminate size="20" />
            Daten werden ermittelt …
          </div>

          <div v-else>
            <v-alert
              :type="archivEanPreview.items.length === 0 ? 'info' : 'success'"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div>
                Inventur-Katalog: <strong>{{ archivEanPreview.katalog_gesamt }}</strong> Artikel,
                davon <strong>{{ archivEanPreview.ohne_ean_gesamt }}</strong> ohne EAN.
              </div>
              <div>
                Treffer in {{ archivEanSourceLabel }}:
                <strong>{{ archivEanPreview.treffer_anzahl }}</strong>
                ({{ archivEanPreview.eindeutig }} eindeutig,
                {{ archivEanPreview.mehrdeutig }} mehrdeutig).
              </div>
            </v-alert>

            <div
              v-if="archivEanPreview.items.length"
              class="d-flex align-center mb-2"
              style="gap:12px;"
            >
              <v-checkbox
                :model-value="archivEanPreviewAllSelected"
                :indeterminate="archivEanPreviewSomeSelected && !archivEanPreviewAllSelected"
                hide-details
                density="compact"
                label="Alle auswählen"
                @update:model-value="toggleArchivEanSelectAll"
              />
              <span class="text-caption text-medium-emphasis">
                {{ archivEanPreviewSelectedCount }} von {{ archivEanPreview.items.length }} ausgewählt
              </span>
            </div>

            <v-table
              v-if="archivEanPreview.items.length"
              density="compact"
              class="ean-preview-table"
            >
              <thead>
                <tr>
                  <th style="width:40px;"></th>
                  <th>CMMF</th>
                  <th>Kurztext (aktuell)</th>
                  <th>Werk</th>
                  <th>Lagerort</th>
                  <th>EAN-Auswahl (Archiv)</th>
                  <th>Quelle</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in archivEanPreview.items" :key="item.cmmf_nummer">
                  <td>
                    <v-checkbox
                      v-model="archivEanSelected[item.cmmf_nummer]"
                      hide-details
                      density="compact"
                    />
                  </td>
                  <td><strong>{{ item.cmmf_nummer }}</strong></td>
                  <td>{{ item.kurztext }}</td>
                  <td>{{ item.werk }}</td>
                  <td>{{ item.lagerort }}</td>
                  <td>
                    <template v-if="item.kandidaten.length === 1">
                      <span class="font-weight-medium">{{ item.kandidaten[0].ean }}</span>
                    </template>
                    <template v-else>
                      <v-select
                        v-model="archivEanChoice[item.cmmf_nummer]"
                        :items="item.kandidaten.map(k => ({ title: k.ean + (k.archiv_nr ? ' · ' + k.archiv_nr : ''), value: k.ean }))"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width:240px;"
                      />
                      <span class="text-caption text-warning">
                        {{ item.kandidaten.length }} Kandidaten
                      </span>
                    </template>
                  </td>
                  <td class="text-caption">
                    {{ getSelectedArchivCandidateInfo(item) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="archivEanPreviewDialog = false" :disabled="loading.archivEanSync">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            :disabled="archivEanPreviewSelectedCount === 0 || loading.archivEanSync"
            :loading="loading.archivEanSync"
            @click="applyArchivEanSelection"
          >
            <v-icon start>mdi-check</v-icon>
            {{ archivEanPreviewSelectedCount }} Zuordnung(en) übernehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: EAN-Übernahme per Kurztext-Match (Vorschau) -->
    <v-dialog v-model="kurztextPreviewDialog" max-width="1200" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon>mdi-text-search</v-icon>
          EAN-Übernahme per Kurztext-Match – Vorschau
        </v-card-title>
        <v-card-text>
          <div v-if="loading.kurztextSync" class="d-flex align-center" style="gap:8px;">
            <v-progress-circular indeterminate size="20" />
            Daten werden ermittelt …
          </div>

          <div v-else>
            <v-alert
              :type="kurztextPreview.items.length === 0 ? 'info' : 'success'"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div>
                Treffer per Kurztext-Match: <strong>{{ kurztextPreview.treffer_anzahl }}</strong>
                ({{ kurztextPreview.eindeutig }} eindeutig, {{ kurztextPreview.mehrdeutig }} mehrdeutig).
              </div>
              <div class="text-caption mt-1">
                Es werden nur Artikel angezeigt, bei denen sich die CMMF-Nummern unterscheiden
                und die Bezeichnung exakt übereinstimmt.
              </div>
            </v-alert>

            <div
              v-if="kurztextPreview.items.length"
              class="d-flex align-center mb-2"
              style="gap:12px;"
            >
              <v-checkbox
                :model-value="kurztextPreviewAllSelected"
                :indeterminate="kurztextPreviewSomeSelected && !kurztextPreviewAllSelected"
                hide-details
                density="compact"
                label="Alle auswählen"
                @update:model-value="toggleKurztextSelectAll"
              />
              <span class="text-caption text-medium-emphasis">
                {{ kurztextPreviewSelectedCount }} von {{ kurztextPreview.items.length }} ausgewählt
              </span>
            </div>

            <v-table
              v-if="kurztextPreview.items.length"
              density="compact"
              class="ean-preview-table"
            >
              <thead>
                <tr>
                  <th style="width:40px;"></th>
                  <th>CMMF (Inventur)</th>
                  <th>Kurztext (Inventur)</th>
                  <th>Werk / Lagerort</th>
                  <th>EAN-Auswahl</th>
                  <th>Match-CMMF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in kurztextPreview.items" :key="item.katalog_id">
                  <td>
                    <v-checkbox
                      v-model="kurztextSelected[item.katalog_id]"
                      hide-details
                      density="compact"
                    />
                  </td>
                  <td><strong>{{ item.katalog_cmmf }}</strong></td>
                  <td>{{ item.katalog_kurztext }}</td>
                  <td class="text-caption">
                    {{ [item.werk, item.lagerort].filter(Boolean).join(' / ') || '–' }}
                  </td>
                  <td>
                    <template v-if="item.kandidaten.length === 1">
                      <span class="font-weight-medium">{{ item.kandidaten[0].ean }}</span>
                    </template>
                    <template v-else>
                      <v-select
                        v-model="kurztextChoice[item.katalog_id]"
                        :items="item.kandidaten.map(k => ({
                          title: k.ean + ' · CMMF ' + (k.quelle_cmmf || '?'),
                          value: k.ean
                        }))"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width:280px;"
                      />
                      <span class="text-caption text-warning">
                        {{ item.kandidaten.length }} Kandidaten
                      </span>
                    </template>
                  </td>
                  <td class="text-caption">
                    {{ getSelectedKurztextCandidateInfo(item) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="kurztextPreviewDialog = false" :disabled="loading.kurztextSync">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            :disabled="kurztextPreviewSelectedCount === 0 || loading.kurztextSync"
            :loading="loading.kurztextSync"
            @click="applyKurztextSelection"
          >
            <v-icon start>mdi-check</v-icon>
            {{ kurztextPreviewSelectedCount }} Zuordnung(en) übernehmen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Artikel-Katalog bearbeiten / neu anlegen -->
    <!-- Dialog: Kurztext nachpflegen (Import ohne Kurztext) -->
    <v-dialog v-model="kurztextNachpflegeDialog" max-width="1100" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon color="warning">mdi-text-box-edit-outline</v-icon>
          Kurztext nachpflegen
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            {{ kurztextNachpflegeItems.length }} Artikel ohne Kurztext.
            Bitte Bezeichnung eintragen und speichern. CMMF bleibt unverändert.
          </v-alert>
          <v-progress-linear v-if="loading.kurztextNachpflege" indeterminate class="mb-3" />
          <v-table v-else density="compact" fixed-header height="480">
            <thead>
              <tr>
                <th>CMMF</th>
                <th>EAN</th>
                <th>Werk</th>
                <th>Lagerort</th>
                <th class="text-right">Soll</th>
                <th style="min-width:280px;">Kurztext *</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in kurztextNachpflegeItems" :key="row.id">
                <td><code>{{ row.cmmf_nummer }}</code></td>
                <td>{{ row.gtin_ean || '–' }}</td>
                <td>{{ row.werk || '–' }}</td>
                <td>{{ row.lagerort || '–' }}</td>
                <td class="text-right">{{ row.sollbestand }}</td>
                <td>
                  <v-text-field
                    v-model="kurztextNachpflegeDraft[row.id]"
                    density="compact"
                    variant="outlined"
                    hide-details
                    placeholder="Bezeichnung eingeben"
                  />
                </td>
              </tr>
              <tr v-if="kurztextNachpflegeItems.length === 0">
                <td colspan="6" class="text-center text-medium-emphasis py-6">
                  Keine Artikel ohne Kurztext.
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="kurztextNachpflegeDialog = false" :disabled="loading.kurztextNachpflegeSave">
            Schließen
          </v-btn>
          <v-btn
            color="warning"
            :loading="loading.kurztextNachpflegeSave"
            :disabled="kurztextNachpflegeFilledCount === 0"
            @click="saveKurztextNachpflege"
          >
            <v-icon start>mdi-content-save</v-icon>
            {{ kurztextNachpflegeFilledCount }} Kurztext(e) speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="katalogEditDialog" max-width="800" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon :color="katalogEditMode === 'create' ? 'success' : 'primary'">
            {{ katalogEditMode === 'create' ? 'mdi-plus-box' : 'mdi-pencil' }}
          </v-icon>
          {{ katalogEditMode === 'create' ? 'Neuer Artikel anlegen' : 'Artikel bearbeiten' }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="katalogEditMode === 'create'" type="warning" variant="tonal" density="compact" class="mb-3">
            Diese Neuanlage wird im Audit-Log mit Hash-Chain dokumentiert.
            Die Inventur gilt damit als gestartet.
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="katalogEditForm.cmmf_nummer"
                label="CMMF-Nummer *"
                :readonly="katalogEditMode === 'edit'"
                :variant="katalogEditMode === 'edit' ? 'filled' : 'outlined'"
                density="comfortable"
                hide-details
                prepend-inner-icon="mdi-identifier"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="katalogEditForm.gtin_ean"
                label="EAN-Code"
                density="comfortable"
                hide-details="auto"
                :rules="[v => !v || /^\d{6,18}$/.test(String(v).trim()) || 'Nur Ziffern, 6–18 Stellen']"
                prepend-inner-icon="mdi-barcode"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="katalogEditForm.kurztext"
                :label="katalogEditMode === 'create' ? 'Kurztext *' : 'Kurztext'"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="katalogEditForm.werk" label="Werk" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="katalogEditForm.lagerort" label="Lagerort" density="comfortable" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="katalogEditForm.sollbestand"
                label="Sollbestand"
                type="number"
                density="comfortable"
                hide-details
              />
            </v-col>
          </v-row>
          <v-alert
            v-if="katalogEditResult.show"
            :type="katalogEditResult.type"
            variant="tonal"
            class="mt-3"
          >
            <strong>{{ katalogEditResult.title }}</strong>
            <div v-if="katalogEditResult.message" class="mt-1">{{ katalogEditResult.message }}</div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeKatalogEdit" :disabled="loading.katalogEdit">Abbrechen</v-btn>
          <v-btn
            color="success"
            :loading="loading.katalogEdit"
            :disabled="!katalogEditForm.cmmf_nummer || (katalogEditMode === 'create' && !katalogEditForm.kurztext)"
            @click="saveKatalogEdit"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Bestätigung Mehrfach-EAN-Zuordnung -->
    <v-dialog v-model="eanDuplicateDialog" max-width="640" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-barcode-scan</v-icon>
          EAN ist mehreren Artikeln zugeordnet
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Die EAN <code><strong>{{ eanDuplicateData.ean }}</strong></code> ist bereits
            folgenden Artikel(n) im Katalog zugeordnet:
          </p>
          <v-table density="compact" class="mb-3">
            <thead>
              <tr>
                <th>CMMF</th>
                <th>Bezeichnung</th>
                <th>Werk</th>
                <th>Lagerort</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(k, i) in eanDuplicateData.konflikte" :key="i">
                <td><strong>{{ k.cmmf_nummer }}</strong></td>
                <td>{{ k.kurztext }}</td>
                <td>{{ k.werk }}</td>
                <td>{{ k.lagerort }}</td>
              </tr>
            </tbody>
          </v-table>
          <v-alert type="info" variant="tonal" density="compact" class="mb-0">
            <strong>Hinweis:</strong> Mehrfach-Zuordnung einer EAN ist erlaubt
            (z. B. gleicher Artikel in verschiedenen Werken). Beim späteren Scan zeigt
            der Scanner einen Auswahldialog für die richtige CMMF. Die Bestätigung wird
            im Audit-Log dokumentiert.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="eanDuplicateDialog = false" :disabled="loading.katalogEdit">
            Abbrechen
          </v-btn>
          <v-spacer />
          <v-btn
            color="warning"
            :loading="loading.katalogEdit"
            @click="confirmDuplicateEanAndSave"
          >
            <v-icon start>mdi-check-bold</v-icon>
            Trotzdem speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Paletten-Inhalt -->
    <v-dialog v-model="paletteDetailDialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-pallet</v-icon>
            Palette {{ paletteDetail?.paletten_nr || '' }}
            <v-chip
              v-if="paletteDetail?.status"
              :color="paletteDetail.status === 'abgeschlossen' ? 'green' : paletteDetail.status === 'erfassung' ? 'blue' : 'grey'"
              size="small"
              class="ml-3"
            >
              {{ paletteDetail.status }}
            </v-chip>
          </div>
          <v-btn icon variant="text" @click="paletteDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="paletteDetail">
          Erfasst von <strong>{{ paletteDetail.erstellt_von_name || '–' }}</strong>
          am {{ formatDateTime(paletteDetail.erfassung_datum) }}
          <span v-if="paletteDetail.abgeschlossen_am">
            · Abgeschlossen am {{ formatDateTime(paletteDetail.abgeschlossen_am) }}
          </span>
          · {{ paletteDetailPositionen.length }} Position(en)
          · <strong>{{ paletteDetailGesamtstueck }}</strong> Stk gesamt
        </v-card-subtitle>
        <v-divider />
        <v-card-text style="max-height:65vh;">
          <v-progress-linear v-if="paletteDetailLoading" indeterminate />
          <v-alert
            v-else-if="paletteDetailPositionen.length === 0"
            type="info"
            variant="tonal"
            density="compact"
          >
            Keine aktiven Positionen auf dieser Palette.
          </v-alert>
          <v-data-table
            v-else
            :headers="[
              { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
              { title: 'EAN', key: 'gtin_ean', sortable: true },
              { title: 'Kurztext', key: 'kurztext', sortable: true },
              { title: 'Werk', key: 'werk', sortable: true },
              { title: 'Lager', key: 'lagerort', sortable: true },
              { title: 'Ist', key: 'istbestand', align: 'end', sortable: true },
              { title: 'Erfasst', key: 'scan_zeitpunkt', sortable: true },
              { title: 'Von', key: 'gescannt_von_name', sortable: true }
            ]"
            :items="paletteDetailPositionen"
            :items-per-page="100"
            density="compact"
          >
            <template v-slot:[`item.scan_zeitpunkt`]="{ item }">
              {{ item.scan_zeitpunkt ? formatDateTime(item.scan_zeitpunkt) : '–' }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="paletteDetail"
            color="info"
            variant="tonal"
            @click="openPalettenscheinDialog(paletteDetail); paletteDetailDialog = false;"
          >
            <v-icon start>mdi-printer</v-icon>
            Palettenschein drucken
          </v-btn>
          <v-btn variant="text" @click="paletteDetailDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Korrektur-Buchung erfassen -->
    <v-dialog v-model="korrekturDialog" max-width="780" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="warning">mdi-file-document-edit-outline</v-icon>
            Korrekturbuchung erfassen
          </div>
          <v-btn icon variant="text" @click="korrekturDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" density="compact" class="mb-4" icon="mdi-shield-lock-outline">
            Der archivierte Stichtagsbestand wird <strong>nicht</strong> verändert.
            Diese Korrektur wird als separate Buchung mit Hash-Chain protokolliert.
          </v-alert>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4" icon="mdi-information-outline">
            <strong>Wichtig:</strong> Eine Korrektur betrifft immer
            <u>genau eine Palette</u> (Position). Wenn dieselbe CMMF auf
            mehreren Paletten erfasst wurde, ergibt sich der Gesamt-IST
            aus der Summe aller Paletten – die unten gezeigte Position
            kann also nur einen Teil davon enthalten.
          </v-alert>
          <v-text-field
            v-model="korrekturSearch"
            label="Position suchen (CMMF, EAN, Kurztext, Palette)"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            class="mb-3"
            clearable
          />
          <v-autocomplete
            v-model="korrekturForm.position_id"
            :items="filteredKorrekturPositionen"
            item-value="position_id"
            :item-title="(p) => `${p.cmmf_nummer || '–'} | ${p.kurztext || ''} | Palette ${p.paletten_nr || '–'} | aktuell ${Number(p.bestand_alt || 0) + Number(p.korrektur_summe || 0)} Stk`"
            label="Position auswählen"
            :loading="korrekturLoading"
            density="compact"
            class="mb-3"
            clearable
          />
          <v-card v-if="selectedKorrekturPosition" variant="tonal" color="info" class="mb-3">
            <v-card-text class="py-2">
              <div><strong>{{ selectedKorrekturPosition.kurztext || '–' }}</strong></div>
              <div class="text-caption">
                CMMF: {{ selectedKorrekturPosition.cmmf_nummer || '–' }}
                · EAN: {{ selectedKorrekturPosition.gtin_ean || '–' }}
                · Werk/Lager: {{ [selectedKorrekturPosition.werk, selectedKorrekturPosition.lagerort].filter(Boolean).join(' / ') || '–' }}
                · Palette: {{ selectedKorrekturPosition.paletten_nr }}
              </div>
              <div class="mt-1">
                Stichtagsbestand: <strong>{{ selectedKorrekturPosition.bestand_alt }}</strong>
                <span v-if="Number(selectedKorrekturPosition.korrektur_summe || 0) !== 0">
                  · Bisherige Korrekturen:
                  <strong :class="Number(selectedKorrekturPosition.korrektur_summe) > 0 ? 'text-success' : 'text-error'">
                    {{ Number(selectedKorrekturPosition.korrektur_summe) > 0 ? '+' : '' }}{{ selectedKorrekturPosition.korrektur_summe }}
                  </strong>
                </span>
                · Aktuell wirksam (diese Palette): <strong>{{ aktuellerBestandKorrektur }}</strong>
              </div>
              <div
                v-if="Number(selectedKorrekturPosition.anzahl_positionen_cmmf || 0) > 1"
                class="mt-1 text-caption text-medium-emphasis"
              >
                Hinweis: Diese CMMF ist im Archiv auf
                <strong>{{ selectedKorrekturPosition.anzahl_positionen_cmmf }}</strong> Paletten erfasst
                · Gesamt-IST (CMMF, alle Paletten): <strong>{{ selectedKorrekturPosition.gesamt_ist_cmmf }}</strong>
              </div>
            </v-card-text>
          </v-card>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="korrekturForm.bestand_neu"
                type="number"
                min="0"
                label="Neuer Bestand (Stk)"
                :disabled="!selectedKorrekturPosition"
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-select
                v-model="korrekturForm.grund_kategorie"
                :items="grundKategorien"
                label="Grund-Kategorie *"
              />
            </v-col>
          </v-row>
          <v-textarea
            v-model="korrekturForm.grund_text"
            label="Ausführliche Begründung * (Pflichtfeld, mind. 5 Zeichen)"
            rows="3"
            counter
          />
          <v-text-field
            v-model="korrekturForm.belegverweis"
            label="Belegverweis (optional, z. B. Foto-Nr., externer Beleg)"
            density="compact"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="korrekturDialog = false">Abbrechen</v-btn>
          <v-btn
            color="warning"
            :disabled="!korrekturForm.position_id || !korrekturForm.grund_kategorie || (korrekturForm.grund_text || '').trim().length < 5"
            @click="submitKorrektur"
          >
            <v-icon start>mdi-content-save-check</v-icon>
            Korrektur speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Inventur archivieren & neu starten -->
    <v-dialog v-model="archivDialog" max-width="640">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-archive-arrow-down</v-icon>
          Inventur archivieren & neu starten
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="paletenInErfassungCount > 0"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            <strong>Achtung:</strong> Es befinden sich noch
            <strong>{{ paletenInErfassungCount }}</strong> Palette(n) in laufender Erfassung.
            Bitte zuerst alle Paletten abschließen, bevor die Inventur archiviert werden kann.
          </v-alert>
          <v-alert
            v-else
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            Mit dieser Aktion wird die <strong>komplette aktuelle Inventur</strong>
            (Paletten, Positionen, Audit-Log <u>und</u> Artikel-Katalog) in ein Archiv verschoben.
            Eine neue Inventur startet anschließend mit leerem Katalog
            (Re-Import des Sollbestands erforderlich).
            <br><br>
            Die archivierten Daten bleiben dauerhaft erhalten und können später
            unter dem Archiv-Eintrag wieder eingesehen werden.
          </v-alert>

          <div v-if="paletenInErfassungCount === 0" class="archiv-summary mb-4">
            <div><strong>Aktuell in der Inventur:</strong></div>
            <ul class="ml-4">
              <li>Paletten: {{ palettenAktuell }} ({{ palettenAbgeschlossen }} abgeschlossen)</li>
              <li>Artikel im Katalog: {{ kompaktliste.length }}</li>
              <li>Gesamt-IST: {{ kompaktGesamtIst }} Stk</li>
              <li>Gesamt-SOLL: {{ kompaktGesamtSoll }} Stk</li>
            </ul>
          </div>

          <v-text-field
            v-model="archivForm.name"
            label="Bezeichnung des Archivs (optional)"
            placeholder="z. B. Jahresinventur 2026"
            :disabled="paletenInErfassungCount > 0 || loading.archiv"
          />
          <v-textarea
            v-model="archivForm.notiz"
            label="Notiz (optional)"
            rows="2"
            :disabled="paletenInErfassungCount > 0 || loading.archiv"
          />

          <v-checkbox
            v-model="archivForm.bestaetigt"
            :disabled="paletenInErfassungCount > 0 || loading.archiv"
            label="Ich habe verstanden, dass die aktuelle Inventur danach leer ist und der Katalog neu importiert werden muss."
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="loading.archiv" @click="archivDialog = false">
            Abbrechen
          </v-btn>
          <v-btn
            color="warning"
            :disabled="paletenInErfassungCount > 0 || !archivForm.bestaetigt || loading.archiv"
            :loading="loading.archiv"
            @click="confirmArchivieren"
          >
            <v-icon start>mdi-archive-arrow-down</v-icon>
            Jetzt archivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Bezeichnung der laufenden Inventur -->
    <v-dialog v-model="inventurNameDialog" max-width="640">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2" color="primary">mdi-rename-box</v-icon>
          Bezeichnung der laufenden Inventur
        </v-card-title>
        <v-card-text>
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Die Bezeichnung erscheint im Dropdown <strong>„Anzeige"</strong>
            und wird beim späteren Archivieren automatisch in den
            Archiv-Datensatz übernommen. Die Änderung wird im
            <strong>Audit-Log</strong> mit Hash-Chain dokumentiert.
          </v-alert>
          <v-text-field
            v-model="inventurNameForm.name"
            label="Bezeichnung"
            placeholder="z. B. Jahresinventur 2026"
            counter="255"
            maxlength="255"
            :disabled="inventurNameSaving"
            autofocus
          />
          <v-textarea
            v-model="inventurNameForm.notiz"
            label="Notiz (optional)"
            rows="2"
            :disabled="inventurNameSaving"
          />

          <v-divider class="my-3" />
          <div class="text-subtitle-2 mb-2 d-flex align-center">
            <v-icon size="small" class="mr-1">mdi-barcode</v-icon>
            Paletten-Nummern-Format
          </div>
          <v-alert
            v-if="aktuelleMeta.paletten_format_locked"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Das Format kann nicht mehr geändert werden, weil bereits
            <strong>{{ aktuelleMeta.paletten_count_aktuell }}</strong>
            Palette(n) in der laufenden Inventur erfasst wurden.
            Aktuelles Format:
            <code>{{ aktuelleMeta.paletten_prefix_effektiv }}-{{ aktuelleMeta.paletten_jahr_effektiv }}-XXXXX</code>
          </v-alert>
          <v-alert
            v-else
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Solange noch keine Palette gescannt wurde, kann das Format frei
            gewählt werden (z. B. Geschäftsjahr 2025 obwohl Erfassung Anfang 2026 läuft).
          </v-alert>
          <div class="d-flex ga-2">
            <v-text-field
              v-model="inventurNameForm.paletten_prefix"
              label="Präfix"
              hint="1-10 Zeichen, A-Z / 0-9"
              persistent-hint
              maxlength="10"
              style="max-width: 140px"
              :disabled="inventurNameSaving || aktuelleMeta.paletten_format_locked"
              @input="inventurNameForm.paletten_prefix = String(inventurNameForm.paletten_prefix || '').toUpperCase().replace(/[^A-Z0-9]/g, '')"
            />
            <v-text-field
              v-model.number="inventurNameForm.paletten_jahr"
              label="Jahr"
              type="number"
              :min="2000"
              :max="2099"
              hint="z. B. 2025"
              persistent-hint
              style="max-width: 140px"
              :disabled="inventurNameSaving || aktuelleMeta.paletten_format_locked"
            />
            <div class="flex-grow-1 d-flex align-center">
              <div class="text-caption text-medium-emphasis">
                Nächste Paletten-Nr.:<br>
                <code class="text-body-2">{{ palettenBeispielVorschau }}</code>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="inventurNameSaving"
            @click="inventurNameDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            :loading="inventurNameSaving"
            @click="saveInventurName"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

/** Polling-Intervall für Live-Aktualisierung der laufenden Inventur (ms). */
const OFFICE_LIVE_POLL_MS = 10000;

const authStore = useAuthStore();
const toast = useToast();

// ========================================
// STATE
// ========================================

const activeTab = ref('einzelliste');
const einzelliste = ref([]);
const kompaktliste = ref([]);
const auditLog = ref([]);
const importPasteText = ref('');
const importHasHeader = ref(true);
const importHasEan = ref(false);
const parsedImportRows = ref([]);
const paletten = ref([]);
const reopeningPaletteId = ref(null);
const deletingPaletteId = ref(null);
const palettenscheinDialog = ref(false);
const palettenscheinData = ref({ ansager: '', schreiber: '', kontrolle: '', palette_id: null });

// Mitarbeiter-Liste fuer Dropdowns "Gepackt von" / "Kontrolle"
const userOptions = ref([]);
const userOptionsLoading = ref(false);

const currentUserFullName = computed(() => {
  const u = authStore.user;
  if (!u) return '';
  const vn = (u.vorname || '').trim();
  const nn = (u.nachname || '').trim();
  const full = `${vn} ${nn}`.trim();
  return full || (u.loginname || '');
});

const sapExportDialog = ref(false);
const sapExportResult = ref({ filename: '', csvHash: '', anchorHash: '', anchorId: '', rows: '', sumIst: '', sumSoll: '', referenz: '', zeit: '' });
const sapExportConfirmDialog = ref(false);
const sapExportConfirm = ref({ referenz: '', bestaetigt: false, busy: false });
const korrekturenExportDialog = ref(false);
const korrekturenExportResult = ref({ filename: '', csvHash: '', anchorHash: '', anchorId: '', rows: '', sumDiff: '', zeit: '' });

const endloslisteDialog = ref(false);
const endloslisteFilter = ref({
  jahr: null,                                   // optionaler Filter auf inventur_paletten.jahr (Anlegejahr)
  druckJahr: new Date().getFullYear() - 1,      // Jahr im Druck-Header (Inventur-Jahr)
  status: '',
  ansager: '',
  schreiber: ''
});
const endloslisteStatusOptions = [
  { title: 'Alle', value: '' },
  { title: 'In Erfassung', value: 'erfassung' },
  { title: 'Abgeschlossen', value: 'abgeschlossen' },
  { title: 'Archiv', value: 'archiv' }
];

const loading = ref({
  einzelliste: false,
  kompaktliste: false,
  log: false,
  import: false,
  paletten: false,
  endlosliste: false,
  auditPrint: false,
  korrekturenPrint: false,
  eanSync: false,
  archiv: false,
  verifyChain: false,
  eanLookupStats: false,
  katalogStats: false,
  eanLookupImport: false,
  eanLookupClear: false,
  eanLookupSync: false,
  kurztextSync: false,
  kurztextNachpflege: false,
  kurztextNachpflegeSave: false,
  katalogVerwaltung: false,
  katalogEdit: false,
  archivEanSync: false
});

// EAN-Lookup-Tabelle (globale EAN ↔ CMMF ↔ Bezeichnung-Liste)
const eanLookupPasteText = ref('');
const eanLookupHasHeader = ref(false);
const eanLookupReplaceAll = ref(false);
const parsedEanLookupRows = ref([]);
const eanLookupStats = ref({ gesamt: 0, letzte_aktualisierung: null });
const katalogStats = ref({ gesamt: 0, mit_ean: 0, ohne_ean: 0, ohne_kurztext: 0, letzte_aktualisierung: null });
const kurztextNachpflegeDialog = ref(false);
const kurztextNachpflegeItems = ref([]);
const kurztextNachpflegeDraft = ref({});
const kurztextNachpflegeFilledCount = computed(() =>
  kurztextNachpflegeItems.value.filter((row) => {
    const v = String(kurztextNachpflegeDraft.value[row.id] || '').trim();
    return v !== '';
  }).length
);
const eanLookupResult = ref({
  show: false,
  type: 'success',
  title: '',
  total: 0,
  eingefuegt: 0,
  aktualisiert: 0,
  uebersprungen: 0,
  geloescht: 0,
  fehler: []
});
const headersEanLookupPreview = [
  { title: 'EAN', key: 'ean' },
  { title: 'CMMF', key: 'cmmf_nummer' },
  { title: 'Bezeichnung', key: 'bezeichnung' }
];
const parsedEanLookupPreview = computed(() => parsedEanLookupRows.value.slice(0, 25));

const archivDialog = ref(false);
const archivForm = ref({ name: '', notiz: '', bestaetigt: false });

// Archiv-Auswahl: 0 = aktuell laufende Inventur, > 0 = archivierter Lauf
const selectedArchivId = ref(0);
const archivList = ref([]);
// Bezeichnung/Notiz der aktuell laufenden (nicht archivierten) Inventur.
// Wird vom Backend (inventur_aktuelle_meta) geladen und beim Archivieren
// in inventur_archive.name uebernommen.
const aktuelleMeta = ref({
  name: '',
  notiz: '',
  paletten_prefix: null,
  paletten_jahr: null,
  paletten_prefix_effektiv: 'INV',
  paletten_jahr_effektiv: new Date().getFullYear(),
  paletten_beispiel: '',
  paletten_format_locked: false,
  paletten_count_aktuell: 0,
});
const inventurNameDialog = ref(false);
const inventurNameForm = ref({
  name: '',
  notiz: '',
  paletten_prefix: 'INV',
  paletten_jahr: new Date().getFullYear(),
});
const inventurNameSaving = ref(false);
const isViewingArchiv = computed(() => selectedArchivId.value > 0);
const archivOptions = computed(() => {
  const aktuellTitle = aktuelleMeta.value.name
    ? `Aktuell: ${aktuelleMeta.value.name}`
    : 'Aktuell laufende Inventur';
  const opts = [{ value: 0, title: aktuellTitle }];
  for (const a of archivList.value) {
    const datum = a.archiviert_am ? new Date(a.archiviert_am).toLocaleDateString('de-DE') : '';
    const label = a.name
      ? `${a.archiv_nr} – ${a.name} (${datum})`
      : `${a.archiv_nr} (${datum})`;
    opts.push({ value: a.id, title: label });
  }
  return opts;
});

const filterEinzelliste = ref({
  suche: '',
  onlyAbweichungen: false
});

// 'einzeln' = jede Scan-Position einzeln, 'zusammengefasst' = pro CMMF aggregiert
const einzellisteAnsicht = ref('einzeln');

const filterKompaktliste = ref({
  suche: '',
  onlyAbweichungen: false
});

const expandedKompaktliste = ref([]);
const kompaktArtikelPaletten = ref({});
const loadingArtikelPaletten = ref({});

const importResult = ref({
  show: false,
  type: 'info',
  title: '',
  total: 0,
  success: 0,
  errors: 0,
  messages: [],
  sollChanges: null,
  sollDiffSha: null,
  sollDiffBeleg: null,
  sollDiffBelegName: null,
  sollDiffBelegId: null,
  sollDiffBelegStatus: null,
  sollDiffBelegError: null,
  ohneKurztext: 0
});

/** Chunk-Größe für große Paste-Imports (~14k Zeilen) – Fortschritt + weniger Timeouts */
const IMPORT_CHUNK_SIZE = 750;
const importProgress = ref({
  active: false,
  done: 0,
  total: 0,
  percent: 0,
  chunkIndex: 0,
  chunkTotal: 0,
  label: ''
});

const eanSyncResult = ref({
  show: false,
  type: 'info',
  title: '',
  aktualisiert: 0,
  uebersprungen: 0,
  nachher: 0,
  gesamt: 0
});

const eanPreviewDialog = ref(false);
const eanPreview = ref({
  ohne_ean_gesamt: 0,
  katalog_gesamt: 0,
  treffer_anzahl: 0,
  eindeutig: 0,
  mehrdeutig: 0,
  items: []
});
const eanSelected = ref({}); // { cmmf_nummer: true/false }
const eanChoice = ref({});   // { cmmf_nummer: gewählte EAN }

const eanPreviewSelectedCount = computed(() =>
  Object.values(eanSelected.value).filter(Boolean).length
);
const eanPreviewAllSelected = computed(() =>
  eanPreview.value.items.length > 0 &&
  eanPreviewSelectedCount.value === eanPreview.value.items.length
);
const eanPreviewSomeSelected = computed(() => eanPreviewSelectedCount.value > 0);

// ----- EAN-Übernahme aus Lookup-Tabelle (analog Refurbish) -----
const eanLookupSyncResult = ref({
  show: false,
  type: 'info',
  title: '',
  aktualisiert: 0,
  uebersprungen: 0,
  nachher: 0,
  gesamt: 0
});
const eanLookupPreviewDialog = ref(false);
const eanLookupPreview = ref({
  ohne_ean_gesamt: 0,
  katalog_gesamt: 0,
  treffer_anzahl: 0,
  eindeutig: 0,
  mehrdeutig: 0,
  items: []
});
const eanLookupSelected = ref({});
const eanLookupChoice = ref({});
const eanLookupPreviewSelectedCount = computed(() =>
  Object.values(eanLookupSelected.value).filter(Boolean).length
);
const eanLookupPreviewAllSelected = computed(() =>
  eanLookupPreview.value.items.length > 0 &&
  eanLookupPreviewSelectedCount.value === eanLookupPreview.value.items.length
);
const eanLookupPreviewSomeSelected = computed(() => eanLookupPreviewSelectedCount.value > 0);

// ----- EAN-Übernahme per Kurztext-Match (Duplikat-Erkennung mit abweichender CMMF) -----
const kurztextSyncResult = ref({
  show: false,
  type: 'info',
  title: '',
  aktualisiert: 0,
  uebersprungen: 0,
  nachher: 0,
  gesamt: 0
});
const kurztextPreviewDialog = ref(false);
const kurztextPreview = ref({
  treffer_anzahl: 0,
  eindeutig: 0,
  mehrdeutig: 0,
  items: []
});
const kurztextSelected = ref({}); // key = katalog_id
const kurztextChoice = ref({});   // key = katalog_id, value = gewählte EAN
const kurztextPreviewSelectedCount = computed(() =>
  Object.values(kurztextSelected.value).filter(Boolean).length
);
const kurztextPreviewAllSelected = computed(() =>
  kurztextPreview.value.items.length > 0 &&
  kurztextPreviewSelectedCount.value === kurztextPreview.value.items.length
);
const kurztextPreviewSomeSelected = computed(() => kurztextPreviewSelectedCount.value > 0);

// ----- EAN-Übernahme aus archivierten Inventuren -----
const archivEanSyncResult = ref({
  show: false,
  type: 'info',
  title: '',
  aktualisiert: 0,
  uebersprungen: 0,
  nachher: 0,
  gesamt: 0
});
const archivEanPreviewDialog = ref(false);
const archivEanPreview = ref({
  ohne_ean_gesamt: 0,
  katalog_gesamt: 0,
  treffer_anzahl: 0,
  eindeutig: 0,
  mehrdeutig: 0,
  items: []
});
const archivEanSelected = ref({});
const archivEanChoice = ref({});
// 0 = alle Archive berücksichtigen, > 0 = einzelnes Archiv
const archivEanSourceId = ref(0);
const archivEanSourceOptions = computed(() => {
  const opts = [{ value: 0, title: 'Alle archivierten Inventuren' }];
  for (const a of archivList.value) {
    const datum = a.archiviert_am
      ? new Date(a.archiviert_am).toLocaleDateString('de-DE')
      : '';
    const label = a.name
      ? `${a.archiv_nr} – ${a.name} (${datum})`
      : `${a.archiv_nr} (${datum})`;
    opts.push({ value: a.id, title: label });
  }
  return opts;
});
const archivEanSourceLabel = computed(() => {
  const opt = archivEanSourceOptions.value.find(o => o.value === archivEanSourceId.value);
  return opt ? opt.title : 'Archiv';
});
const archivEanPreviewSelectedCount = computed(() =>
  Object.values(archivEanSelected.value).filter(Boolean).length
);
const archivEanPreviewAllSelected = computed(() =>
  archivEanPreview.value.items.length > 0 &&
  archivEanPreviewSelectedCount.value === archivEanPreview.value.items.length
);
const archivEanPreviewSomeSelected = computed(() => archivEanPreviewSelectedCount.value > 0);

// ----- Artikel-Katalog verwalten (Tabellen-Ansicht im Office) -----
const katalogVerwaltungListe = ref([]);
const katalogVerwaltungFilter = ref('');
const katalogVerwaltungNurOhneEan = ref(false);
// Liste ist initial ausgeblendet (kann ueber Button geoeffnet werden).
// Spart beim Tab-Wechsel "Artikel-Import" das Laden ggf. tausender Eintraege.
const katalogVerwaltungVisible = ref(false);
const katalogVerwaltungHeaders = [
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'gtin_ean', sortable: true },
  { title: 'Kurztext', key: 'kurztext', sortable: true },
  { title: 'Werk', key: 'werk', sortable: true },
  { title: 'Lagerort', key: 'lagerort', sortable: true },
  { title: 'Soll', key: 'sollbestand', sortable: true, align: 'end' },
  { title: '', key: 'actions', sortable: false, align: 'end', width: 60 }
];
const katalogVerwaltungGefiltert = computed(() => {
  const q = (katalogVerwaltungFilter.value || '').trim().toLowerCase();
  return katalogVerwaltungListe.value.filter(r => {
    if (katalogVerwaltungNurOhneEan.value && r.gtin_ean) return false;
    if (!q) return true;
    return ['cmmf_nummer', 'gtin_ean', 'kurztext', 'werk', 'lagerort']
      .some(k => String(r[k] || '').toLowerCase().includes(q));
  });
});
const katalogEditDialog = ref(false);
const katalogEditMode = ref('edit'); // 'edit' | 'create'
const katalogEditForm = ref({
  cmmf_nummer: '',
  gtin_ean: '',
  kurztext: '',
  werk: '',
  lagerort: '',
  sollbestand: null
});
const katalogEditResult = ref({ show: false, type: 'info', title: '', message: '' });
// Confirm-Dialog für Mehrfach-EAN-Zuordnung. Wird gezeigt, wenn das Backend mit
// status:"duplicate_ean" antwortet (gleiche EAN bereits anderen CMMFs zugeordnet).
const eanDuplicateDialog = ref(false);
const eanDuplicateData = ref({ ean: '', konflikte: [], message: '' });

// ========================================
// COMPUTED
// ========================================

const headersEinzelliste = [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Erfasst von', key: 'erstellt_von_name', sortable: true },
  { title: 'Gescannt am', key: 'scan_zeitpunkt', sortable: true },
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'gtin_ean', sortable: true },
  { title: 'Artikel', key: 'kurztext', sortable: true },
  { title: 'Werk', key: 'werk', sortable: true },
  { title: 'Lagerort', key: 'lagerort', sortable: true },
  { title: 'Soll', key: 'sollbestand', sortable: true },
  { title: 'IST', key: 'istbestand', sortable: true },
  { title: 'Differenz (kumuliert)', key: 'kumulative_differenz', sortable: true }
];

const headersEinzellisteAggregiert = [
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'gtin_ean', sortable: true },
  { title: 'Artikel', key: 'kurztext', sortable: true },
  { title: 'Werk', key: 'werk', sortable: true },
  { title: 'Lagerort', key: 'lagerort', sortable: true },
  { title: 'Paletten-Nrn.', key: 'paletten_nrs', sortable: true },
  { title: 'Scans', key: 'anzahl_scans', sortable: true },
  { title: 'Letzter Scan', key: 'scan_zeitpunkt', sortable: true },
  { title: 'Soll', key: 'sollbestand', sortable: true },
  { title: 'IST (Gesamt)', key: 'istbestand', sortable: true },
  { title: 'Differenz', key: 'kumulative_differenz', sortable: true }
];

const headersKompaktliste = [
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'gtin_ean', sortable: true },
  { title: 'Artikel', key: 'kurztext', sortable: true },
  { title: 'Werk', key: 'werk', sortable: true },
  { title: 'Lagerort', key: 'lagerort', sortable: true },
  { title: 'Soll', key: 'sollbestand', sortable: true },
  { title: 'IST (Gesamt)', key: 'gesamt_istbestand', sortable: true },
  { title: 'Paletten', key: 'anzahl_paletten', sortable: true },
  { title: 'Differenz', key: 'gesamt_differenz', sortable: true }
];

const headersLog = [
  { title: 'Aktion', key: 'aktion', sortable: true },
  { title: 'Benutzer', key: 'username', sortable: true },
  { title: 'Beschreibung', key: 'beschreibung', sortable: false },
  { title: 'Zeitstempel', key: 'created_at', sortable: true },
  { title: 'Hash (SHA-256)', key: 'record_hash', sortable: false, width: 280 }
];

const headersPaletten = [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Erfasst von', key: 'erstellt_von_name', sortable: true },
  { title: 'Erfasst am', key: 'erfassung_datum', sortable: true },
  { title: 'Positionen', key: 'anzahl_positionen', sortable: true },
  { title: 'Abgeschlossen am', key: 'abgeschlossen_am', sortable: true },
  { title: 'Aktion', key: 'actions', sortable: false }
];

const headersImportPreview = [
  { title: 'CMMF', key: 'cmmf_nummer' },
  { title: 'EAN', key: 'gtin_ean' },
  { title: 'Kurztext', key: 'kurztext' },
  { title: 'Werk', key: 'werk' },
  { title: 'Lagerort', key: 'lagerort' },
  { title: 'Soll', key: 'sollbestand' }
];

const parsedImportPreview = computed(() => parsedImportRows.value.slice(0, 20));

const toNumber = (value) => Number.parseInt(value, 10) || 0;

const normalizeEinzellisteItem = (item) => ({
  ...item,
  palette_id: item.palette_id != null ? Number(item.palette_id) : null,
  sollbestand: toNumber(item.sollbestand),
  istbestand: toNumber(item.istbestand),
  differenz: toNumber(item.differenz)
});

const normalizeKompaktlisteItem = (item) => ({
  ...item,
  sollbestand: toNumber(item.sollbestand),
  gesamt_istbestand: toNumber(item.gesamt_istbestand),
  anzahl_paletten: toNumber(item.anzahl_paletten),
  gesamt_differenz: toNumber(item.gesamt_differenz)
});

const filteredEinzelliste = computed(() => {
  // Kumulative Differenz pro CMMF berechnen: Einträge je CMMF nach
  // Scan-Zeitpunkt sortieren und IST aufaddieren. So zeigt der zweite
  // Scan einer CMMF nicht erneut die volle Soll-Differenz, sondern den
  // tatsächlich noch fehlenden Rest.
  const tsOf = (r) => {
    if (!r.scan_zeitpunkt) return 0;
    return new Date(String(r.scan_zeitpunkt).replace(' ', 'T')).getTime() || 0;
  };
  const groups = new Map();
  for (const row of einzelliste.value) {
    const key = row.cmmf_nummer || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  const enriched = new Map();
  for (const [, rows] of groups) {
    const sorted = [...rows].sort((a, b) => tsOf(a) - tsOf(b));
    const soll = Number(sorted[0]?.sollbestand) || 0;
    let kumIst = 0;
    for (const r of sorted) {
      kumIst += Number(r.istbestand) || 0;
      enriched.set(r, { ...r, kumulative_differenz: kumIst - soll });
    }
  }

  let filtered = einzelliste.value.map(r => enriched.get(r) || { ...r, kumulative_differenz: r.differenz });

  if (filterEinzelliste.value.onlyAbweichungen) {
    filtered = filtered.filter(item => item.kumulative_differenz !== 0);
  }

  if (!filterEinzelliste.value.suche) return filtered;

  const suche = filterEinzelliste.value.suche.toLowerCase();
  return filtered.filter(item =>
    item.paletten_nr?.toLowerCase().includes(suche) ||
    item.cmmf_nummer?.toLowerCase().includes(suche) ||
    item.kurztext?.toLowerCase().includes(suche)
  );
});

// Aggregierte Sicht der Einzelauflistung: pro CMMF eine Zeile mit IST-Summe,
// Paletten-Liste, letztem Scan-Zeitpunkt und Gesamt-Differenz. Nutzt die
// bereits gefilterte Einzelliste, damit Suche/Abweichungs-Filter greifen.
const aggregatedEinzelliste = computed(() => {
  const base = filteredEinzelliste.value;
  const groups = new Map();
  for (const row of base) {
    const key = row.cmmf_nummer || '';
    if (!groups.has(key)) {
      groups.set(key, {
        cmmf_nummer: row.cmmf_nummer,
        gtin_ean: row.gtin_ean,
        kurztext: row.kurztext,
        werk: row.werk,
        lagerort: row.lagerort,
        sollbestand: Number(row.sollbestand) || 0,
        istbestand: 0,
        anzahl_scans: 0,
        paletten_map: new Map(),
        scan_zeitpunkt: null
      });
    }
    const g = groups.get(key);
    g.istbestand += Number(row.istbestand) || 0;
    g.anzahl_scans += 1;
    if (row.paletten_nr) {
      const pid = row.palette_id != null ? Number(row.palette_id) : null;
      // Key nach Nr., damit Duplikat-Scans auf derselben Palette nicht doppelt erscheinen
      if (!g.paletten_map.has(row.paletten_nr)) {
        g.paletten_map.set(row.paletten_nr, { id: pid, nr: row.paletten_nr });
      }
    }
    const ts = row.scan_zeitpunkt ? new Date(String(row.scan_zeitpunkt).replace(' ', 'T')).getTime() : 0;
    const tsCur = g.scan_zeitpunkt ? new Date(String(g.scan_zeitpunkt).replace(' ', 'T')).getTime() : 0;
    if (ts && ts > tsCur) g.scan_zeitpunkt = row.scan_zeitpunkt;
  }
  const out = [];
  for (const g of groups.values()) {
    const paletten_links = Array.from(g.paletten_map.values())
      .sort((a, b) => String(a.nr).localeCompare(String(b.nr)));
    out.push({
      cmmf_nummer: g.cmmf_nummer,
      gtin_ean: g.gtin_ean,
      kurztext: g.kurztext,
      werk: g.werk,
      lagerort: g.lagerort,
      sollbestand: g.sollbestand,
      istbestand: g.istbestand,
      anzahl_scans: g.anzahl_scans,
      paletten_links,
      paletten_nrs: paletten_links.map((p) => p.nr).join(', '),
      scan_zeitpunkt: g.scan_zeitpunkt,
      kumulative_differenz: g.istbestand - g.sollbestand
    });
  }
  return out;
});

const filteredKompaktliste = computed(() => {
  let filtered = kompaktliste.value;

  if (filterKompaktliste.value.onlyAbweichungen) {
    filtered = filtered.filter(item => item.gesamt_differenz !== 0);
  }

  if (!filterKompaktliste.value.suche) return filtered;

  const suche = filterKompaktliste.value.suche.toLowerCase();
  return filtered.filter(item =>
    item.cmmf_nummer?.toLowerCase().includes(suche) ||
    item.gtin_ean?.toLowerCase().includes(suche) ||
    item.kurztext?.toLowerCase().includes(suche)
  );
});

const kompaktGesamtIst = computed(() => {
  return filteredKompaktliste.value.reduce((sum, item) => sum + toNumber(item.gesamt_istbestand), 0);
});

const kompaktGesamtSoll = computed(() => {
  return filteredKompaktliste.value.reduce((sum, item) => sum + toNumber(item.sollbestand), 0);
});

const kompaktGesamtDiff = computed(() => {
  return kompaktGesamtIst.value - kompaktGesamtSoll.value;
});

// Plausibilitäts-Check über die VOLLE Kompaktliste (nicht gefiltert!),
// damit ein gesetzter Suchfilter die Vorschau nicht "schönfärbt".
// Wird sowohl im Kompaktliste-Tab als auch im SAP-Export-Bestätigungsdialog gezeigt.
const kompaktPlausi = computed(() => {
  const all = kompaktliste.value || [];
  let total = all.length;
  let ohneScan = 0;          // gar nicht gescannt: ist=0, soll>0
  let summeAusbuchung = 0;   // Stück, die nach SAP als Fehlbestand gehen (= Σ soll bei ohneScan)
  let fehlbestand = 0;       // teilweise gescannt: ist>0 aber ist<soll
  let summeFehlbestand = 0;  // negative Differenz (als positive Zahl)
  let uebermenge = 0;        // ist>soll
  let summeUebermenge = 0;
  let exakt = 0;             // diff=0 mit soll>0
  let neuArtikel = 0;        // soll=0 aber ist>0 (nicht im SAP-Soll, dazugescannt)

  for (const item of all) {
    const ist = toNumber(item.gesamt_istbestand);
    const soll = toNumber(item.sollbestand);
    const diff = toNumber(item.gesamt_differenz);

    if (soll > 0 && ist === 0) {
      ohneScan++;
      summeAusbuchung += soll;
    } else if (soll === 0 && ist > 0) {
      neuArtikel++;
      summeUebermenge += ist;
      uebermenge++;
    } else if (diff > 0) {
      uebermenge++;
      summeUebermenge += diff;
    } else if (diff < 0) {
      fehlbestand++;
      summeFehlbestand += -diff;
    } else {
      // diff === 0: entweder echte Übereinstimmung (IST = SOLL > 0)
      // oder SOLL = 0 und IST = 0 (Artikel war im Katalog, hatte aber keinen Sollbestand
      // und wurde auch nicht gescannt → ebenfalls "exakt" im Sinne der Differenz).
      exakt++;
    }
  }

  return {
    total,
    ohneScan,
    summeAusbuchung,
    fehlbestand,
    summeFehlbestand,
    uebermenge,
    summeUebermenge,
    exakt,
    neuArtikel,
    hatOffenePaletten: paletenInErfassungCount.value > 0,
    offenePaletten: paletenInErfassungCount.value
  };
});

const paletenInErfassungCount = computed(() => {
  return paletten.value.filter(p => {
    const s = String(p.status || '').toLowerCase();
    return s !== 'abgeschlossen' && s !== 'archiv';
  }).length;
});

const palettenAktuell = computed(() => paletten.value.length);
const palettenAbgeschlossen = computed(() => {
  return paletten.value.filter(p => {
    const s = String(p.status || '').toLowerCase();
    return s === 'abgeschlossen' || s === 'archiv';
  }).length;
});

// ========================================
// METHODS
// ========================================

const fetchEinzelliste = async (opts = {}) => {
  const silent = !!opts.silent;
  if (!silent) loading.value.einzelliste = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_einzelauflistung', archiv_id: selectedArchivId.value }
    });

    if (response.data.status === 'success') {
      einzelliste.value = (response.data.daten || []).map(normalizeEinzellisteItem);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Einzelauflistung:', error);
    if (!silent) toast.error('Fehler beim Laden der Daten');
  } finally {
    if (!silent) loading.value.einzelliste = false;
  }
};

const fetchKompaktliste = async (opts = {}) => {
  const silent = !!opts.silent;
  if (!silent) loading.value.kompaktliste = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_kompaktliste', archiv_id: selectedArchivId.value }
    });

    if (response.data.status === 'success') {
      kompaktliste.value = (response.data.daten || []).map(normalizeKompaktlisteItem);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Kompaktliste:', error);
    if (!silent) toast.error('Fehler beim Laden der Daten');
  } finally {
    if (!silent) loading.value.kompaktliste = false;
  }
};

const fetchPaletten = async (opts = {}) => {
  const silent = !!opts.silent;
  if (!silent) loading.value.paletten = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_paletten', archiv_id: selectedArchivId.value }
    });
    if (response.data.status === 'success') {
      paletten.value = response.data.daten || response.data.paletten || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Paletten:', error);
    if (!silent) toast.error('Fehler beim Laden der Paletten');
  } finally {
    if (!silent) loading.value.paletten = false;
  }
};

const reopenPalette = async (palette) => {
  const isOfficeUser = !!(authStore.user?.office || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer);
  if (!isOfficeUser) {
    toast.error('Keine Berechtigung zum Wiederöffnen.');
    return;
  }
  if (!confirm(`Palette ${palette.paletten_nr} wirklich wieder zur Bearbeitung freigeben?`)) return;
  reopeningPaletteId.value = palette.id;
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'reopen_palette',
      palette_id: palette.id
    });
    if (response.data.status === 'success') {
      toast.success('Palette wurde wieder geöffnet');
      await fetchPaletten();
      // Drilldown-Cache leeren, damit neuer Status sichtbar wird
      kompaktArtikelPaletten.value = {};
    } else {
      toast.error(response.data.message || 'Fehler beim Wiederöffnen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Wiederöffnen der Palette');
  } finally {
    reopeningPaletteId.value = null;
  }
};

const deletePalette = async (palette) => {
  if (Number(palette.anzahl_positionen || 0) > 0) {
    toast.error('Palette enthält Positionen und kann nicht gelöscht werden');
    return;
  }
  if (!confirm(`Leere Palette ${palette.paletten_nr} wirklich löschen?`)) return;
  deletingPaletteId.value = palette.id;
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'delete_palette',
      palette_id: palette.id
    });
    if (response.data.status === 'success') {
      toast.success(response.data.message || 'Palette gelöscht');
      await fetchPaletten();
      await fetchLog();
    } else {
      toast.error(response.data.message || 'Fehler beim Löschen');
    }
  } catch (error) {
    console.error(error);
    toast.error(error.response?.data?.message || 'Fehler beim Löschen der Palette');
  } finally {
    deletingPaletteId.value = null;
  }
};

const openPalettenscheinDialog = (palette) => {
  // "Gescannt von" = tatsächliche Scanner laut Positionen (falls vorhanden),
  // sonst Ersteller der Palette, sonst aktuell angemeldeter User.
  const scanners = (palette && palette.scanner_names)
    ? String(palette.scanner_names).trim()
    : '';
  const ersteller = (palette && palette.erstellt_von_name)
    ? String(palette.erstellt_von_name).trim()
    : '';
  palettenscheinData.value = {
    ansager: '',
    schreiber: scanners || ersteller || currentUserFullName.value || '',
    kontrolle: '',
    palette_id: palette.id
  };
  palettenscheinDialog.value = true;
  // Mitarbeiterliste fuer die Dropdowns laden (einmalig gecached)
  if (userOptions.value.length === 0 && !userOptionsLoading.value) {
    fetchUserList();
  }
};

const fetchUserList = async () => {
  userOptionsLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_user_list' },
      withCredentials: true
    });
    if (response.data?.status === 'success' && Array.isArray(response.data.users)) {
      userOptions.value = response.data.users.map(u => u.fullname);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Mitarbeiterliste:', error);
  } finally {
    userOptionsLoading.value = false;
  }
};

const executePrintPalettenschein = async () => {
  const id = palettenscheinData.value.palette_id;
  const ansager = palettenscheinData.value.ansager || '';
  const schreiber = palettenscheinData.value.schreiber || '';
  const kontrolle = palettenscheinData.value.kontrolle || '';
  palettenscheinDialog.value = false;
  await printPalettenschein(id, ansager, schreiber, kontrolle);
};

const printPalettenschein = async (paletteId, ansager = '', schreiber = '', kontrolle = '') => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_palette', palette_id: paletteId }
    });
    if (response.data?.status !== 'success') {
      toast.error('Palette konnte nicht geladen werden');
      return;
    }
    const palette = response.data.palette;
    const positionen = response.data.positionen || [];
    let drucknummer = 1;
    let druckdatum = new Date().toLocaleString('de-DE');
    let auditAnchor = null;
    try {
      const druckResp = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
        action: 'log_palettenschein_druck',
        palette_id: paletteId,
        ansager_name: ansager,
        schreiber_name: schreiber
      });
      if (druckResp?.data?.status === 'success') {
        drucknummer = Number(druckResp.data.drucknummer || 1);
        if (druckResp.data.druckdatum) {
          druckdatum = new Date(String(druckResp.data.druckdatum).replace(' ', 'T'))
            .toLocaleString('de-DE');
        }
        if (druckResp.data.audit_anchor) {
          auditAnchor = druckResp.data.audit_anchor;
        }
      }
    } catch (e) { /* nicht kritisch */ }
    const { html, footerLeft } = buildPalettenscheinHtml(palette, positionen, ansager, schreiber, drucknummer, druckdatum, auditAnchor, kontrolle);

    // Pagination im Hauptfenster ausfuehren (versteckter iframe als Mess-
    // Kontext). Das fertige, bereits paginierte HTML wird anschliessend
    // dem Druck-Popup uebergeben — ohne Inline-Script, sodass strenge
    // Content-Security-Policies das Drucken nicht blockieren.
    const measure = document.createElement('iframe');
    measure.setAttribute('aria-hidden', 'true');
    measure.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:600mm;border:0;visibility:hidden;';
    document.body.appendChild(measure);
    const idoc = measure.contentDocument || measure.contentWindow.document;
    idoc.open();
    idoc.write(html);
    idoc.close();
    await new Promise((resolve) => {
      if (idoc.readyState === 'complete') resolve();
      else measure.addEventListener('load', () => resolve(), { once: true });
    });
    try {
      paginatePalettenscheinDoc(idoc, footerLeft);
    } catch (e) {
      console.error('Pagination fehlgeschlagen', e);
    }
    const finalHtml = '<!DOCTYPE html>\n' + idoc.documentElement.outerHTML;
    measure.remove();

    const win = window.open('', '_blank', 'width=900,height=900');
    if (!win) { toast.error('Popup blockiert'); return; }
    win.document.open();
    win.document.write(finalHtml);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { console.warn(e); } }, 350);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Drucken des Palettenscheins');
  }
};

const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

// DHS Firmenlogo (Inline-SVG, identisch zu VersandView.vue)
const dhsLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.18 94.77"><path fill="#2f5e30" d="m.04,8.52h26.13c18.64,0,28.04,11.38,28.04,24.96,0,15.93-11.67,26.64-28.19,26.64H.04V8.52Zm16.74,40h7.27c8.29,0,12.48-4.99,12.48-14.68,0-7.93-3.3-13.73-12.48-13.8h-7.27v28.48Z"/><path fill="#2f5e30" d="m61.25,8.52h16.74v18.64h18.64V8.52h16.81v51.6h-16.81v-21.07h-18.64v21.07h-16.74V8.52Z"/><path fill="#2f5e30" d="m137.51,44.56c1.1,3.45,3.74,6.02,10.5,6.02,5.95,0,7.78-1.84,7.78-4.41,0-2.72-1.61-4.33-11.3-6.02-19.09-3.3-21.8-9.84-21.8-16.66,0-10.2,9.61-16.15,23.71-16.15,17.18,0,24,7.93,24.66,15.56h-16.29c-.59-2.28-1.98-4.92-8.44-4.92-4.55,0-6.68,1.47-6.68,3.89s2.13,3.52,10.2,4.99c19.45,3.45,23.27,9.62,23.27,17.4,0,9.84-8.29,16.95-25.84,16.95s-25.03-7.34-26.57-16.66h16.81Z"/><g><path fill="#f4980c" d="m0,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76H1.31v5.91H0v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76H1.31v5.52h5.14Z"/><path fill="#f4980c" d="m13.92,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m26.9,80.73h5.72c2.33,0,3.9,1.64,3.9,4.11s-1.57,4.12-3.9,4.12h-4.41v5.52h-1.31v-13.74Zm5.72,7.07c1.55,0,2.6-1.19,2.6-2.96s-1.05-2.95-2.6-2.95h-4.41v5.91h4.41Z"/><path fill="#f4980c" d="m42.73,80.73h1.31l5.56,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.55-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m52.37,80.73h1.31v13.74h-1.31v-13.74Z"/><path fill="#f4980c" d="m57.58,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m76.72,87.59c0-4.29,2.54-7.16,6.34-7.16s6.35,2.87,6.35,7.16-2.55,7.18-6.35,7.18-6.34-2.88-6.34-7.18Zm11.38,0c0-3.59-2.03-6-5.04-6s-5.03,2.41-5.03,6,2.02,6.02,5.03,6.02,5.04-2.42,5.04-6.02Z"/><path fill="#f4980c" d="m92.82,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m112.47,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m126.39,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m139.37,80.73h1.27l7.95,11.59v-11.59h1.27v13.74h-1.27l-7.95-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m153.76,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m165.61,80.73h1.28l3.26,11.64,3.27-11.64h1.28l3.26,11.64,3.27-11.64h1.28l-3.89,13.74h-1.31l-3.24-11.43-3.26,11.43h-1.31l-3.88-13.74Z"/><path fill="#f4980c" d="m191.01,80.73h1.64l4.96,12.01,4.97-12.01h1.64v13.74h-1.21v-11.59l-4.8,11.59h-1.19l-4.8-11.59v11.59h-1.21v-13.74Z"/><path fill="#f4980c" d="m208.13,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m225.55,80.73h1.31l5.55,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.56-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m235.19,80.73h1.27l7.94,11.59v-11.59h1.27v13.74h-1.27l-7.94-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m248.86,90.33h1.31c0,1.96,1.61,3.29,4,3.29s4.01-1.09,4.01-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m268.4,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m282.32,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m295.31,90.16v-9.43h1.31v9.43c0,2.06,1.53,3.45,3.82,3.45s3.83-1.39,3.83-3.46v-9.42h1.31v9.43c0,2.75-2.05,4.61-5.13,4.61s-5.12-1.86-5.12-4.61Z"/><path fill="#f4980c" d="m308.76,90.33h1.31c0,1.96,1.62,3.29,4.01,3.29s4-1.09,4-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m322.57,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/></g><g><path fill="#f4980c" fill-rule="evenodd" d="m269.62,34.92c4.82-.26,8.53-4.39,8.27-9.21-.26-4.82-4.39-8.53-9.21-8.27-4.82.26-8.53,4.39-8.27,9.21s4.39,8.53,9.21,8.27Z"/><path fill="#f4980c" fill-rule="evenodd" d="m294.88,28.72c-.89-16.7-15.21-29.57-31.91-28.67-7.72.41-14.6,3.71-19.71,8.77-2.29,2.27-15.27,18.22-15.27,18.22l2,1.78c2.17,5.08,1.38,11.17-2.54,15.56-4.03,4.53-10.24,5.98-15.65,4.17l9.46-10.63-8.65-7.7-9.46,10.62c-2.42-5.16-1.7-11.5,2.34-16.02,3.91-4.4,9.87-5.88,15.16-4.32l2.13,1.9,14.59-17.38c-5.23-3.44-11.53-5.33-18.25-4.98-16.7.89-29.56,15.21-28.67,31.91s15.21,29.57,31.91,28.67c8.76-.47,16.43-4.64,21.65-10.9,1.49-1.73,13.93-16.55,13.93-16.55,0,0-1.08-1.87-1.4-2.85h0s-3.28.18-3.28.18l-.37-6.94,3.28-.18h0c.23-1.07.6-2.09,1.08-3.05h0s-2.44-2.19-2.44-2.19l4.65-5.17,2.44,2.2h0c.9-.58,1.88-1.05,2.92-1.39h0s-.18-3.28-.18-3.28l6.94-.37.18,3.28h0c1.07.23,2.1.6,3.06,1.08h0s2.2-2.45,2.2-2.45l5.17,4.65-2.2,2.44h0c.58.9,1.05,1.88,1.4,2.92h0s3.28-.18,3.28-.18l.37,6.94-3.28.18h0c-.23,1.07-.6,2.09-1.08,3.05h0s2.44,2.19,2.44,2.19l-4.65,5.17-2.44-2.2h0c-.9.58-1.88,1.05-2.92,1.4h0s.18,3.28.18,3.28l-6.94.37-.18-3.28h0c-1.05-.23-3.06-1.1-3.06-1.1l-15,17.88c5.2,3.38,11.45,5.24,18.11,4.88,16.7-.89,29.56-15.21,28.67-31.91Z"/></g></svg>`;

const buildPalettenscheinHtml = (palette, positionen, ansager, schreiber, drucknummer = 1, druckDatum = null, auditAnchor = null, kontrolle = '') => {
  const datum = palette.erfassung_datum
    ? formatDeDateTime(palette.erfassung_datum)
    : formatDeDateTime(new Date());
  const druckDatumStr = druckDatum || formatDeDateTime(new Date());
  const istNachdruck = Number(drucknummer) > 1;
  let gesamtIst = 0;
  const rows = positionen.map(p => {
    const ist = Number(p.istbestand || 0);
    gesamtIst += ist;
    return `<tr><td>${escapeHtml(p.cmmf_nummer || '')}</td><td>${escapeHtml(p.kurztext || '')}</td>` +
      `<td>${escapeHtml(p.werk || '')}</td><td>${escapeHtml(p.lagerort || '')}</td>` +
      `<td style="text-align:center;">${ist}</td></tr>`;
  }).join('');
  const totalsRow = `<tr class="totals"><td colspan="4"><strong>GESAMT</strong></td><td style="text-align:center;"><strong>${gesamtIst}</strong></td></tr>`;
  const anchorHash = auditAnchor && auditAnchor.hash ? String(auditAnchor.hash) : '';
  const anchorLogId = auditAnchor && auditAnchor.log_id != null ? String(auditAnchor.log_id) : '';
  const anchorAt = auditAnchor && auditAnchor.anchor_at
    ? formatDeDateTime(auditAnchor.anchor_at)
    : '';
  const anchorBlock = anchorHash
    ? `<div class="anchor" data-block="anchor">
         <div class="anchor-title">Hash-Anker (GoBD &middot; Audit-Log SHA-256)</div>
         <div class="anchor-meta">Audit-Log-Eintrag #${escapeHtml(anchorLogId)} vom ${escapeHtml(anchorAt)}</div>
         <div class="anchor-hash">${escapeHtml(anchorHash)}</div>
         <div class="anchor-info">Dieser SHA-256-Hash ankert die gesamte Inventur-Audit-Kette zum Druckzeitpunkt. Stimmt er bei einer sp&auml;teren Pr&uuml;fung mit dem record_hash in der Datenbank bzw. CSV-Export &uuml;berein, ist bewiesen, dass seit der Unterschrift kein Datensatz in der Kette ver&auml;ndert wurde.</div>
         <div class="anchor-sigs">
           <div class="sig"><div class="sig-line"></div><div class="sig-cap">Hash-Anker best&auml;tigt (Unterschrift / Datum)</div></div>
           <div class="sig"><div class="sig-line"></div><div class="sig-cap">Inventurleitung (Unterschrift / Datum)</div></div>
         </div>
       </div>`
    : '<div class="anchor anchor-empty" data-block="anchor">Kein Audit-Log-Anker verf&uuml;gbar.</div>';

  const titelOriginal = istNachdruck
    ? `PALETTENSCHEIN (NACHDRUCK Nr. ${drucknummer} vom ${druckDatumStr})`
    : 'PALETTENSCHEIN (ORIGINAL)';
  const titelKopie = istNachdruck
    ? `PALETTENSCHEIN (NACHDRUCK Nr. ${drucknummer} – KOPIE vom ${druckDatumStr})`
    : 'PALETTENSCHEIN (KOPIE)';
  const pageFooterLeft = `Palettenschein ${escapeHtml(palette.paletten_nr || '')} · Druck: ${escapeHtml(druckDatumStr)}${istNachdruck ? ' · Druck-Nr. ' + drucknummer : ''}`;

  const scheinFlow = (titel) => `
    <div class="schein-flow">
      <div class="header" data-block="header">
        <div class="header-logo">${dhsLogoSvg}</div>
        <div class="header-title">${titel}</div>
        <div class="header-spacer"></div>
      </div>
      <table class="info" data-block="info">
        <tr><td class="lbl">Paletten-Nr.:</td><td><strong>${escapeHtml(palette.paletten_nr || '')}</strong></td>
            <td class="lbl">Datum:</td><td>${escapeHtml(datum)}</td></tr>
      </table>
      <table class="positions" data-block="positions">
        <thead><tr><th>CMMF</th><th>Artikel</th><th>Werk</th><th>Lagerort</th><th>IST</th></tr></thead>
        <tbody>
          ${rows || '<tr><td colspan="5" style="text-align:center;color:#888;">Keine Positionen</td></tr>'}
          ${totalsRow}
        </tbody>
      </table>
      <div class="signatures" data-block="sig1">
        <div class="sig"><div class="sig-lbl">Gepackt von</div><div class="sig-line">${escapeHtml(ansager)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
        <div class="sig"><div class="sig-lbl">Gescannt von</div><div class="sig-line">${escapeHtml(schreiber)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
      </div>
      <div class="signatures" data-block="sig2">
        <div class="sig"><div class="sig-lbl">Kontrolle</div><div class="sig-line">${escapeHtml(kontrolle)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
      </div>
      ${anchorBlock}
    </div>`;

  const html = `<html><head><meta charset="UTF-8"><title>Palettenschein ${escapeHtml(palette.paletten_nr || '')}${istNachdruck ? ` – Nachdruck Nr. ${drucknummer}` : ''}</title>
    <style>
      @page { size: A4; margin: 12mm; }
      body { font-family: Arial, sans-serif; font-size: 11pt; margin: 0; padding: 0; color: #000; }

      /* Mess-Container exakt in Druckbreite, damit offsetHeight stimmt */
      #content-flow { width: 186mm; box-sizing: border-box; margin: 0 auto; }
      .schein-flow { box-sizing: border-box; }

      .header {
        display: flex;
        align-items: center;
        gap: 6mm;
        border-bottom: 2px solid #000;
        padding-bottom: 4mm;
        margin-bottom: 6mm;
        ${istNachdruck ? 'color: #b71c1c;' : ''}
      }
      .header-logo { flex: 0 0 32mm; }
      .header-logo svg { display: block; width: 100%; height: auto; }
      .header-title { flex: 1; text-align: center; font-size: 18pt; font-weight: bold; }
      .header-spacer { flex: 0 0 32mm; }
      table.info { width: 100%; border-collapse: collapse; margin-bottom: 6mm; }
      table.info td { padding: 3mm 4mm; border: 1px solid #ccc; }
      table.info td.lbl { background: #f0f0f0; font-weight: bold; width: 18%; }
      table.positions { width: 100%; border-collapse: collapse; margin-bottom: 8mm; }
      table.positions th, table.positions td { border: 1px solid #000; padding: 2.5mm 3mm; font-size: 10pt; }
      table.positions th { background: #e8e8e8; }
      table.positions thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      tr.totals td { background: #f0f0f0; }
      .signatures { display: flex; justify-content: space-between; margin-top: 8mm; gap: 10mm; page-break-inside: avoid; }
      .sig { width: 48%; }
      .sig-lbl { font-weight: bold; margin-bottom: 14mm; }
      .sig-line { border-bottom: 1px solid #000; min-height: 6mm; padding: 0 2mm; }
      .sig-cap { font-size: 9pt; color: #555; text-align: center; margin-top: 1mm; }

      .anchor { margin-top: 8mm; border: 1px solid #000; padding: 4mm; font-size: 9pt; page-break-inside: avoid; }
      .anchor-title { font-weight: bold; font-size: 10pt; margin-bottom: 2mm; }
      .anchor-meta { font-size: 9pt; color: #333; }
      .anchor-hash { margin-top: 2mm; font-family: 'Courier New', monospace; font-size: 8pt; word-break: break-all; background: #f7f7f7; padding: 2mm; border: 1px dashed #999; }
      .anchor-info { margin-top: 3mm; font-size: 8pt; color: #444; }
      .anchor-sigs { display: flex; justify-content: space-between; gap: 8mm; margin-top: 6mm; }
      .anchor-sigs .sig { width: 48%; }
      .anchor-sigs .sig-line { border-bottom: 1px solid #000; min-height: 10mm; }
      .anchor-sigs .sig-cap { font-size: 8pt; color: #555; text-align: center; margin-top: 1mm; }
      .anchor-empty { color: #888; font-style: italic; }

      /* Pagination: jede print-page entspricht genau einer A4-Seite */
      .print-page {
        position: relative;
        box-sizing: border-box;
        width: 186mm;
        height: 273mm;
        padding-bottom: 14mm;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .print-page:last-child { page-break-after: auto; break-after: auto; }
      .page-footer {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        display: flex; justify-content: space-between; align-items: center;
        font-size: 8pt; color: #555;
        border-top: 1px solid #999;
        padding: 1.2mm 1mm 0 1mm;
      }
      .page-footer .pf-page { font-weight: bold; color: #000; }
    </style></head><body>
    <div id="content-flow">
      ${scheinFlow(titelOriginal)}
      ${scheinFlow(titelKopie)}
    </div>
    </body></html>`;
  return { html, footerLeft: pageFooterLeft };
};

// Pagination im Hauptfenster (nicht im Popup), damit das Druck-HTML
// frei von Inline-Scripts bleibt und nicht von Content-Security-Policy
// blockiert werden kann. Operiert auf einem beliebigen Document (z. B.
// einem versteckten Mess-iframe), dessen CSS-Regeln identisch zum
// finalen Druck-Popup sind.
const paginatePalettenscheinDoc = (doc, footerLeft) => {
  const PX_PER_MM = 96 / 25.4;
  const FOOTER_RESERVE_PX = 16 * PX_PER_MM;
  const SAFETY_PX = 12 * PX_PER_MM;
  const PAGE_PX = Math.floor(273 * PX_PER_MM) - FOOTER_RESERVE_PX - SAFETY_PX;

  const scheine = Array.prototype.slice.call(doc.querySelectorAll('.schein-flow'));
  if (scheine.length === 0) return;

  const allPages = [];
  const scheinRanges = [];
  let currentPage = null;
  let currentH = 0;

  const startNewPage = () => {
    currentPage = doc.createElement('div');
    currentPage.className = 'print-page';
    allPages.push(currentPage);
    currentH = 0;
  };

  scheine.forEach((schein) => {
    const rangeStart = allPages.length;
    startNewPage();

    const blocks = Array.prototype.slice.call(schein.children);
    blocks.forEach((blk) => {
      const kind = blk.getAttribute('data-block');
      if (kind === 'positions') {
        const thead = blk.querySelector('thead');
        const theadH = thead ? thead.offsetHeight : 0;
        const theadHtml = thead ? thead.outerHTML : '';
        const trs = Array.prototype.slice.call(blk.querySelectorAll('tbody > tr'));
        const trHs = trs.map((r) => r.offsetHeight);

        const makeTbl = () => {
          const tbl = doc.createElement('table');
          tbl.className = 'positions';
          tbl.innerHTML = theadHtml + '<tbody></tbody>';
          return tbl;
        };
        let tbl = makeTbl();
        let tbody = tbl.querySelector('tbody');
        currentPage.appendChild(tbl);
        currentH += theadH + 8;

        for (let i = 0; i < trs.length; i++) {
          if (currentH + trHs[i] > PAGE_PX && tbody.children.length > 0) {
            startNewPage();
            tbl = makeTbl();
            tbody = tbl.querySelector('tbody');
            currentPage.appendChild(tbl);
            currentH = theadH + 8;
          }
          tbody.appendChild(trs[i].cloneNode(true));
          currentH += trHs[i];
        }
      } else {
        const h = blk.offsetHeight + 8;
        if (currentH + h > PAGE_PX && currentPage.children.length > 0) {
          startNewPage();
        }
        currentPage.appendChild(blk.cloneNode(true));
        currentH += h;
      }
    });
    scheinRanges.push({ start: rangeStart, end: allPages.length });
  });

  // Footer + Numerierung: Seite x von N pro Schein (Kopie startet bei 1).
  const container = doc.createElement('div');
  scheinRanges.forEach((rng) => {
    const subTotal = rng.end - rng.start;
    for (let idx = 0; idx < subTotal; idx++) {
      const p = rng.start + idx;
      const footer = doc.createElement('div');
      footer.className = 'page-footer';
      const left = doc.createElement('span');
      left.textContent = footerLeft;
      const right = doc.createElement('span');
      right.className = 'pf-page';
      right.textContent = `Seite ${idx + 1} von ${subTotal}`;
      footer.appendChild(left);
      footer.appendChild(right);
      allPages[p].appendChild(footer);
      container.appendChild(allPages[p]);
    }
  });
  const oldFlow = doc.getElementById('content-flow');
  if (oldFlow) oldFlow.remove();
  doc.body.appendChild(container);
};

const loadArtikelPaletten = async (cmmf) => {
  if (!cmmf) return;
  if (kompaktArtikelPaletten.value[cmmf]) return; // bereits geladen
  loadingArtikelPaletten.value = { ...loadingArtikelPaletten.value, [cmmf]: true };
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_artikel_paletten', cmmf, archiv_id: selectedArchivId.value }
    });
    if (response.data.status === 'success') {
      const rows = response.data.daten || [];
      // Kumulative Differenz pro Palette: nach scan_zeitpunkt sortieren,
      // IST aufaddieren, gegen Soll der ersten Position vergleichen.
      const tsOf = (r) => {
        if (!r.scan_zeitpunkt) return 0;
        return new Date(String(r.scan_zeitpunkt).replace(' ', 'T')).getTime() || 0;
      };
      const sorted = [...rows].sort((a, b) => tsOf(a) - tsOf(b));
      const soll = Number(sorted[0]?.sollbestand) || 0;
      let kumIst = 0;
      const enriched = sorted.map(r => {
        kumIst += Number(r.istbestand) || 0;
        return { ...r, kumulative_differenz: kumIst - soll };
      });
      kompaktArtikelPaletten.value = {
        ...kompaktArtikelPaletten.value,
        [cmmf]: enriched
      };
    }
  } catch (error) {
    console.error('Fehler beim Laden der Paletten:', error);
    toast.error('Fehler beim Laden der Paletten');
  } finally {
    loadingArtikelPaletten.value = { ...loadingArtikelPaletten.value, [cmmf]: false };
  }
};

const fetchLog = async (opts = {}) => {
  const silent = !!opts.silent;
  if (!silent) loading.value.log = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_log', limit: 200, archiv_id: selectedArchivId.value }
    });

    if (response.data.status === 'success') {
      auditLog.value = response.data.logs || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden des Logs:', error);
    if (!silent) toast.error('Fehler beim Laden des Logs');
  } finally {
    if (!silent) loading.value.log = false;
  }
};

/**
 * Live-Refresh der laufenden Inventur (ohne Loading-Spinner).
 * Scanner und Office laufen meist auf getrennten Rechnern – daher Polling.
 */
let officeLivePollTimer = null;
let officeLiveRefreshBusy = false;
let inventurBroadcast = null;

const refreshOfficeLive = async () => {
  // Nur aktuelle Inventur; Archiv-Ansicht bleibt statisch.
  if (selectedArchivId.value !== 0) return;
  if (typeof document !== 'undefined' && document.hidden) return;
  if (officeLiveRefreshBusy) return;
  officeLiveRefreshBusy = true;
  try {
    const silent = { silent: true };
    // Paletten immer: Banner „offene Paletten“ + Tab Paletten
    const jobs = [fetchPaletten(silent)];
    if (activeTab.value === 'einzelliste') {
      jobs.push(fetchEinzelliste(silent));
    } else if (activeTab.value === 'kompaktliste') {
      jobs.push(fetchKompaktliste(silent));
    } else if (activeTab.value === 'log') {
      jobs.push(fetchLog(silent));
    }
    await Promise.allSettled(jobs);
  } finally {
    officeLiveRefreshBusy = false;
  }
};

const startOfficeLivePoll = () => {
  stopOfficeLivePoll();
  if (selectedArchivId.value !== 0) return;
  officeLivePollTimer = setInterval(() => {
    refreshOfficeLive();
  }, OFFICE_LIVE_POLL_MS);
};

const stopOfficeLivePoll = () => {
  if (officeLivePollTimer) {
    clearInterval(officeLivePollTimer);
    officeLivePollTimer = null;
  }
};

const onOfficeVisibilityChange = () => {
  if (!document.hidden && selectedArchivId.value === 0) {
    refreshOfficeLive();
  }
};

// ===== GoBD: Hash-Chain-Verify und CSV-Export =====
const verifyChainDialog = ref(false);
const verifyChainResult = ref(null);

const verifyAuditChain = async () => {
  loading.value.verifyChain = true;
  verifyChainResult.value = null;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'verify_log_chain', archiv_id: selectedArchivId.value }
    });
    if (response.data?.status === 'success') {
      verifyChainResult.value = response.data;
      verifyChainDialog.value = true;
      if (response.data.valid) {
        toast.success(`Hash-Chain geprüft: ${response.data.ok}/${response.data.total} OK`);
      } else {
        toast.error(`Hash-Chain GEBROCHEN ab Eintrag #${response.data.first_break?.id ?? '?'}`);
      }
      // Log neu laden, damit der Verify-Eintrag selbst sichtbar wird
      fetchLog();
    } else {
      toast.error(response.data?.message || 'Hash-Chain-Prüfung fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Hash-Chain-Prüfung fehlgeschlagen');
  } finally {
    loading.value.verifyChain = false;
  }
};

const exportAuditLogCsv = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'export_log_csv', archiv_id: selectedArchivId.value },
      responseType: 'blob'
    });
    // Versuche Dateinamen aus Content-Disposition zu lesen
    let filename = `inventur_audit_log_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'')}.csv`;
    const cd = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition');
    if (cd) {
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m) filename = m[1];
    }
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Audit-Log als CSV heruntergeladen');
  } catch (error) {
    console.error(error);
    toast.error('CSV-Export fehlgeschlagen');
  }
};

const openArchivDialog = async () => {
  // Sicherstellen, dass paletten/kompaktliste aktuell sind, damit Counts/Statistiken stimmen
  await Promise.allSettled([fetchPaletten(), fetchKompaktliste()]);
  archivForm.value = {
    name: aktuelleMeta.value.name || '',
    notiz: aktuelleMeta.value.notiz || '',
    bestaetigt: false,
  };
  archivDialog.value = true;
};

// ----- Bezeichnung der laufenden Inventur -----
const fetchAktuelleMeta = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_aktuelle_inventur_meta' }
    });
    if (response.data?.status === 'success' && response.data.meta) {
      const m = response.data.meta;
      aktuelleMeta.value = {
        name: m.name || '',
        notiz: m.notiz || '',
        paletten_prefix: m.paletten_prefix ?? null,
        paletten_jahr: m.paletten_jahr ?? null,
        paletten_prefix_effektiv: m.paletten_prefix_effektiv || 'INV',
        paletten_jahr_effektiv: m.paletten_jahr_effektiv || new Date().getFullYear(),
        paletten_beispiel: m.paletten_beispiel || '',
        paletten_format_locked: !!m.paletten_format_locked,
        paletten_count_aktuell: m.paletten_count_aktuell || 0,
      };
    }
  } catch (error) {
    console.error('Fehler beim Laden der Inventur-Bezeichnung:', error);
  }
};

const openInventurNameDialog = () => {
  inventurNameForm.value = {
    name: aktuelleMeta.value.name || '',
    notiz: aktuelleMeta.value.notiz || '',
    paletten_prefix: aktuelleMeta.value.paletten_prefix
      || aktuelleMeta.value.paletten_prefix_effektiv
      || 'INV',
    paletten_jahr: aktuelleMeta.value.paletten_jahr
      || aktuelleMeta.value.paletten_jahr_effektiv
      || new Date().getFullYear(),
  };
  inventurNameDialog.value = true;
};

// Live-Vorschau der naechsten Paletten-Nr im Dialog
const palettenBeispielVorschau = computed(() => {
  const p = String(inventurNameForm.value.paletten_prefix || 'INV').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const j = parseInt(inventurNameForm.value.paletten_jahr, 10) || new Date().getFullYear();
  return `${p || 'INV'}-${String(j).padStart(4, '0')}-00001`;
});

const saveInventurName = async () => {
  inventurNameSaving.value = true;
  try {
    const payload = {
      action: 'set_aktuelle_inventur_meta',
      name: (inventurNameForm.value.name || '').trim(),
      notiz: (inventurNameForm.value.notiz || '').trim(),
    };
    // Paletten-Format nur mitsenden, wenn (noch) aenderbar.
    if (!aktuelleMeta.value.paletten_format_locked) {
      payload.paletten_prefix = String(inventurNameForm.value.paletten_prefix || 'INV')
        .toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) || 'INV';
      payload.paletten_jahr = parseInt(inventurNameForm.value.paletten_jahr, 10) || new Date().getFullYear();
    }
    const response = await axios.post(
      `${API_BASE_URL}/inventur/index.php`,
      payload
    );
    if (response.data?.status === 'success') {
      // Komplett neu laden, damit auch Lock-Flag/Beispiel frisch sind.
      await fetchAktuelleMeta();
      toast.success('Bezeichnung gespeichert');
      inventurNameDialog.value = false;
      fetchLog();
    } else {
      toast.error(response.data?.message || 'Bezeichnung konnte nicht gespeichert werden');
    }
  } catch (error) {
    console.error('Fehler beim Speichern der Bezeichnung:', error);
    toast.error(error.response?.data?.message || 'Fehler beim Speichern der Bezeichnung');
  } finally {
    inventurNameSaving.value = false;
  }
};

const fetchArchivList = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_inventur_archive' }
    });
    if (response.data?.status === 'success') {
      archivList.value = response.data.archive || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Archiv-Liste:', error);
  }
};

// ========================================
// KORREKTURBUCHUNGEN (GoBD)
// ========================================
const korrekturen = ref([]);
const korrekturDialog = ref(false);
const korrekturPositionen = ref([]);
const korrekturForm = ref({
  position_id: null,
  bestand_neu: 0,
  grund_kategorie: '',
  grund_text: '',
  belegverweis: ''
});
const korrekturLoading = ref(false);
const korrekturSearch = ref('');

// ========================================
// PALETTEN-INHALT (Detail-Dialog)
// ========================================
const paletteDetailDialog = ref(false);
const paletteDetailLoading = ref(false);
const paletteDetail = ref(null);
const paletteDetailPositionen = ref([]);
const paletteDetailGesamtstueck = computed(() =>
  paletteDetailPositionen.value.reduce((sum, p) => sum + (Number(p.istbestand) || 0), 0)
);

const openPaletteDetail = async (item) => {
  if (!item) return;
  // Akzeptiert id (Paletten-Tab) oder palette_id (Einzel-/Kompaktliste)
  let paletteId = item.id ?? item.palette_id ?? null;
  if (!paletteId && item.paletten_nr) {
    const found = paletten.value.find((p) => p.paletten_nr === item.paletten_nr);
    paletteId = found?.id ?? null;
  }
  if (!paletteId) {
    toast.error('Palette konnte nicht geöffnet werden (keine ID)');
    return;
  }
  paletteDetailDialog.value = true;
  paletteDetailLoading.value = true;
  paletteDetail.value = { ...item, id: paletteId };
  paletteDetailPositionen.value = [];
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_palette_inhalt', palette_id: paletteId }
    });
    if (response.data?.status === 'success') {
      paletteDetail.value = response.data.palette || { ...item, id: paletteId };
      paletteDetailPositionen.value = response.data.positionen || [];
    } else {
      toast.error(response.data?.message || 'Palette konnte nicht geladen werden');
    }
  } catch (error) {
    console.error('Fehler beim Laden der Palette:', error);
    toast.error('Fehler beim Laden der Palette');
  } finally {
    paletteDetailLoading.value = false;
  }
};

const grundKategorien = [
  { value: 'zaehlfehler', title: 'Zählfehler' },
  { value: 'ean_falsch', title: 'EAN/CMMF falsch zugeordnet' },
  { value: 'nachtraeglich_gefunden', title: 'Nachträglich gefunden' },
  { value: 'doppelt_gescannt', title: 'Doppelt gescannt' },
  { value: 'schwund', title: 'Schwund/Verlust' },
  { value: 'sonstiges', title: 'Sonstiges' }
];

const fetchKorrekturen = async () => {
  if (!isViewingArchiv.value) {
    korrekturen.value = [];
    return;
  }
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_korrekturen', archiv_id: selectedArchivId.value }
    });
    if (response.data?.status === 'success') {
      korrekturen.value = response.data.korrekturen || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Korrekturen:', error);
  }
};

// Map: original_position_id -> Summe der Korrektur-Differenzen
const korrekturMap = computed(() => {
  const map = {};
  for (const k of korrekturen.value) {
    const pid = k.original_position_id;
    if (!pid) continue;
    map[pid] = (map[pid] || 0) + Number(k.differenz || 0);
  }
  return map;
});

const openKorrekturDialog = async () => {
  if (!isViewingArchiv.value) return;
  korrekturForm.value = {
    position_id: null,
    bestand_neu: 0,
    grund_kategorie: '',
    grund_text: '',
    belegverweis: ''
  };
  korrekturSearch.value = '';
  korrekturDialog.value = true;
  korrekturLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_korrektur_positionen', archiv_id: selectedArchivId.value }
    });
    if (response.data?.status === 'success') {
      korrekturPositionen.value = response.data.positionen || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Positionen:', error);
    toast.error('Positionen konnten nicht geladen werden.');
  } finally {
    korrekturLoading.value = false;
  }
};

const selectedKorrekturPosition = computed(() => {
  if (!korrekturForm.value.position_id) return null;
  return korrekturPositionen.value.find(p => p.position_id === korrekturForm.value.position_id) || null;
});

const aktuellerBestandKorrektur = computed(() => {
  const p = selectedKorrekturPosition.value;
  if (!p) return 0;
  return Number(p.bestand_alt || 0) + Number(p.korrektur_summe || 0);
});

const filteredKorrekturPositionen = computed(() => {
  const s = korrekturSearch.value.trim().toLowerCase();
  if (!s) return korrekturPositionen.value;
  return korrekturPositionen.value.filter(p =>
    [p.cmmf_nummer, p.gtin_ean, p.kurztext, p.paletten_nr, p.werk, p.lagerort]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(s))
  );
});

const submitKorrektur = async () => {
  const f = korrekturForm.value;
  if (!f.position_id || f.grund_kategorie === '' || (f.grund_text || '').trim().length < 5) {
    toast.error('Bitte Position, Kategorie und ausführlichen Grund (mind. 5 Zeichen) angeben.');
    return;
  }
  if (Number(f.bestand_neu) === aktuellerBestandKorrektur.value) {
    toast.error('Keine Änderung – der neue Bestand entspricht dem aktuellen.');
    return;
  }
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'create_korrektur',
      archiv_id: selectedArchivId.value,
      original_position_id: f.position_id,
      bestand_neu: Number(f.bestand_neu),
      grund_kategorie: f.grund_kategorie,
      grund_text: f.grund_text.trim(),
      belegverweis: (f.belegverweis || '').trim()
    });
    if (response.data?.status === 'success') {
      toast.success(`Korrektur gespeichert (Δ ${response.data.differenz > 0 ? '+' : ''}${response.data.differenz}).`);
      korrekturDialog.value = false;
      await Promise.allSettled([fetchKorrekturen(), fetchLog()]);
    } else {
      toast.error(response.data?.message || 'Fehler beim Speichern.');
    }
  } catch (error) {
    console.error('Korrektur-Fehler:', error);
    toast.error(error.response?.data?.message || 'Fehler beim Speichern der Korrektur.');
  }
};

const confirmArchivieren = async () => {
  if (paletenInErfassungCount.value > 0) {
    toast.error('Es sind noch Paletten in Erfassung – Archivieren nicht möglich.');
    return;
  }
  if (!archivForm.value.bestaetigt) return;
  loading.value.archiv = true;
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'archive_inventur',
      name: archivForm.value.name?.trim() || '',
      notiz: archivForm.value.notiz?.trim() || ''
    });
    if (response.data?.status === 'success') {
      toast.success(
        `Inventur archiviert als ${response.data.archiv_nr} ` +
        `(${response.data.anzahl_paletten} Paletten, ${response.data.anzahl_positionen} Positionen).`
      );
      archivDialog.value = false;
      // Alles neu laden – die laufende Inventur ist jetzt leer
      await Promise.allSettled([
        fetchArchivList(),
        fetchPaletten(),
        fetchEinzelliste(),
        fetchKompaktliste(),
        fetchLog(),
        fetchAktuelleMeta()
      ]);
    } else {
      toast.error(response.data?.message || 'Archivieren fehlgeschlagen');
    }
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Fehler beim Archivieren';
    toast.error(msg);
    console.error('archive_inventur Fehler:', error);
  } finally {
    loading.value.archiv = false;
  }
};

const exportExcelEinzelliste = async () => {
  try {
    window.location.href = `${API_BASE_URL}inventur/export_import.php?action=export_einzelauflistung&archiv_id=${selectedArchivId.value}`;
  } catch (error) {
    console.error('Fehler beim Export:', error);
    toast.error('Fehler beim Export');
  }
};

const exportExcelKompaktliste = async () => {
  try {
    window.location.href = `${API_BASE_URL}inventur/export_import.php?action=export_kompaktliste&archiv_id=${selectedArchivId.value}`;
  } catch (error) {
    console.error('Fehler beim Export:', error);
    toast.error('Fehler beim Export');
  }
};

// Finaler SAP-Export: l\u00e4dt die CSV als Blob, liest den SHA-256 +
// Audit-Anker aus den Response-Headern und zeigt sie dem Office-Mitarbeiter
// an. Der Hash muss auf das ausgedruckte SAP-\u00dcbergabeprotokoll
// notiert und gegengezeichnet werden (Medienbruch-Anker, GoBD).
// Startet den SAP-Export. Öffnet zuerst einen Bestätigungs-Dialog mit
// Plausibilitäts-Vorschau (ungescannte Artikel, offene Paletten, Summen),
// damit der/die Verantwortliche aktiv bestätigen muss.
const exportSap = () => {
  if (!selectedArchivId.value) {
    toast.error('Bitte zuerst eine archivierte/abgeschlossene Inventur w\u00e4hlen');
    return;
  }
  // Für nicht-archivierte Auswertung sicherstellen, dass die Kompaktliste geladen ist –
  // sonst zeigt die Plausi-Vorschau alte/leere Werte.
  if (!kompaktliste.value || kompaktliste.value.length === 0) {
    fetchKompaktliste();
  }
  sapExportConfirm.value = { referenz: '', bestaetigt: false, busy: false };
  sapExportConfirmDialog.value = true;
};

// Führt den eigentlichen SAP-Export nach Bestätigung durch.
const performSapExport = async () => {
  if (sapExportConfirm.value.busy) return;
  if (!sapExportConfirm.value.bestaetigt) {
    toast.error('Bitte die Plausibilitätsprüfung bestätigen.');
    return;
  }
  if (kompaktPlausi.value.hatOffenePaletten) {
    toast.error('Es sind noch Paletten in Erfassung – Export nicht möglich.');
    return;
  }
  sapExportConfirm.value.busy = true;
  const referenz = (sapExportConfirm.value.referenz || '').trim();
  try {
    const response = await axios.get(`${API_BASE_URL}inventur/export_import.php`, {
      params: {
        action: 'export_sap',
        archiv_id: selectedArchivId.value,
        referenz: referenz
      },
      responseType: 'blob'
    });
    const csvHash    = response.headers['x-inventur-csv-sha256'] || '';
    const anchorHash = response.headers['x-inventur-audit-anchor'] || '';
    const anchorId   = response.headers['x-inventur-audit-log-id'] || '';
    const rows       = response.headers['x-inventur-rows'] || '?';
    const sumIst     = response.headers['x-inventur-sum-ist'] || '?';
    const sumSoll    = response.headers['x-inventur-sum-soll'] || '?';

    // Datei zum Download anbieten
    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const filename = m ? m[1] : `Inventur_SAP_Export_${selectedArchivId.value}.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 0);

    sapExportResult.value = {
      filename,
      csvHash,
      anchorHash,
      anchorId,
      rows,
      sumIst,
      sumSoll,
      referenz,
      zeit: new Date().toLocaleString('de-DE')
    };
    sapExportConfirmDialog.value = false;
    sapExportDialog.value = true;
  } catch (error) {
    console.error('SAP-Export-Fehler:', error);
    toast.error('SAP-Export fehlgeschlagen');
  } finally {
    sapExportConfirm.value.busy = false;
  }
};

const copySapHash = async () => {
  try {
    await navigator.clipboard.writeText(sapExportResult.value.csvHash || '');
    toast.success('SHA-256 in die Zwischenablage kopiert');
  } catch (e) {
    toast.error('Kopieren fehlgeschlagen');
  }
};

const exportKorrekturen = async () => {
  if (!isViewingArchiv.value) return;
  try {
    const response = await axios.get(`${API_BASE_URL}inventur/export_import.php`, {
      params: {
        action: 'export_korrekturen',
        archiv_id: selectedArchivId.value
      },
      responseType: 'blob'
    });

    const csvHash    = response.headers['x-inventur-csv-sha256'] || '';
    const anchorHash = response.headers['x-inventur-audit-anchor'] || '';
    const anchorId   = response.headers['x-inventur-audit-log-id'] || '';
    const rows       = response.headers['x-inventur-rows'] || '?';
    const sumDiff    = response.headers['x-inventur-sum-diff'] || '0';

    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const filename = m ? m[1] : `Inventur_Korrekturen_${selectedArchivId.value}.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 0);

    korrekturenExportResult.value = {
      filename,
      csvHash,
      anchorHash,
      anchorId,
      rows,
      sumDiff,
      zeit: new Date().toLocaleString('de-DE')
    };
    korrekturenExportDialog.value = true;
  } catch (error) {
    console.error('Fehler beim Export:', error);
    toast.error('Fehler beim Export');
  }
};

const copyKorrekturenHash = async () => {
  try {
    await navigator.clipboard.writeText(korrekturenExportResult.value.csvHash || '');
    toast.success('SHA-256 in die Zwischenablage kopiert');
  } catch (e) {
    toast.error('Kopieren fehlgeschlagen');
  }
};

const copyVerifyCommand = async (tool) => {
  const file = korrekturenExportResult.value.filename || 'datei.csv';
  const cmd = tool === 'pwsh'
    ? `Get-FileHash -Algorithm SHA256 "${file}" | Select-Object -ExpandProperty Hash`
    : `sha256sum "${file}"`;
  try {
    await navigator.clipboard.writeText(cmd);
    toast.success('Befehl kopiert');
  } catch (e) {
    toast.error('Kopieren fehlgeschlagen');
  }
};

const printKorrekturen = async () => {
  if (!isViewingArchiv.value) {
    toast.warning('Druck nur in der Archiv-Ansicht möglich');
    return;
  }
  if (!korrekturen.value.length) {
    toast.warning('Keine Korrekturen vorhanden');
    return;
  }
  loading.value.korrekturenPrint = true;
  try {
    const items = [...korrekturen.value];
    const summeDiff = items.reduce((s, k) => s + Number(k.differenz || 0), 0);

    // Audit-Eintrag (vor dem Druck, damit auch Abbruch erfasst wird)
    let auditAnchor = null;
    try {
      const logResp = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
        action: 'log_korrekturen_druck',
        archiv_id: selectedArchivId.value,
        anzahl: items.length,
        summe_differenz: summeDiff
      });
      auditAnchor = logResp?.data?.audit_anchor || null;
    } catch (e) {
      console.warn('Audit-Log-Eintrag fehlgeschlagen', e);
    }

    const archivMeta = archivList.value.find(a => a.id === selectedArchivId.value) || null;
    const html = buildKorrekturenDruckHtml(items, {
      archiv: archivMeta,
      summeDiff,
      auditAnchor
    });
    const win = window.open('', '_blank', 'width=1100,height=900');
    if (!win) {
      toast.error('Popup blockiert. Bitte Popups erlauben.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Drucken der Korrekturliste');
  } finally {
    loading.value.korrekturenPrint = false;
  }
};

const buildKorrekturenDruckHtml = (items, meta) => {
  const druckDatum = new Date().toLocaleString('de-DE');
  const benutzer = (authStore.user?.vorname && authStore.user?.nachname)
    ? `${authStore.user.vorname} ${authStore.user.nachname}`
    : (authStore.user?.loginname || 'Unbekannt');
  const archiv = meta.archiv || {};
  const archivLabel = archiv.archiv_nr
    ? (archiv.name ? `${archiv.archiv_nr} – ${archiv.name}` : String(archiv.archiv_nr))
    : `#${selectedArchivId.value}`;
  const archivDatum = archiv.archiviert_am
    ? new Date(archiv.archiviert_am).toLocaleDateString('de-DE')
    : '';

  const kategorieLabel = (val) =>
    (grundKategorien.find(g => g.value === val) || {}).title || val || '';

  let lfd = 0;
  const rows = items.map(k => {
    lfd += 1;
    const diff = Number(k.differenz || 0);
    const datum = k.erstellt_am
      ? new Date(String(k.erstellt_am).replace(' ', 'T')).toLocaleString('de-DE')
      : '';
    const werkLager = [k.werk, k.lagerort].filter(Boolean).join(' / ') || '–';
    const hashKurz = k.record_hash ? String(k.record_hash).substring(0, 16) + '…' : '';
    const diffClass = diff > 0 ? 'pos' : (diff < 0 ? 'neg' : '');
    return `<tr>
      <td class="num">${lfd}</td>
      <td>${escapeHtml(datum)}</td>
      <td>${escapeHtml(k.cmmf_nummer || '')}</td>
      <td>${escapeHtml(k.kurztext || '')}</td>
      <td>${escapeHtml(werkLager)}</td>
      <td class="num">${escapeHtml(String(k.bestand_alt ?? ''))}</td>
      <td class="num">${escapeHtml(String(k.bestand_neu ?? ''))}</td>
      <td class="num ${diffClass}"><strong>${diff > 0 ? '+' : ''}${diff}</strong></td>
      <td>${escapeHtml(kategorieLabel(k.grund_kategorie))}</td>
      <td>${escapeHtml(k.grund_text || '')}</td>
      <td>${escapeHtml(k.erstellt_von_name || '')}</td>
      <td class="hash">${escapeHtml(hashKurz)}</td>
    </tr>`;
  }).join('');

  const anchor = meta.auditAnchor || null;
  const anchorHash = anchor?.hash ? String(anchor.hash) : '';
  const anchorLogId = anchor?.log_id != null ? String(anchor.log_id) : '';
  const anchorAt = anchor?.anchor_at ? String(anchor.anchor_at) : '';
  const anchorBlock = anchorHash
    ? `<div class="hash-anchor sig-block">
         <div class="ha-title">Hash-Anker (GoBD / Audit-Log SHA-256)</div>
         <div class="ha-meta">Druckzeit: ${escapeHtml(druckDatum)}</div>
         <div class="ha-meta">Letzter Audit-Log-Eintrag: #${escapeHtml(anchorLogId)} vom ${escapeHtml(anchorAt)}</div>
         <div class="ha-hash">${escapeHtml(anchorHash)}</div>
         <div class="ha-note">
           Dieser SHA-256-Hash ankert die gesamte Inventur-Audit-Kette zum Druckzeitpunkt.
           Stimmt er bei einer späteren Prüfung mit dem Wert in der Datenbank überein,
           ist bewiesen, dass seit der Unterschrift keine Korrektur in der Kette
           verändert wurde.
         </div>
         <div class="ha-sigs">
           <div class="sig">
             <div class="sig-line"></div>
             <div class="sig-cap">Hash-Anker bestätigt (Unterschrift / Datum)</div>
           </div>
           <div class="sig">
             <div class="sig-line"></div>
             <div class="sig-cap">Inventurleitung (Unterschrift / Datum)</div>
           </div>
         </div>
       </div>`
    : `<div class="hash-anchor sig-block"><div class="ha-note">Kein Audit-Log-Anker verfügbar.</div></div>`;

  return `<html><head><meta charset="UTF-8"><title>Inventur-Korrekturbuchungen</title>
    <style>
      @page { size: A4; margin: 12mm 10mm 18mm 10mm; }
      body { font-family: Arial, sans-serif; font-size: 9.5pt; margin: 0; padding: 0; color: #000; }
      .doc-header { display: flex; align-items: center; gap: 8mm; border-bottom: 2px solid #000; padding-bottom: 4mm; margin-bottom: 4mm; }
      .doc-header .dh-logo { flex: 0 0 36mm; }
      .doc-header .dh-logo svg { display: block; width: 100%; height: auto; }
      .doc-header .dh-body { flex: 1; }
      .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; }
      .doc-header .meta { font-size: 9pt; }
      .doc-header .meta span { margin-right: 6mm; }

      table.list { width: 100%; border-collapse: collapse; }
      table.list thead { display: table-header-group; }
      table.list th, table.list td {
        border: 1px solid #444;
        padding: 1.4mm 1.6mm;
        font-size: 8.5pt;
        vertical-align: top;
      }
      table.list th { background: #e0e0e0; text-align: left; }
      table.list td.num { text-align: right; font-variant-numeric: tabular-nums; }
      table.list td.pos { color: #1b5e20; }
      table.list td.neg { color: #b71c1c; }
      table.list td.hash { font-family: 'Courier New', monospace; font-size: 7.5pt; }
      tr { page-break-inside: avoid; }

      .summary {
        margin-top: 4mm;
        padding: 2mm 3mm;
        border: 1px solid #000;
        background: #f7f7f7;
        font-size: 10pt;
      }
      .summary .delta-pos { color: #1b5e20; }
      .summary .delta-neg { color: #b71c1c; }

      .signatures {
        display: flex;
        justify-content: space-between;
        gap: 8mm;
        margin-top: 10mm;
        page-break-inside: avoid;
      }
      .sig { width: 48%; }
      .sig-lbl { font-weight: bold; margin-bottom: 14mm; }
      .sig-line { border-bottom: 1px solid #000; min-height: 6mm; padding: 0 2mm; }
      .sig-cap { font-size: 8.5pt; color: #555; text-align: center; margin-top: 1mm; }

      .hash-anchor {
        margin-top: 8mm;
        border: 1px solid #000;
        padding: 3mm 4mm;
        font-size: 9pt;
        page-break-inside: avoid;
      }
      .hash-anchor .ha-title { font-weight: bold; font-size: 10pt; margin-bottom: 2mm; }
      .hash-anchor .ha-meta { margin-bottom: 1mm; }
      .hash-anchor .ha-hash {
        margin-top: 2mm;
        font-family: 'Courier New', monospace;
        font-size: 8.5pt;
        word-break: break-all;
        background: #f7f7f7;
        padding: 2mm;
        border: 1px dashed #888;
      }
      .hash-anchor .ha-note { margin-top: 2mm; font-size: 8pt; color: #444; }
      .hash-anchor .ha-sigs { display: flex; justify-content: space-between; gap: 8mm; margin-top: 6mm; }
      .hash-anchor .ha-sigs .sig { width: 48%; }
      .hash-anchor .ha-sigs .sig-line { margin-top: 10mm; }

      .print-page {
        position: relative;
        box-sizing: border-box;
        width: 190mm;
        height: 267mm;
        overflow: hidden;
        padding-bottom: 14mm;
        page-break-after: always;
      }
      .print-page:last-child { page-break-after: auto; }
      .page-footer {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 8pt;
        color: #555;
        border-top: 1px solid #999;
        padding: 1.2mm 1mm 0 1mm;
      }
      .page-footer .pf-page { font-weight: bold; color: #000; }

      #content-flow {
        width: 190mm;
        box-sizing: border-box;
        padding: 0;
        margin: 0 auto;
      }
    </style></head><body>
    <div id="content-flow">
      <div id="doc-header" class="doc-header">
        <div class="dh-logo">${dhsLogoSvg}</div>
        <div class="dh-body">
          <h1>Inventur – Korrekturbuchungen</h1>
          <div class="meta">
            <span><strong>Archiv:</strong> ${escapeHtml(archivLabel)}</span>
            ${archivDatum ? `<span><strong>Archiviert am:</strong> ${escapeHtml(archivDatum)}</span>` : ''}
            <span><strong>Datensätze:</strong> ${items.length}</span>
            <span><strong>Druck:</strong> ${escapeHtml(druckDatum)}</span>
            <span><strong>Drucker:</strong> ${escapeHtml(benutzer)}</span>
          </div>
        </div>
      </div>

      <table id="rows-table" class="list">
        <thead id="rows-thead">
          <tr>
            <th style="width:8mm;">Nr.</th>
            <th style="width:28mm;">Datum</th>
            <th style="width:20mm;">CMMF</th>
            <th>Kurztext</th>
            <th style="width:24mm;">Werk/Lager</th>
            <th style="width:12mm;">Alt</th>
            <th style="width:12mm;">Neu</th>
            <th style="width:12mm;">Δ</th>
            <th style="width:24mm;">Kategorie</th>
            <th>Begründung</th>
            <th style="width:22mm;">Erfasst von</th>
            <th style="width:22mm;">Hash</th>
          </tr>
        </thead>
        <tbody id="rows-tbody">
          ${rows}
        </tbody>
      </table>

      <div id="doc-summary" class="summary">
        <strong>Zusammenfassung:</strong>
        ${items.length} Korrekturbuchungen,
        Summe Δ:
        <strong class="${meta.summeDiff > 0 ? 'delta-pos' : (meta.summeDiff < 0 ? 'delta-neg' : '')}">
          ${meta.summeDiff > 0 ? '+' : ''}${meta.summeDiff}
        </strong> Stk
      </div>

      <div class="signatures sig-block">
        <div class="sig">
          <div class="sig-lbl">Erfassung / Sachbearbeitung</div>
          <div class="sig-line"></div>
          <div class="sig-cap">Name, Unterschrift / Datum</div>
        </div>
        <div class="sig">
          <div class="sig-lbl">Inventurleitung</div>
          <div class="sig-line"></div>
          <div class="sig-cap">Name, Unterschrift / Datum</div>
        </div>
      </div>
      ${anchorBlock}
    </div>

    <script>
      (function() {
        var DRUCK = ${JSON.stringify(druckDatum)};
        var DRUCKER = ${JSON.stringify(benutzer)};

        function paginate() {
          var PX_PER_MM = 96 / 25.4;
          var FOOTER_RESERVE_PX = 16 * PX_PER_MM;
          var SAFETY_PX = 12 * PX_PER_MM;
          var PAGE_PX = Math.floor(267 * PX_PER_MM) - FOOTER_RESERVE_PX - SAFETY_PX;

          var headerEl  = document.getElementById('doc-header');
          var theadEl   = document.getElementById('rows-thead');
          var tbody     = document.getElementById('rows-tbody');
          var summaryEl = document.getElementById('doc-summary');
          var sigEls    = Array.prototype.slice.call(document.querySelectorAll('.sig-block'));
          if (!headerEl || !tbody) return;

          var theadHtml = theadEl.outerHTML;
          var rows      = Array.prototype.slice.call(tbody.children);
          if (rows.length === 0) return;

          var headerH  = headerEl.offsetHeight + 16;
          var theadH   = theadEl.offsetHeight;
          var summaryH = summaryEl.offsetHeight + 8;
          var rowHs    = rows.map(function(r) { return r.offsetHeight; });
          var sigHs    = sigEls.map(function(e) { return e.offsetHeight + 8; });

          var pages = [[]];
          var curH  = headerH + theadH;
          for (var i = 0; i < rows.length; i++) {
            if (curH + rowHs[i] > PAGE_PX && pages[pages.length - 1].length > 0) {
              pages.push([]);
              curH = theadH;
            }
            pages[pages.length - 1].push(i);
            curH += rowHs[i];
          }

          var trailing = [summaryEl].concat(sigEls);
          var trailingHs = [summaryH].concat(sigHs);

          var placements = pages.map(function(idxs, p) {
            var used = (p === 0 ? headerH : 0) + theadH;
            for (var k = 0; k < idxs.length; k++) used += rowHs[idxs[k]];
            return { rowIdxs: idxs, trailIdxs: [], used: used };
          });
          if (placements.length === 0) placements.push({ rowIdxs: [], trailIdxs: [], used: headerH + theadH });

          var cur = placements[placements.length - 1];
          for (var t = 0; t < trailing.length; t++) {
            if (cur.used + trailingHs[t] > PAGE_PX && cur.trailIdxs.length + cur.rowIdxs.length > 0) {
              cur = { rowIdxs: [], trailIdxs: [], used: 0 };
              placements.push(cur);
            }
            cur.trailIdxs.push(t);
            cur.used += trailingHs[t];
          }
          var totalPages = placements.length;

          var container = document.createElement('div');
          for (var p = 0; p < totalPages; p++) {
            var plc     = placements[p];
            var isFirst = (p === 0);
            var page    = document.createElement('div');
            page.className = 'print-page';
            if (isFirst) page.appendChild(headerEl.cloneNode(true));

            if (plc.rowIdxs.length > 0) {
              var tbl = document.createElement('table');
              tbl.className = 'list';
              tbl.innerHTML = theadHtml + '<tbody></tbody>';
              var newTbody = tbl.querySelector('tbody');
              for (var j = 0; j < plc.rowIdxs.length; j++) {
                newTbody.appendChild(rows[plc.rowIdxs[j]].cloneNode(true));
              }
              page.appendChild(tbl);
            }

            for (var s = 0; s < plc.trailIdxs.length; s++) {
              page.appendChild(trailing[plc.trailIdxs[s]].cloneNode(true));
            }

            var footer = document.createElement('div');
            footer.className = 'page-footer';
            footer.innerHTML =
              '<span>Inventur-Korrekturbuchungen · Druck: ' + DRUCK + ' · ' + DRUCKER + '</span>' +
              '<span class="pf-page">Seite ' + (p + 1) + ' von ' + totalPages + '</span>';
            page.appendChild(footer);

            container.appendChild(page);
          }
          document.getElementById('content-flow').remove();
          document.body.appendChild(container);
        }

        function start() {
          try { paginate(); } catch (e) { console.error('Pagination fehlgeschlagen', e); }
          setTimeout(function() { try { window.print(); } catch (e) { console.warn(e); } }, 150);
        }

        if (document.readyState === 'complete') start();
        else window.addEventListener('load', function() { setTimeout(start, 50); });
      })();
    ${'<'}/script>
    </body></html>`;
};

const handleFileSelect = () => {
  // Datei ist bereits in importFile.value
};

const onImportPaste = () => {
  // Nach dem Paste-Event triggert v-textarea automatisch input → parseImportPaste
  setTimeout(() => parseImportPaste(), 0);
};

const clearImportPaste = () => {
  importPasteText.value = '';
  parsedImportRows.value = [];
  importResult.value.show = false;
};

const parseImportPaste = () => {
  const text = String(importPasteText.value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!text.trim()) {
    parsedImportRows.value = [];
    return;
  }
  const lines = text.split('\n').filter(l => l.trim() !== '');
  const startIdx = importHasHeader.value && lines.length > 1 ? 1 : 0;
  const out = [];
  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i];
    const sep = line.includes('\t') ? '\t' : (line.includes(';') ? ';' : '\t');
    const parts = line.split(sep).map(s => s.trim());
    let cmmf = '', ean = '', kurztext = '', werk = '', lagerort = '', soll = '';
    if (importHasEan.value) {
      [cmmf = '', ean = '', kurztext = '', werk = '', lagerort = '', soll = ''] = parts;
    } else {
      [cmmf = '', kurztext = '', werk = '', lagerort = '', soll = ''] = parts;
      ean = '';
    }
    if (!cmmf) continue;
    const sollNum = parseInt(String(soll).replace(/[^0-9-]/g, ''), 10);
    out.push({
      cmmf_nummer: cmmf,
      gtin_ean: ean,
      kurztext,
      werk,
      lagerort,
      sollbestand: Number.isNaN(sollNum) ? 0 : sollNum
    });
  }
  parsedImportRows.value = out;
};

const openEanPreview = async () => {
  loading.value.eanSync = true;
  eanSyncResult.value.show = false;
  eanPreview.value = {
    ohne_ean_gesamt: 0,
    katalog_gesamt: 0,
    treffer_anzahl: 0,
    eindeutig: 0,
    mehrdeutig: 0,
    items: []
  };
  eanSelected.value = {};
  eanChoice.value = {};
  eanPreviewDialog.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'preview_ean_from_refurbish' }
    );
    if (response.data?.status === 'success') {
      eanPreview.value = {
        ohne_ean_gesamt: Number(response.data.ohne_ean_gesamt || 0),
        katalog_gesamt: Number(response.data.katalog_gesamt || 0),
        treffer_anzahl: Number(response.data.treffer_anzahl || 0),
        eindeutig: Number(response.data.eindeutig || 0),
        mehrdeutig: Number(response.data.mehrdeutig || 0),
        items: response.data.items || []
      };
      // Vorauswahl: alle eindeutigen Treffer ankreuzen, mehrdeutige nicht
      // Vorgewählte EAN: erster (=neuester) Kandidat
      for (const item of eanPreview.value.items) {
        eanSelected.value[item.cmmf_nummer] = item.kandidaten.length === 1;
        eanChoice.value[item.cmmf_nummer] = item.kandidaten[0]?.ean || '';
      }
      if (eanPreview.value.items.length === 0) {
        toast.info('Keine Treffer im Refurbish-Katalog für offene EAN-Felder.');
      }
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
      eanPreviewDialog.value = false;
    }
  } catch (error) {
    console.error('Fehler bei EAN-Vorschau:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
    eanPreviewDialog.value = false;
  } finally {
    loading.value.eanSync = false;
  }
};

const toggleEanSelectAll = (val) => {
  const target = !!val;
  for (const item of eanPreview.value.items) {
    eanSelected.value[item.cmmf_nummer] = target;
  }
};

const getSelectedCandidateDescription = (item) => {
  const ean = eanChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
  const cand = item.kandidaten.find(k => k.ean === ean) || item.kandidaten[0];
  if (!cand) return '';
  const parts = [];
  if (cand.code_commercial) parts.push(cand.code_commercial);
  if (cand.description_cmmf) parts.push(cand.description_cmmf);
  return parts.join(' – ');
};

const applyEanSelection = async () => {
  const mappings = [];
  for (const item of eanPreview.value.items) {
    if (!eanSelected.value[item.cmmf_nummer]) continue;
    const ean = eanChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
    if (!ean) continue;
    mappings.push({ cmmf_nummer: item.cmmf_nummer, gtin_ean: ean });
  }
  if (mappings.length === 0) {
    toast.warning('Keine Zuordnungen ausgewählt.');
    return;
  }

  loading.value.eanSync = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'apply_ean_from_refurbish', mappings }
    );
    if (response.data?.status === 'success') {
      const aktualisiert = Number(response.data.aktualisiert || 0);
      const uebersprungen = Number(response.data.uebersprungen || 0);
      eanSyncResult.value = {
        show: true,
        type: aktualisiert > 0 ? 'success' : 'info',
        title: aktualisiert > 0
          ? `${aktualisiert} EAN-Codes übernommen`
          : 'Keine EAN-Codes übernommen',
        aktualisiert,
        uebersprungen,
        nachher: Number(response.data.ohne_ean_nachher || 0),
        gesamt: Number(response.data.gesamt || 0)
      };
      if (aktualisiert > 0) {
        toast.success(`${aktualisiert} EAN-Codes übernommen`);
        await fetchKompaktliste();
      }
      eanPreviewDialog.value = false;
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim EAN-Apply:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.eanSync = false;
  }
};

// ----- EAN-Übernahme aus Lookup-Tabelle (analog zur Refurbish-Übernahme) -----
const openEanLookupPreview = async () => {
  loading.value.eanLookupSync = true;
  eanLookupSyncResult.value.show = false;
  eanLookupPreview.value = {
    ohne_ean_gesamt: 0,
    katalog_gesamt: 0,
    treffer_anzahl: 0,
    eindeutig: 0,
    mehrdeutig: 0,
    items: []
  };
  eanLookupSelected.value = {};
  eanLookupChoice.value = {};
  eanLookupPreviewDialog.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'preview_ean_from_lookup' }
    );
    if (response.data?.status === 'success') {
      eanLookupPreview.value = {
        ohne_ean_gesamt: Number(response.data.ohne_ean_gesamt || 0),
        katalog_gesamt: Number(response.data.katalog_gesamt || 0),
        treffer_anzahl: Number(response.data.treffer_anzahl || 0),
        eindeutig: Number(response.data.eindeutig || 0),
        mehrdeutig: Number(response.data.mehrdeutig || 0),
        items: response.data.items || []
      };
      for (const item of eanLookupPreview.value.items) {
        eanLookupSelected.value[item.cmmf_nummer] = item.kandidaten.length === 1;
        eanLookupChoice.value[item.cmmf_nummer] = item.kandidaten[0]?.ean || '';
      }
      if (eanLookupPreview.value.items.length === 0) {
        toast.info('Keine Treffer in der Lookup-Tabelle für offene EAN-Felder.');
      }
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
      eanLookupPreviewDialog.value = false;
    }
  } catch (error) {
    console.error('Fehler bei Lookup-EAN-Vorschau:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
    eanLookupPreviewDialog.value = false;
  } finally {
    loading.value.eanLookupSync = false;
  }
};

const toggleEanLookupSelectAll = (val) => {
  const target = !!val;
  for (const item of eanLookupPreview.value.items) {
    eanLookupSelected.value[item.cmmf_nummer] = target;
  }
};

const getSelectedLookupCandidateDescription = (item) => {
  const ean = eanLookupChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
  const cand = item.kandidaten.find(k => k.ean === ean) || item.kandidaten[0];
  if (!cand) return '';
  return cand.bezeichnung || '';
};

const applyEanLookupSelection = async () => {
  const mappings = [];
  for (const item of eanLookupPreview.value.items) {
    if (!eanLookupSelected.value[item.cmmf_nummer]) continue;
    const ean = eanLookupChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
    if (!ean) continue;
    mappings.push({ cmmf_nummer: item.cmmf_nummer, gtin_ean: ean });
  }
  if (mappings.length === 0) {
    toast.warning('Keine Zuordnungen ausgewählt.');
    return;
  }

  loading.value.eanLookupSync = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'apply_ean_from_lookup', mappings }
    );
    if (response.data?.status === 'success') {
      const aktualisiert = Number(response.data.aktualisiert || 0);
      const uebersprungen = Number(response.data.uebersprungen || 0);
      eanLookupSyncResult.value = {
        show: true,
        type: aktualisiert > 0 ? 'success' : 'info',
        title: aktualisiert > 0
          ? `${aktualisiert} EAN-Codes übernommen`
          : 'Keine EAN-Codes übernommen',
        aktualisiert,
        uebersprungen,
        nachher: Number(response.data.ohne_ean_nachher || 0),
        gesamt: Number(response.data.gesamt || 0)
      };
      if (aktualisiert > 0) {
        toast.success(`${aktualisiert} EAN-Codes übernommen`);
        await fetchKompaktliste();
      }
      eanLookupPreviewDialog.value = false;
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim Lookup-EAN-Apply:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.eanLookupSync = false;
  }
};

// ----- EAN-Übernahme per Kurztext-Match -----
const openKurztextPreview = async () => {
  loading.value.kurztextSync = true;
  kurztextSyncResult.value.show = false;
  kurztextPreview.value = { treffer_anzahl: 0, eindeutig: 0, mehrdeutig: 0, items: [] };
  kurztextSelected.value = {};
  kurztextChoice.value = {};
  kurztextPreviewDialog.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'preview_ean_by_kurztext' }
    );
    if (response.data?.status === 'success') {
      kurztextPreview.value = {
        treffer_anzahl: Number(response.data.treffer_anzahl || 0),
        eindeutig: Number(response.data.eindeutig || 0),
        mehrdeutig: Number(response.data.mehrdeutig || 0),
        items: response.data.items || []
      };
      for (const item of kurztextPreview.value.items) {
        // Vorauswahl: nur eindeutige Treffer angehakt, mehrdeutige bewusst leer
        kurztextSelected.value[item.katalog_id] = item.kandidaten.length === 1;
        kurztextChoice.value[item.katalog_id] = item.kandidaten[0]?.ean || '';
      }
      if (kurztextPreview.value.items.length === 0) {
        toast.info('Keine zusätzlichen Treffer per Kurztext-Match gefunden.');
      }
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
      kurztextPreviewDialog.value = false;
    }
  } catch (error) {
    console.error('Fehler bei Kurztext-Vorschau:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
    kurztextPreviewDialog.value = false;
  } finally {
    loading.value.kurztextSync = false;
  }
};

const toggleKurztextSelectAll = (val) => {
  const target = !!val;
  for (const item of kurztextPreview.value.items) {
    kurztextSelected.value[item.katalog_id] = target;
  }
};

const getSelectedKurztextCandidateInfo = (item) => {
  const ean = kurztextChoice.value[item.katalog_id] || item.kandidaten[0]?.ean;
  const cand = item.kandidaten.find(k => k.ean === ean) || item.kandidaten[0];
  if (!cand) return '';
  return cand.quelle_cmmf ? 'CMMF ' + cand.quelle_cmmf : '';
};

const applyKurztextSelection = async () => {
  const mappings = [];
  for (const item of kurztextPreview.value.items) {
    if (!kurztextSelected.value[item.katalog_id]) continue;
    const ean = kurztextChoice.value[item.katalog_id] || item.kandidaten[0]?.ean;
    if (!ean) continue;
    mappings.push({ katalog_id: item.katalog_id, gtin_ean: ean });
  }
  if (mappings.length === 0) {
    toast.warning('Keine Zuordnungen ausgewählt.');
    return;
  }

  loading.value.kurztextSync = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'apply_ean_by_kurztext', mappings }
    );
    if (response.data?.status === 'success') {
      const aktualisiert = Number(response.data.aktualisiert || 0);
      const uebersprungen = Number(response.data.uebersprungen || 0);
      kurztextSyncResult.value = {
        show: true,
        type: aktualisiert > 0 ? 'success' : 'info',
        title: aktualisiert > 0
          ? `${aktualisiert} EAN-Codes per Kurztext-Match übernommen`
          : 'Keine EAN-Codes übernommen',
        aktualisiert,
        uebersprungen,
        nachher: Number(response.data.ohne_ean_nachher || 0),
        gesamt: Number(response.data.gesamt || 0)
      };
      if (aktualisiert > 0) {
        toast.success(`${aktualisiert} EAN-Codes übernommen`);
        await fetchKatalogStats?.();
      }
      kurztextPreviewDialog.value = false;
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim Kurztext-Apply:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.kurztextSync = false;
  }
};

// ----- EAN-Übernahme aus archivierten Inventuren -----
const openArchivEanPreview = async () => {
  loading.value.archivEanSync = true;
  archivEanSyncResult.value.show = false;
  archivEanPreview.value = {
    ohne_ean_gesamt: 0,
    katalog_gesamt: 0,
    treffer_anzahl: 0,
    eindeutig: 0,
    mehrdeutig: 0,
    items: []
  };
  archivEanSelected.value = {};
  archivEanChoice.value = {};
  archivEanPreviewDialog.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      {
        action: 'preview_ean_from_archiv',
        source_archiv_id: Number(archivEanSourceId.value) || 0
      }
    );
    if (response.data?.status === 'success') {
      archivEanPreview.value = {
        ohne_ean_gesamt: Number(response.data.ohne_ean_gesamt || 0),
        katalog_gesamt: Number(response.data.katalog_gesamt || 0),
        treffer_anzahl: Number(response.data.treffer_anzahl || 0),
        eindeutig: Number(response.data.eindeutig || 0),
        mehrdeutig: Number(response.data.mehrdeutig || 0),
        items: response.data.items || []
      };
      for (const item of archivEanPreview.value.items) {
        archivEanSelected.value[item.cmmf_nummer] = item.kandidaten.length === 1;
        archivEanChoice.value[item.cmmf_nummer] = item.kandidaten[0]?.ean || '';
      }
      if (archivEanPreview.value.items.length === 0) {
        toast.info('Keine Treffer in archivierten Inventuren für offene EAN-Felder.');
      }
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
      archivEanPreviewDialog.value = false;
    }
  } catch (error) {
    console.error('Fehler bei Archiv-EAN-Vorschau:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
    archivEanPreviewDialog.value = false;
  } finally {
    loading.value.archivEanSync = false;
  }
};

const toggleArchivEanSelectAll = (val) => {
  const target = !!val;
  for (const item of archivEanPreview.value.items) {
    archivEanSelected.value[item.cmmf_nummer] = target;
  }
};

const getSelectedArchivCandidateInfo = (item) => {
  const ean = archivEanChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
  const cand = item.kandidaten.find(k => k.ean === ean) || item.kandidaten[0];
  if (!cand) return '';
  const parts = [];
  if (cand.archiv_nr) parts.push(cand.archiv_nr);
  if (cand.archiv_name) parts.push(cand.archiv_name);
  if (cand.archiv_kurztext && cand.archiv_kurztext !== item.kurztext) {
    parts.push('„' + cand.archiv_kurztext + '"');
  }
  return parts.join(' · ');
};

const applyArchivEanSelection = async () => {
  const mappings = [];
  for (const item of archivEanPreview.value.items) {
    if (!archivEanSelected.value[item.cmmf_nummer]) continue;
    const ean = archivEanChoice.value[item.cmmf_nummer] || item.kandidaten[0]?.ean;
    if (!ean) continue;
    mappings.push({ cmmf_nummer: item.cmmf_nummer, gtin_ean: ean });
  }
  if (mappings.length === 0) {
    toast.warning('Keine Zuordnungen ausgewählt.');
    return;
  }

  loading.value.archivEanSync = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      {
        action: 'apply_ean_from_archiv',
        source_archiv_id: Number(archivEanSourceId.value) || 0,
        mappings
      }
    );
    if (response.data?.status === 'success') {
      const aktualisiert = Number(response.data.aktualisiert || 0);
      const uebersprungen = Number(response.data.uebersprungen || 0);
      archivEanSyncResult.value = {
        show: true,
        type: aktualisiert > 0 ? 'success' : 'info',
        title: aktualisiert > 0
          ? `${aktualisiert} EAN-Codes aus Archiv übernommen`
          : 'Keine EAN-Codes übernommen',
        aktualisiert,
        uebersprungen,
        nachher: Number(response.data.ohne_ean_nachher || 0),
        gesamt: Number(response.data.gesamt || 0)
      };
      if (aktualisiert > 0) {
        toast.success(`${aktualisiert} EAN-Codes übernommen`);
        await fetchKatalogStats?.();
      }
      archivEanPreviewDialog.value = false;
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim Archiv-EAN-Apply:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.archivEanSync = false;
  }
};

// ----- Artikel-Katalog verwalten (Tabellen-Ansicht im Office) -----
const loadKatalogVerwaltung = async () => {
  loading.value.katalogVerwaltung = true;
  try {
    const response = await axios.get(
      `${API_BASE_URL}inventur/index.php`,
      { params: { action: 'get_katalog' } }
    );
    const d = response.data || {};
    if (d.status === 'success' && Array.isArray(d.katalog)) {
      katalogVerwaltungListe.value = d.katalog;
    } else {
      toast.error('Katalog konnte nicht geladen werden');
    }
  } catch (error) {
    console.error('Fehler beim Laden des Katalogs:', error);
    toast.error('Fehler beim Laden des Katalogs');
  } finally {
    loading.value.katalogVerwaltung = false;
  }
};

// Toggle fuer die Tabelle im Tab "Artikel-Import": beim Oeffnen wird der
// Katalog lazy nachgeladen, beim Schliessen die Liste geleert um Speicher zu sparen.
const toggleKatalogVerwaltung = async () => {
  if (katalogVerwaltungVisible.value) {
    katalogVerwaltungVisible.value = false;
    return;
  }
  katalogVerwaltungVisible.value = true;
  if (!katalogVerwaltungListe.value.length) {
    await loadKatalogVerwaltung();
  }
};

const openKatalogEdit = (item) => {
  katalogEditResult.value = { show: false, type: 'info', title: '', message: '' };
  if (item) {
    katalogEditMode.value = 'edit';
    katalogEditForm.value = {
      cmmf_nummer: String(item.cmmf_nummer || ''),
      gtin_ean: String(item.gtin_ean || ''),
      kurztext: String(item.kurztext || ''),
      werk: String(item.werk || ''),
      lagerort: String(item.lagerort || ''),
      sollbestand: item.sollbestand ?? null
    };
  } else {
    katalogEditMode.value = 'create';
    katalogEditForm.value = {
      cmmf_nummer: '',
      gtin_ean: '',
      kurztext: '',
      werk: '',
      lagerort: '',
      sollbestand: null
    };
  }
  katalogEditDialog.value = true;
};

const closeKatalogEdit = () => {
  katalogEditDialog.value = false;
  katalogEditResult.value = { show: false, type: 'info', title: '', message: '' };
};

const saveKatalogEdit = async (confirmDuplicateEan = false) => {
  const f = katalogEditForm.value;
  const cmmf = String(f.cmmf_nummer || '').trim();
  if (!cmmf) {
    toast.warning('CMMF-Nummer erforderlich');
    return;
  }
  const ean = String(f.gtin_ean || '').trim();
  if (ean && !/^\d{6,18}$/.test(ean)) {
    toast.error('EAN-Code ungültig (nur Ziffern, 6–18 Stellen)');
    return;
  }
  const isCreate = katalogEditMode.value === 'create';
  if (isCreate && !String(f.kurztext || '').trim()) {
    toast.warning('Kurztext für Neuanlage erforderlich');
    return;
  }

  loading.value.katalogEdit = true;
  katalogEditResult.value = { show: false, type: 'info', title: '', message: '' };
  try {
    const payload = {
      action: 'office_upsert_artikel',
      cmmf_nummer: cmmf,
      gtin_ean: ean,
      kurztext: String(f.kurztext || '').trim(),
      werk: String(f.werk || '').trim(),
      lagerort: String(f.lagerort || '').trim(),
      allow_create: isCreate
    };
    if (f.sollbestand !== null && f.sollbestand !== '' && !Number.isNaN(Number(f.sollbestand))) {
      payload.sollbestand = Number(f.sollbestand);
    }
    if (confirmDuplicateEan) {
      payload.confirm_duplicate_ean = true;
    }

    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      payload
    );
    const d = response.data || {};
    if (d.status === 'success') {
      const mode = d.mode || 'updated';
      const titel = mode === 'created'
        ? `Artikel ${cmmf} neu angelegt`
        : mode === 'unchanged'
          ? 'Keine Änderungen'
          : `Artikel ${cmmf} aktualisiert`;
      toast.success(titel);
      eanDuplicateDialog.value = false;
      katalogEditDialog.value = false;
      // Liste nur neu laden, wenn sie aktuell sichtbar ist.
      const tasks = [fetchKatalogStats?.()];
      if (katalogVerwaltungVisible.value) {
        tasks.push(loadKatalogVerwaltung());
      }
      await Promise.all(tasks);
    } else if (d.status === 'duplicate_ean') {
      // EAN ist bereits anderen CMMFs zugeordnet – Mehrfach-Zuordnung ist
      // erlaubt, aber muss aktiv bestätigt werden.
      eanDuplicateData.value = {
        ean: d.ean || ean,
        konflikte: Array.isArray(d.konflikte) ? d.konflikte : [],
        message: d.message || ''
      };
      eanDuplicateDialog.value = true;
    } else if (d.status === 'not_found') {
      katalogEditResult.value = {
        show: true,
        type: 'warning',
        title: 'Artikel nicht vorhanden',
        message: d.message || 'Beim Öffnen über „Neuer Artikel" wird die Neuanlage automatisch erlaubt.'
      };
    } else {
      const msg = d.message || 'Unbekannt';
      katalogEditResult.value = { show: true, type: 'error', title: 'Fehler', message: msg };
      toast.error('Fehler: ' + msg);
    }
  } catch (error) {
    console.error('Fehler beim Speichern des Artikels:', error);
    const msg = error?.response?.data?.message || error.message;
    katalogEditResult.value = { show: true, type: 'error', title: 'Fehler', message: msg };
    toast.error('Fehler: ' + msg);
  } finally {
    loading.value.katalogEdit = false;
  }
};

// Wird aus dem Bestätigungs-Dialog aufgerufen: speichert den Artikel
// trotz Mehrfach-EAN-Zuordnung (Audit-Log markiert die Bestätigung).
const confirmDuplicateEanAndSave = () => {
  return saveKatalogEdit(true);
};

const startImport = async () => {
  if (parsedImportRows.value.length === 0) {
    toast.error('Keine Daten zum Importieren – bitte zuerst aus Excel kopieren & einfügen.');
    return;
  }

  const allRows = [...parsedImportRows.value];
  const total = allRows.length;
  const chunkTotal = Math.max(1, Math.ceil(total / IMPORT_CHUNK_SIZE));

  loading.value.import = true;
  importResult.value.show = false;
  importProgress.value = {
    active: true,
    done: 0,
    total,
    percent: 0,
    chunkIndex: 0,
    chunkTotal,
    label: 'Import startet …'
  };

  let success = 0;
  let errors = 0;
  const messages = [];
  let sollChanges = 0;
  let lastBeleg = {
    sha: null,
    beleg: null,
    belegName: null,
    belegId: null,
    belegStatus: null,
    belegError: null
  };
  let ohneKurztext = 0;
  let aborted = false;

  try {
    for (let chunkIndex = 1; chunkIndex <= chunkTotal; chunkIndex++) {
      const start = (chunkIndex - 1) * IMPORT_CHUNK_SIZE;
      const chunk = allRows.slice(start, start + IMPORT_CHUNK_SIZE);
      const isFinal = chunkIndex === chunkTotal;

      importProgress.value.chunkIndex = chunkIndex;
      importProgress.value.label = isFinal
        ? `Chunk ${chunkIndex}/${chunkTotal} – Abschluss (Sync) …`
        : `Chunk ${chunkIndex}/${chunkTotal} wird importiert …`;

      let response;
      try {
        response = await axios.post(
          `${API_BASE_URL}inventur/import_artikel.php`,
          {
            action: 'import_rows',
            rows: chunk,
            finalize: isFinal,
            chunk_index: chunkIndex,
            chunk_total: chunkTotal
          },
          { timeout: 180000 }
        );
      } catch (chunkErr) {
        aborted = true;
        console.error(`Import-Chunk ${chunkIndex}/${chunkTotal} fehlgeschlagen:`, chunkErr);
        toast.error(
          `Import abgebrochen bei Chunk ${chunkIndex}/${chunkTotal}: `
          + (chunkErr.response?.data?.message || chunkErr.message)
        );
        break;
      }

      if (response.data?.status !== 'success') {
        aborted = true;
        toast.error(
          `Import abgebrochen bei Chunk ${chunkIndex}/${chunkTotal}: `
          + (response.data?.message || 'Unbekannt')
        );
        break;
      }

      success += Number(response.data.erfolgreiche_zeilen || 0);
      errors += Number(response.data.fehlerhafte_zeilen || 0);
      sollChanges += Number(response.data.sollbestand_aenderungen || 0);
      const chunkMsgs = response.data.fehlermeldungen || [];
      for (const msg of chunkMsgs) {
        if (messages.length < 50) messages.push(msg);
        else if (messages.length === 50) {
          messages.push('… weitere Fehler unterdrückt');
          break;
        }
      }

      if (response.data.sollbestand_diff_sha256 || response.data.sollbestand_diff_beleg_id) {
        const belegRel = response.data.sollbestand_diff_beleg || null;
        lastBeleg = {
          sha: response.data.sollbestand_diff_sha256 || lastBeleg.sha,
          beleg: belegRel || lastBeleg.beleg,
          belegName: belegRel ? String(belegRel).split('/').pop() : lastBeleg.belegName,
          belegId: response.data.sollbestand_diff_beleg_id || lastBeleg.belegId,
          belegStatus: response.data.sollbestand_diff_beleg_status || lastBeleg.belegStatus,
          belegError: response.data.sollbestand_diff_beleg_error || lastBeleg.belegError
        };
      }
      if (isFinal) {
        ohneKurztext = Number(response.data.ohne_kurztext || 0);
      }

      importProgress.value.done = Math.min(total, start + chunk.length);
      importProgress.value.percent = Math.round((importProgress.value.done / total) * 100);
    }

    importProgress.value.label = aborted ? 'Import abgebrochen' : 'Import abgeschlossen';
    importProgress.value.percent = aborted
      ? importProgress.value.percent
      : 100;

    importResult.value = {
      show: true,
      type: aborted || errors > 0 ? 'warning' : 'success',
      title: aborted
        ? 'Import teilweise / abgebrochen'
        : (errors === 0 ? 'Import erfolgreich!' : 'Import mit Fehlern abgeschlossen'),
      total,
      success,
      errors,
      messages,
      sollChanges,
      sollDiffSha: lastBeleg.sha,
      sollDiffBeleg: lastBeleg.beleg,
      sollDiffBelegName: lastBeleg.belegName,
      sollDiffBelegId: lastBeleg.belegId,
      sollDiffBelegStatus: lastBeleg.belegStatus,
      sollDiffBelegError: lastBeleg.belegError,
      ohneKurztext
    };

    if (!aborted && errors === 0) {
      toast.success(`${success.toLocaleString('de-DE')} Artikel erfolgreich importiert/aktualisiert`);
      importPasteText.value = '';
      parsedImportRows.value = [];
      await fetchKompaktliste();
      await fetchKatalogStats();
    } else {
      toast.warning(
        `Import: ${success.toLocaleString('de-DE')} OK, ${errors.toLocaleString('de-DE')} Fehler`
        + (aborted ? ' (abgebrochen)' : '')
      );
      await fetchKatalogStats();
    }

    if (!aborted && ohneKurztext > 0) {
      await openKurztextNachpflege();
    }
  } catch (error) {
    console.error('Fehler beim Import:', error);
    toast.error('Fehler beim Import: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.import = false;
    importProgress.value.active = false;
  }
};

const downloadSollDiff = (resultOrName) => {
  const params = new URLSearchParams({ action: 'download_soll_diff' });
  if (resultOrName && typeof resultOrName === 'object') {
    if (resultOrName.sollDiffBelegId) {
      params.set('id', String(resultOrName.sollDiffBelegId));
    } else if (resultOrName.sollDiffSha) {
      params.set('sha256', String(resultOrName.sollDiffSha));
    } else if (resultOrName.sollDiffBelegName || resultOrName.sollDiffBeleg) {
      const name = resultOrName.sollDiffBelegName
        || String(resultOrName.sollDiffBeleg).split('/').pop();
      params.set('filename', name);
    } else {
      return;
    }
  } else if (resultOrName) {
    params.set('filename', String(resultOrName));
  } else {
    return;
  }
  window.open(`${API_BASE_URL}inventur/import_artikel.php?${params.toString()}`, '_blank');
};

// =====================================================================
// EAN-LOOKUP-TABELLE (Excel-Paste-Import)
// =====================================================================

const onEanLookupPaste = () => {
  setTimeout(() => parseEanLookupPaste(), 0);
};

const clearEanLookupPaste = () => {
  eanLookupPasteText.value = '';
  parsedEanLookupRows.value = [];
  eanLookupResult.value.show = false;
};

// Erwartet drei Spalten: EAN | CMMF | Bezeichnung. Trenner Tab oder Semikolon.
// Falls eine Zeile mehr Spalten hat, werden ab Spalte 3 alle als Bezeichnung
// zusammengeführt. EAN wird auf Ziffern reduziert (Excel rendert Barcodes
// teilweise mit Leerzeichen oder als wissenschaftliche Notation).
const parseEanLookupPaste = () => {
  const text = String(eanLookupPasteText.value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!text.trim()) {
    parsedEanLookupRows.value = [];
    return;
  }
  const lines = text.split('\n').filter(l => l.trim() !== '');
  const startIdx = eanLookupHasHeader.value && lines.length > 1 ? 1 : 0;
  const out = [];
  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i];
    const sep = line.includes('\t') ? '\t' : (line.includes(';') ? ';' : '\t');
    const parts = line.split(sep).map(s => s.trim());
    const eanRaw = parts[0] || '';
    const cmmfRaw = parts[1] || '';
    const bezRaw = parts.slice(2).join(' ').trim();
    const ean = eanRaw.replace(/\D/g, '');
    const cmmf = cmmfRaw.replace(/\s+/g, '');
    if (!ean || !cmmf) continue;
    out.push({ ean, cmmf_nummer: cmmf, bezeichnung: bezRaw || '' });
  }
  parsedEanLookupRows.value = out;
};

const fetchEanLookupStats = async () => {
  loading.value.eanLookupStats = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_ean_lookup_stats' }
    });
    if (response.data?.status === 'success') {
      eanLookupStats.value = {
        gesamt: Number(response.data.gesamt || 0),
        letzte_aktualisierung: response.data.letzte_aktualisierung || null
      };
    }
  } catch (error) {
    console.error('Fehler beim Laden der EAN-Lookup-Stats:', error);
  } finally {
    loading.value.eanLookupStats = false;
  }
};

const fetchKatalogStats = async () => {
  loading.value.katalogStats = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, {
      params: { action: 'get_katalog_stats' }
    });
    if (response.data?.status === 'success') {
      katalogStats.value = {
        gesamt: Number(response.data.gesamt || 0),
        mit_ean: Number(response.data.mit_ean || 0),
        ohne_ean: Number(response.data.ohne_ean || 0),
        ohne_kurztext: Number(response.data.ohne_kurztext || 0),
        letzte_aktualisierung: response.data.letzte_aktualisierung || null
      };
    }
  } catch (error) {
    console.error('Fehler beim Laden der Katalog-Stats:', error);
  } finally {
    loading.value.katalogStats = false;
  }
};

const openKurztextNachpflege = async () => {
  loading.value.kurztextNachpflege = true;
  kurztextNachpflegeDialog.value = true;
  kurztextNachpflegeItems.value = [];
  kurztextNachpflegeDraft.value = {};
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'get_katalog_ohne_kurztext' }
    );
    if (response.data?.status === 'success') {
      const items = response.data.items || [];
      kurztextNachpflegeItems.value = items;
      const draft = {};
      for (const row of items) {
        draft[row.id] = '';
      }
      kurztextNachpflegeDraft.value = draft;
      if (items.length === 0) {
        toast.info('Keine Artikel ohne Kurztext.');
      }
    } else {
      toast.error(response.data?.message || 'Laden fehlgeschlagen');
      kurztextNachpflegeDialog.value = false;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Kurztext-Lücken:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
    kurztextNachpflegeDialog.value = false;
  } finally {
    loading.value.kurztextNachpflege = false;
  }
};

const saveKurztextNachpflege = async () => {
  const mappings = [];
  for (const row of kurztextNachpflegeItems.value) {
    const kt = String(kurztextNachpflegeDraft.value[row.id] || '').trim();
    if (!kt) continue;
    mappings.push({
      katalog_id: row.id,
      cmmf_nummer: row.cmmf_nummer,
      kurztext: kt
    });
  }
  if (mappings.length === 0) {
    toast.warning('Bitte mindestens einen Kurztext eintragen.');
    return;
  }

  loading.value.kurztextNachpflegeSave = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}inventur/import_artikel.php`,
      { action: 'apply_kurztexte', mappings }
    );
    if (response.data?.status === 'success') {
      const n = Number(response.data.aktualisiert || 0);
      const rest = Number(response.data.ohne_kurztext_nachher || 0);
      toast.success(`${n} Kurztext(e) gespeichert`);
      importResult.value.ohneKurztext = rest;
      await fetchKatalogStats();
      if (katalogVerwaltungVisible.value) {
        await loadKatalogVerwaltung();
      }
      if (rest > 0) {
        await openKurztextNachpflege();
      } else {
        kurztextNachpflegeDialog.value = false;
      }
    } else {
      toast.error(response.data?.message || 'Speichern fehlgeschlagen');
    }
  } catch (error) {
    console.error('Fehler beim Speichern der Kurztexte:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.kurztextNachpflegeSave = false;
  }
};

const startEanLookupImport = async () => {
  if (parsedEanLookupRows.value.length === 0) {
    toast.error('Keine Daten zum Importieren – bitte zuerst aus Excel kopieren & einfügen.');
    return;
  }
  if (eanLookupReplaceAll.value) {
    if (!confirm('Bestehende EAN-Lookup-Tabelle wirklich vor dem Import komplett LÖSCHEN?')) {
      return;
    }
  }
  loading.value.eanLookupImport = true;
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'import_ean_lookup',
      rows: parsedEanLookupRows.value,
      replace_all: !!eanLookupReplaceAll.value
    });
    if (response.data?.status === 'success') {
      const total = parsedEanLookupRows.value.length;
      const eingefuegt = Number(response.data.eingefuegt || 0);
      const aktualisiert = Number(response.data.aktualisiert || 0);
      const uebersprungen = Number(response.data.uebersprungen || 0);
      const geloescht = Number(response.data.geloescht || 0);
      const fehler = response.data.fehler || [];
      eanLookupResult.value = {
        show: true,
        type: fehler.length === 0 ? 'success' : 'warning',
        title: fehler.length === 0 ? 'EAN-Lookup-Import erfolgreich!' : 'Import mit Hinweisen abgeschlossen',
        total,
        eingefuegt,
        aktualisiert,
        uebersprungen,
        geloescht,
        fehler
      };
      toast.success(`EAN-Lookup: ${eingefuegt} neu, ${aktualisiert} aktualisiert`);
      eanLookupPasteText.value = '';
      parsedEanLookupRows.value = [];
      eanLookupReplaceAll.value = false;
      await fetchEanLookupStats();
    } else {
      toast.error('Fehler beim Import: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim EAN-Lookup-Import:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.eanLookupImport = false;
  }
};

const clearEanLookupTable = async () => {
  if (!confirm('Komplette EAN-Lookup-Tabelle wirklich leeren? Diese Aktion wird im Audit-Log dokumentiert.')) {
    return;
  }
  loading.value.eanLookupClear = true;
  try {
    const response = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
      action: 'clear_ean_lookup'
    });
    if (response.data?.status === 'success') {
      toast.success(`${response.data.geloescht || 0} Einträge entfernt`);
      await fetchEanLookupStats();
    } else {
      toast.error('Fehler: ' + (response.data?.message || 'Unbekannt'));
    }
  } catch (error) {
    console.error('Fehler beim Leeren:', error);
    toast.error('Fehler: ' + (error.response?.data?.message || error.message));
  } finally {
    loading.value.eanLookupClear = false;
  }
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleString('de-DE');
};

// Deutsches Datum/Uhrzeit immer mit fuehrenden Nullen: DD.MM.YYYY HH:mm
// (toLocaleString('de-DE') liefert je nach Browser/Node-Locale teils
// einstellige Monats- oder Tagwerte, z. B. "13.5.2026").
const formatDeDateTime = (dateTime) => {
  if (!dateTime) return '';
  const d = (dateTime instanceof Date) ? dateTime : new Date(String(dateTime).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(dateTime);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ========================================
// ENDLOSLISTE (GoBD-konformer Druck)
// ========================================

const openEndloslisteDialog = () => {
  if (!endloslisteFilter.value.druckJahr) {
    endloslisteFilter.value.druckJahr = new Date().getFullYear() - 1;
  }
  endloslisteDialog.value = true;
};

const executePrintEndlosliste = async () => {
  loading.value.endlosliste = true;
  try {
    const params = { action: 'get_endlosliste', archiv_id: selectedArchivId.value || 0 };
    if (endloslisteFilter.value.jahr) params.jahr = endloslisteFilter.value.jahr;
    if (endloslisteFilter.value.status) params.status = endloslisteFilter.value.status;

    const response = await axios.get(`${API_BASE_URL}/inventur/index.php`, { params });
    if (response.data?.status !== 'success') {
      toast.error('Endlosliste konnte nicht geladen werden');
      return;
    }
    const positionen = response.data.positionen || [];
    if (positionen.length === 0) {
      toast.warning('Keine Positionen für die gewählten Filter gefunden');
      return;
    }
    const palettenIds = new Set(positionen.map(p => p.palette_id));

    // Druck im Audit-Log dokumentieren (vor dem Drucken, damit auch ein
    // Abbruch im Druckdialog erfasst wird)
    let auditAnchor = null;
    try {
      const logResp = await axios.post(`${API_BASE_URL}/inventur/index.php`, {
        action: 'log_endlosliste_druck',
        jahr: endloslisteFilter.value.jahr || null,
        status: endloslisteFilter.value.status || '',
        anzahl_positionen: positionen.length,
        anzahl_paletten: palettenIds.size,
        ansager_name: endloslisteFilter.value.ansager || '',
        schreiber_name: endloslisteFilter.value.schreiber || ''
      });
      auditAnchor = logResp?.data?.audit_anchor || null;
    } catch (e) {
      console.warn('Audit-Log-Eintrag fehlgeschlagen', e);
    }

    const { html, druckDatum, benutzer } = buildEndloslisteHtml(positionen, {
      jahr: endloslisteFilter.value.druckJahr,
      status: endloslisteFilter.value.status,
      ansager: endloslisteFilter.value.ansager,
      schreiber: endloslisteFilter.value.schreiber,
      anzahlPaletten: palettenIds.size,
      auditAnchor
    });

    // Pagination im Hauptfenster (versteckter iframe) ausfuehren, damit
    // das Druck-Popup ohne Inline-Script auskommt und nicht von strengen
    // Content-Security-Policies blockiert wird.
    // Iframe großzügig dimensionieren, damit auch lange Listen vollständig
    // gerendert/vermessen werden – an der Iframe-Grenze verhalten sich Browser
    // bezüglich offsetHeight nicht immer identisch.
    const measure = document.createElement('iframe');
    measure.setAttribute('aria-hidden', 'true');
    measure.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:4000mm;border:0;visibility:hidden;';
    document.body.appendChild(measure);
    const idoc = measure.contentDocument || measure.contentWindow.document;
    idoc.open();
    idoc.write(html);
    idoc.close();
    await new Promise((resolve) => {
      if (idoc.readyState === 'complete') resolve();
      else measure.addEventListener('load', () => resolve(), { once: true });
    });
    try {
      paginateEndloslisteDoc(idoc, druckDatum, benutzer);
    } catch (e) {
      console.error('Pagination fehlgeschlagen', e);
    }
    const finalHtml = '<!DOCTYPE html>\n' + idoc.documentElement.outerHTML;
    measure.remove();

    const win = window.open('', '_blank', 'width=1100,height=900');
    if (!win) {
      toast.error('Popup blockiert. Bitte Popups erlauben.');
      return;
    }
    win.document.open();
    win.document.write(finalHtml);
    win.document.close();
    win.focus();

    // Auto-Zoom im Popup: Seite soll in die Fensterbreite passen, damit der
    // Anwender sofort sieht, dass alle Spalten vorhanden sind. Greift NICHT in
    // den Druck ein (zoom ist eine reine Bildschirm-Eigenschaft).
    const applyZoom = () => {
      try {
        const pageWidthPx = 190 /* mm */ * (96 / 25.4); // ~718px
        const available = (win.innerWidth || 1000) - 40;
        const z = Math.min(1, available / pageWidthPx);
        if (win.document?.body) win.document.body.style.zoom = String(z);
      } catch (e) { /* ignore */ }
    };
    try {
      applyZoom();
      win.addEventListener('resize', applyZoom);
    } catch (e) { /* ignore */ }

    setTimeout(() => { try { win.print(); } catch (e) { console.warn(e); } }, 350);

    endloslisteDialog.value = false;
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Drucken der Endlosliste');
  } finally {
    loading.value.endlosliste = false;
  }
};

const buildEndloslisteHtml = (positionen, meta) => {
  const druckDatum = formatDeDateTime(new Date());
  const benutzer = (authStore.user?.vorname && authStore.user?.nachname)
    ? `${authStore.user.vorname} ${authStore.user.nachname}`
    : (authStore.user?.loginname || 'Unbekannt');
  const statusLabel = meta.status
    ? ({ erfassung: 'In Erfassung', abgeschlossen: 'Abgeschlossen', archiv: 'Archiv' }[meta.status] || meta.status)
    : 'Alle';
  const jahrLabel = meta.jahr ? String(meta.jahr) : 'Alle Jahre';

  let lfdNr = 0;
  let gesamtIst = 0;
  let lastPaletteId = null;  const rows = positionen.map(p => {
    lfdNr += 1;
    const ist = Number(p.istbestand || 0);
    gesamtIst += ist;
    const showHeader = p.palette_id !== lastPaletteId;
    lastPaletteId = p.palette_id;
    const paletteHeaderRow = showHeader
      ? `<tr class="palette-header"><td colspan="8">
            Palette: <strong>${escapeHtml(p.paletten_nr || '')}</strong>
            &nbsp;|&nbsp; Status: ${escapeHtml(p.palette_status || '')}
            &nbsp;|&nbsp; Erfasst von: ${escapeHtml(p.erstellt_von_name || '-')}
            &nbsp;|&nbsp; Erfassung: ${p.erfassung_datum ? new Date(p.erfassung_datum.replace(' ', 'T')).toLocaleString('de-DE') : '-'}
         </td></tr>`
      : '';
    const scanZeit = p.scan_zeitpunkt
      ? new Date(p.scan_zeitpunkt.replace(' ', 'T')).toLocaleString('de-DE')
      : '';
    return paletteHeaderRow + `<tr>
      <td class="num">${lfdNr}</td>
      <td>${escapeHtml(p.paletten_nr || '')}</td>
      <td>${escapeHtml(p.cmmf_nummer || '')}</td>
      <td>${escapeHtml(p.kurztext || '')}</td>
      <td>${escapeHtml(p.werk || '')}</td>
      <td>${escapeHtml(p.lagerort || '')}</td>
      <td class="num"><strong>${ist}</strong></td>
      <td class="kontrolle">&nbsp;</td>
    </tr>`;
  }).join('');

  const anchor = meta.auditAnchor || null;
  const anchorHash = anchor?.hash ? String(anchor.hash) : '';
  const anchorLogId = anchor?.log_id != null ? String(anchor.log_id) : '';
  const anchorAt = anchor?.anchor_at ? String(anchor.anchor_at) : '';
  const anchorBlock = anchorHash
    ? `<div class="hash-anchor sig-block">
         <div class="ha-title">Hash-Anker (GoBD / Audit-Log SHA-256)</div>
         <div class="ha-meta">Druckzeit: ${escapeHtml(druckDatum)}</div>
         <div class="ha-meta">Letzter Audit-Log-Eintrag: #${escapeHtml(anchorLogId)} vom ${escapeHtml(anchorAt)}</div>
         <div class="ha-hash">${escapeHtml(anchorHash)}</div>
         <div class="ha-note">
           Dieser SHA-256-Hash ankert die gesamte Inventur-Audit-Kette zum Druckzeitpunkt.
           Stimmt er bei einer späteren Prüfung mit dem Wert in der Datenbank
           bzw. CSV-Export überein, ist bewiesen, dass seit der Unterschrift kein
           Datensatz in der Kette verändert wurde.
         </div>
         <div class="ha-sigs">
           <div class="sig">
             <div class="sig-line"></div>
             <div class="sig-cap">Hash-Anker bestätigt (Unterschrift / Datum)</div>
           </div>
           <div class="sig">
             <div class="sig-line"></div>
             <div class="sig-cap">Inventurleitung (Unterschrift / Datum)</div>
           </div>
         </div>
       </div>`
    : `<div class="hash-anchor sig-block"><div class="ha-note">Kein Audit-Log-Anker verfügbar.</div></div>`;

  const html = `<html><head><meta charset="UTF-8"><title>Inventur-Zählergebnis</title>
    <style>
      @page { size: A4; margin: 12mm 10mm 18mm 10mm; }
      body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; padding: 0; color: #000; }
      .doc-header { display: flex; align-items: center; gap: 8mm; border-bottom: 2px solid #000; padding-bottom: 4mm; margin-bottom: 4mm; }
      .doc-header .dh-logo { flex: 0 0 36mm; }
      .doc-header .dh-logo svg { display: block; width: 100%; height: auto; }
      .doc-header .dh-body { flex: 1; }
      .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; }
      .doc-header .meta { font-size: 9pt; }
      .doc-header .meta span { margin-right: 6mm; }
      /* table-layout:fixed ist entscheidend: ohne diesen Hinweis verteilt der
         Browser die Spaltenbreiten je nach Inhalt der Tabelle. Die Mess-Tabelle
         (alle Zeilen) und die Per-Seite-Tabelle (nur wenige Zeilen) bekommen
         dann unterschiedliche Artikel-Spaltenbreiten → Texte umbrechen anders
         → Zeilen werden im Druck höher als gemessen → Inhalt rutscht unter den
         Footer. Mit table-layout:fixed gelten ausschließlich die <th>-Breiten. */
      /* Tabelle füllt die nutzbare Breite der .print-page (190mm minus 2x8mm Padding = 174mm). */
      table.list { width: 100%; border-collapse: collapse; table-layout: fixed; }
      table.list thead { display: table-header-group; }
      table.list tfoot { display: table-row-group; }
      table.list th, table.list td {
        border: 1px solid #444;
        padding: 1.2mm 1.4mm;
        font-size: 9pt;
        vertical-align: top;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }
      table.list th { background: #e0e0e0; text-align: left; }
      table.list td.num { text-align: right; font-variant-numeric: tabular-nums; }
      table.list tr.palette-header td {
        background: #f4f4f4;
        font-size: 9pt;
        padding: 1mm 1.4mm;
      }
      tr { page-break-inside: avoid; }
      .summary {
        margin-top: 4mm;
        padding: 2mm 3mm;
        border: 1px solid #000;
        background: #f7f7f7;
        font-size: 10pt;
      }
      .signatures {
        display: flex;
        justify-content: space-between;
        gap: 8mm;
        margin-top: 10mm;
        page-break-inside: avoid;
      }
      .sig { width: 48%; }
      .sig-lbl { font-weight: bold; margin-bottom: 14mm; }
      .sig-line { border-bottom: 1px solid #000; min-height: 6mm; padding: 0 2mm; }
      .sig-cap { font-size: 8.5pt; color: #555; text-align: center; margin-top: 1mm; }
      .print-footer { font-size: 8pt; color: #555; margin-top: 8mm; text-align: right; }

      .hash-anchor {
        margin-top: 8mm;
        border: 1px solid #000;
        padding: 3mm 4mm;
        font-size: 9pt;
        page-break-inside: avoid;
      }
      .hash-anchor .ha-title {
        font-weight: bold;
        font-size: 10pt;
        margin-bottom: 2mm;
      }
      .hash-anchor .ha-meta { margin-bottom: 1mm; }
      .hash-anchor .ha-hash {
        margin-top: 2mm;
        font-family: 'Courier New', monospace;
        font-size: 8.5pt;
        word-break: break-all;
        background: #f7f7f7;
        padding: 2mm;
        border: 1px dashed #888;
      }
      .hash-anchor .ha-note {
        margin-top: 2mm;
        font-size: 8pt;
        color: #444;
      }
      .hash-anchor .ha-sigs {
        display: flex;
        justify-content: space-between;
        gap: 8mm;
        margin-top: 6mm;
      }
      .hash-anchor .ha-sigs .sig { width: 48%; }
      .hash-anchor .ha-sigs .sig-line { margin-top: 10mm; }

      /* Pagination: jede print-page entspricht genau einer A4-Seite.
         Horizontales Padding sorgt dafür, dass ALLE Inhalte (Tabelle, Header,
         Zusammenfassung, Hash-Anker) garantiert innerhalb des sicheren
         Druckbereichs bleiben – unabhängig von Drucker-Treiber-Abweichungen. */
      .print-page {
        position: relative;
        box-sizing: border-box;
        width: 190mm;            /* A4 Breite (210) minus 2x10mm Seitenrand */
        min-height: 267mm;       /* A4 Höhe (297) minus 12mm oben + 18mm unten */
        max-height: 267mm;
        overflow: hidden;        /* hart abschneiden, damit 1 print-page = 1 Blatt */
        padding: 0 8mm 22mm 8mm; /* 8mm links/rechts Puffer, 22mm unten für Footer */
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid; /* keine Browser-eigene Trennung innerhalb */
        break-inside: avoid;
      }
      .print-page:last-child { page-break-after: auto; break-after: auto; }

      /* Bildschirm-Ansicht im Popup: graue Fläche + Schatten, damit sofort
         erkennbar ist, wo das A4-Blatt anfängt/endet. Beeinflusst den Druck nicht. */
      @media screen {
        html { background: #d0d0d0; }
        body { padding: 16px 0; }
        .print-page {
          background: #fff;
          margin: 0 auto 16px;
          box-shadow: 0 0 10px rgba(0,0,0,0.25);
          outline: 1px solid #999;
        }
      }
      .page-footer {
        position: absolute;
        left: 8mm;               /* gleicher seitlicher Puffer wie .print-page */
        right: 8mm;
        bottom: 4mm;             /* Footer ein Stück oberhalb der Seitenkante – Druckertreiber-Puffer */
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 8pt;
        color: #555;
        background: #fff;        /* deckt evtl. überlaufende Tabellenränder ab */
        border-top: 1px solid #999;
        padding: 1.2mm 1mm 0 1mm;
      }
      .page-footer .pf-page { font-weight: bold; color: #000; }

      /* Während der Messung dieselbe nutzbare Breite wie später .print-page-
         Inhalt (190mm minus 2x8mm Padding = 174mm), damit offsetHeight korrekt
         ist (sonst zu schmal -> zu hoch im Druck). */
      #content-flow {
        width: 174mm;
        box-sizing: border-box;
        padding: 0;
        margin: 0 auto;
      }
    </style></head><body>
    <div id="content-flow">
      <div id="doc-header" class="doc-header">
        <div class="dh-logo">${dhsLogoSvg}</div>
        <div class="dh-body">
          <h1>Inventur – Zählergebnis</h1>
          <div class="meta">
            <span><strong>Jahr:</strong> ${escapeHtml(jahrLabel)}</span>
            <span><strong>Status:</strong> ${escapeHtml(statusLabel)}</span>
            <span><strong>Paletten:</strong> ${meta.anzahlPaletten}</span>
            <span><strong>Positionen:</strong> ${positionen.length}</span>
            <span><strong>Druck:</strong> ${escapeHtml(druckDatum)}</span>
          </div>
        </div>
      </div>

      <table id="rows-table" class="list">
        <thead id="rows-thead">
          <!--
            Spaltenbreiten in Prozent: skalieren immer mit dem Container und
            können niemals zusammen breiter werden als die nutzbare Inhaltsbreite
            der .print-page (174 mm). mm-Werte sind hier riskant, weil Browser
            bei border-collapse + cell-padding manchmal zusätzlich auflegen.
            5 + 15 + 12 + 29 + 10 + 11 + 8 + 10 = 100 %
          -->
          <tr>
            <th style="width:5%;">Nr.</th>
            <th style="width:15%;">Palette</th>
            <th style="width:12%;">CMMF</th>
            <th style="width:29%;">Artikel</th>
            <th style="width:10%;">Werk</th>
            <th style="width:11%;">Lagerort</th>
            <th style="width:8%;">IST</th>
            <th style="width:10%;">Kontrolle</th>
          </tr>
        </thead>
        <tbody id="rows-tbody">
          ${rows}
        </tbody>
      </table>

      <div id="doc-summary" class="summary">
        <strong>Zusammenfassung:</strong>
        ${meta.anzahlPaletten} Paletten,
        ${positionen.length} Positionen,
        Gesamt-IST: <strong>${gesamtIst}</strong> Stk
      </div>

      ${anchorBlock}
    </div>
    </body></html>`;
  return { html, druckDatum, benutzer };
};

// Pagination im Hauptfenster (nicht im Popup), damit das Druck-HTML
// frei von Inline-Scripts bleibt und nicht von Content-Security-Policy
// blockiert werden kann.
const paginateEndloslisteDoc = (doc, druckDatum, benutzer) => {
  // A4 Hochformat: 297mm Höhe, abzgl. 12mm oben + 18mm unten = 267mm Inhaltsbereich.
  //
  // Hinweis: Zeilen werden im realen Druck regelmäßig 5–10 % höher gerendert
  // als bei der Messung im versteckten Iframe (Font-Hinting, Druckskalierung,
  // Drucker-Treiber). Wir kalkulieren daher sehr konservativ, damit auch bei
  // stärkerer Inflation keine Zeile unter den Footer rutschen oder am Seitenende
  // abgeschnitten werden kann ("Position fehlt"-Effekt).
  const PX_PER_MM = 96 / 25.4;
  const FOOTER_RESERVE_PX = 30 * PX_PER_MM;        // Footer + padding-bottom (22mm) + Toleranz
  const SAFETY_PX = 30 * PX_PER_MM;                 // sehr großzügiger Puffer (Render-Inflation)
  const PAGE_PX = Math.floor(267 * PX_PER_MM) - FOOTER_RESERVE_PX - SAFETY_PX;

  const headerEl  = doc.getElementById('doc-header');
  const theadEl   = doc.getElementById('rows-thead');
  const tbody     = doc.getElementById('rows-tbody');
  const summaryEl = doc.getElementById('doc-summary');
  const sigEls    = Array.prototype.slice.call(doc.querySelectorAll('.sig-block'));
  if (!headerEl || !tbody) return;

  const theadHtml = theadEl.outerHTML;
  const rows      = Array.prototype.slice.call(tbody.children);
  if (rows.length === 0) return;

  const headerH  = headerEl.offsetHeight + 16;
  const theadH   = theadEl.offsetHeight;
  const summaryH = summaryEl.offsetHeight + 8;
  const rowHs    = rows.map((r) => r.offsetHeight);
  const sigHs    = sigEls.map((e) => e.offsetHeight + 8);

  // Erst alle Daten-Zeilen über Seiten verteilen
  const pages = [[]];
  let curH    = headerH + theadH; // Erste Seite trägt zusätzlich den Dokument-Kopf
  for (let i = 0; i < rows.length; i++) {
    if (curH + rowHs[i] > PAGE_PX && pages[pages.length - 1].length > 0) {
      pages.push([]);
      curH = theadH;
    }
    pages[pages.length - 1].push(i);
    curH += rowHs[i];
  }

  // Trailing-Blöcke (Summary + Unterschriften + Hash-Anker) sammeln und auf
  // eigene Seiten verteilen, falls sie nicht mehr auf die letzte Datenseite passen.
  const trailing   = [summaryEl].concat(sigEls);
  const trailingHs = [summaryH].concat(sigHs);

  const placements = pages.map((idxs, p) => {
    let used = (p === 0 ? headerH : 0) + theadH;
    for (let k = 0; k < idxs.length; k++) used += rowHs[idxs[k]];
    return { rowIdxs: idxs, trailIdxs: [], used };
  });
  if (placements.length === 0) placements.push({ rowIdxs: [], trailIdxs: [], used: headerH + theadH });

  let cur = placements[placements.length - 1];
  for (let t = 0; t < trailing.length; t++) {
    if (cur.used + trailingHs[t] > PAGE_PX && cur.trailIdxs.length + cur.rowIdxs.length > 0) {
      cur = { rowIdxs: [], trailIdxs: [], used: 0 };
      placements.push(cur);
    }
    cur.trailIdxs.push(t);
    cur.used += trailingHs[t];
  }
  const totalPages = placements.length;

  const container = doc.createElement('div');
  for (let p = 0; p < totalPages; p++) {
    const plc     = placements[p];
    const isFirst = (p === 0);
    const page    = doc.createElement('div');
    page.className = 'print-page';
    if (isFirst) page.appendChild(headerEl.cloneNode(true));

    if (plc.rowIdxs.length > 0) {
      const tbl = doc.createElement('table');
      tbl.className = 'list';
      tbl.innerHTML = theadHtml + '<tbody></tbody>';
      const newTbody = tbl.querySelector('tbody');
      for (let j = 0; j < plc.rowIdxs.length; j++) {
        newTbody.appendChild(rows[plc.rowIdxs[j]].cloneNode(true));
      }
      page.appendChild(tbl);
    }

    for (let s = 0; s < plc.trailIdxs.length; s++) {
      page.appendChild(trailing[plc.trailIdxs[s]].cloneNode(true));
    }

    const footer = doc.createElement('div');
    footer.className = 'page-footer';
    const left = doc.createElement('span');
    left.textContent = `Inventur-Zählergebnis · Druck: ${druckDatum} · ${benutzer}`;
    const right = doc.createElement('span');
    right.className = 'pf-page';
    right.textContent = `Seite ${p + 1} von ${totalPages}`;
    footer.appendChild(left);
    footer.appendChild(right);
    page.appendChild(footer);

    container.appendChild(page);
  }
  const oldFlow = doc.getElementById('content-flow');
  if (oldFlow) oldFlow.remove();
  doc.body.appendChild(container);
};

// ========================================
// AUDIT-LOG DRUCK (GoBD)
// ========================================

const printAuditLog = async () => {
  if (!auditLog.value.length) {
    toast.warning('Keine Log-Einträge vorhanden');
    return;
  }
  loading.value.auditPrint = true;
  try {
    const eintraege = [...auditLog.value];
    const ids = eintraege.map(e => Number(e.id)).filter(n => !Number.isNaN(n));
    const vonId = ids.length ? Math.min(...ids) : null;
    const bisId = ids.length ? Math.max(...ids) : null;

    try {
      await axios.post(`${API_BASE_URL}/inventur/index.php`, {
        action: 'log_audit_druck',
        anzahl_eintraege: eintraege.length,
        von_id: vonId,
        bis_id: bisId
      });
    } catch (e) {
      console.warn('Audit-Druck-Log fehlgeschlagen', e);
    }

    const html = buildAuditLogHtml(eintraege);
    const win = window.open('', '_blank', 'width=1100,height=900');
    if (!win) {
      toast.error('Popup blockiert. Bitte Popups erlauben.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      try { win.print(); } catch (e) { console.warn(e); }
    }, 400);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Drucken des Audit-Logs');
  } finally {
    loading.value.auditPrint = false;
  }
};

const buildAuditLogHtml = (eintraege) => {
  const druckDatum = new Date().toLocaleString('de-DE');
  const benutzer = (authStore.user?.vorname && authStore.user?.nachname)
    ? `${authStore.user.vorname} ${authStore.user.nachname}`
    : (authStore.user?.loginname || 'Unbekannt');

  const formatTs = (ts) => {
    if (!ts) return '';
    try {
      return new Date(String(ts).replace(' ', 'T')).toLocaleString('de-DE');
    } catch { return String(ts); }
  };

  const shortHash = (h) => {
    const s = String(h || '');
    // Gekuerzte Darstellung mit Anfang UND Ende, damit man auf einen Blick
    // sieht, ob der gedruckte Hash zum Datenbank-/CSV-Wert passt (z.B. fuer
    // Stichprobenpruefung). Bei sehr kurzen Werten wird der Originalwert
    // unveraendert ausgegeben.
    if (s.length <= 20) return s;
    return s.substring(0, 10) + '…' + s.substring(s.length - 10);
  };

  const rows = eintraege.map(e => {
    const name = [e.vorname, e.nachname].filter(Boolean).join(' ').trim()
      || e.username
      || `User #${e.user_id || 0}`;
    return `<tr>
      <td class="num">${escapeHtml(e.id ?? '')}</td>
      <td>${escapeHtml(formatTs(e.created_at))}</td>
      <td>${escapeHtml(e.aktion || '')}</td>
      <td>${escapeHtml(name)}<div class="sub">${escapeHtml(e.username || '')}</div></td>
      <td>${escapeHtml(e.beschreibung || '')}</td>
      <td class="hash">${escapeHtml(shortHash(e.record_hash))}</td>
    </tr>`;
  }).join('');

  // Verifikations-Anhang: vollständige Hashes für Stichprobenprüfung
  const hashRows = eintraege.map(e => `<tr>
      <td class="num">${escapeHtml(e.id ?? '')}</td>
      <td class="hash">${escapeHtml(e.prev_hash || '—')}</td>
      <td class="hash">${escapeHtml(e.record_hash || '')}</td>
    </tr>`).join('');

  return `<html><head><meta charset="UTF-8"><title>Inventur Audit-Log</title>
    <style>
      @page { size: A4 landscape; margin: 10mm 10mm 14mm 10mm; }
      body { font-family: Arial, sans-serif; font-size: 9pt; margin: 0; padding: 0; color: #000; }
      .doc-header {
        display: flex;
        align-items: center;
        gap: 8mm;
        border-bottom: 2px solid #000;
        padding-bottom: 3mm;
        margin-bottom: 3mm;
      }
      .doc-header .dh-logo { flex: 0 0 36mm; }
      .doc-header .dh-logo svg { display: block; width: 100%; height: auto; }
      .doc-header .dh-body { flex: 1; }
      .doc-header h1 { font-size: 14pt; margin: 0 0 1.5mm 0; }
      .doc-header .meta { font-size: 8.5pt; }
      .doc-header .meta span { margin-right: 5mm; }
      table.list { width: 100%; border-collapse: collapse; }
      table.list thead { display: table-header-group; }
      table.list th, table.list td {
        border: 1px solid #444;
        padding: 1.2mm 1.6mm;
        font-size: 8.5pt;
        vertical-align: top;
      }
      table.list th { background: #e0e0e0; text-align: left; }
      table.list td.num { text-align: right; font-variant-numeric: tabular-nums; }
      table.list td.hash {
        font-family: 'Courier New', monospace;
        font-size: 8pt;
        white-space: nowrap;
      }
      table.hash-table td.hash {
        white-space: normal;
        word-break: break-all;
        font-size: 7.5pt;
      }
      code {
        font-family: 'Courier New', monospace;
        font-size: 8.5pt;
        background: #eee;
        padding: 0 2px;
      }
      .sub { font-size: 7.5pt; color: #666; }
      tr { page-break-inside: avoid; }
      .gobd-note {
        margin-top: 4mm;
        padding: 2mm 3mm;
        border: 1px solid #000;
        background: #f7f7f7;
        font-size: 8.5pt;
      }
      .hash-section-title {
        font-size: 11pt;
        margin: 6mm 0 2mm 0;
      }
      .signatures {
        display: flex;
        justify-content: space-between;
        gap: 8mm;
        margin-top: 8mm;
        page-break-inside: avoid;
      }
      .sig { width: 48%; }
      .sig-lbl { font-weight: bold; margin-bottom: 12mm; }
      .sig-line { border-bottom: 1px solid #000; min-height: 6mm; padding: 0 2mm; }
      .sig-cap { font-size: 8pt; color: #555; text-align: center; margin-top: 1mm; }

      /* Pagination: jede print-page entspricht genau einer A4-Seite (Querformat) */
      .print-page {
        position: relative;
        box-sizing: border-box;
        width: 277mm;            /* A4 Querformat Breite (297) minus 2x10mm */
        /* Hoehe: A4 Querformat (210) abzgl. 10mm oben + 14mm unten = 186mm
           Inhaltsbereich. Etwas reduzieren, damit Browser-Rundungs- und
           Skalierungsfehler nicht eine zusaetzliche Leerseite zwischen
           je zwei bedruckte Blaetter schieben. */
        height: 183mm;
        overflow: hidden;
        padding-bottom: 12mm;    /* Platz fuer Fusszeile */
        page-break-after: always;
      }
      .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      .page-footer {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        display: flex; justify-content: space-between; align-items: center;
        font-size: 8pt; color: #555;
        border-top: 1px solid #999;
        padding: 1.2mm 1mm 0 1mm;
      }
      .page-footer .pf-page { font-weight: bold; color: #000; }

      /* Waehrend der Messung dieselbe Breite wie spaeter .print-page */
      #content-flow {
        width: 277mm;
        box-sizing: border-box;
        padding: 0;
        margin: 0 auto;
      }
    </style></head><body>
    <div id="content-flow">
      <div id="doc-header" class="doc-header">
        <div class="dh-logo">${dhsLogoSvg}</div>
        <div class="dh-body">
          <h1>Inventur – Audit-Log (GoBD)</h1>
          <div class="meta">
            <span><strong>Einträge:</strong> ${eintraege.length}</span>
            <span><strong>Druck:</strong> ${escapeHtml(druckDatum)}</span>
            <span><strong>Drucker:</strong> ${escapeHtml(benutzer)}</span>
          </div>
        </div>
      </div>

      <table id="rows-table" class="list">
        <thead id="rows-thead">
          <tr>
            <th style="width:12mm;">ID</th>
            <th style="width:32mm;">Zeitstempel</th>
            <th style="width:34mm;">Aktion</th>
            <th style="width:40mm;">Benutzer</th>
            <th>Beschreibung</th>
            <th style="width:34mm;">Hash (gekürzt)</th>
          </tr>
        </thead>
        <tbody id="rows-tbody">
          ${rows}
        </tbody>
      </table>

      <div id="doc-gobd" class="gobd-note">
        <strong>GoBD-Hinweis:</strong> Jeder Eintrag wird unveränderbar in einer
        Hash-Chain (SHA-256) gespeichert: <code>record_hash = SHA256(prev_hash | payload)</code>.
        Eine nachträgliche Veränderung einzelner Einträge bricht die Kette und ist damit
        revisionssicher erkennbar. Die hier abgedruckten Hash-Werte erlauben eine
        stichprobenartige Verifikation gegen den Datenbank-Bestand.
      </div>

      <h2 id="hash-title" class="hash-section-title">Anhang: Hash-Chain (vollständige Werte)</h2>
      <table id="hash-table" class="list hash-table">
        <thead id="hash-thead">
          <tr>
            <th style="width:12mm;">ID</th>
            <th>prev_hash</th>
            <th>record_hash</th>
          </tr>
        </thead>
        <tbody id="hash-tbody">
          ${hashRows}
        </tbody>
      </table>

      <div id="doc-signatures" class="signatures">
        <div class="sig">
          <div class="sig-lbl">Erfassung</div>
          <div class="sig-line"></div>
          <div class="sig-cap">Name, Unterschrift / Datum</div>
        </div>
        <div class="sig">
          <div class="sig-lbl">Kontrolle</div>
          <div class="sig-line"></div>
          <div class="sig-cap">Name, Unterschrift / Datum</div>
        </div>
      </div>
    </div>

    <script>
      (function() {
        var DRUCK = ${JSON.stringify(druckDatum)};
        var DRUCKER = ${JSON.stringify(benutzer)};

        function paginate() {
          // A4 Querformat: 210mm Hoehe, abzgl. 10mm oben + 14mm unten = 186mm Inhaltsbereich.
          // Wir rechnen mit 183mm (siehe CSS) und ziehen Footer + Sicherheits-Puffer ab.
          var PX_PER_MM = 96 / 25.4;
          var FOOTER_RESERVE_PX = 14 * PX_PER_MM;
          var SAFETY_PX = 10 * PX_PER_MM;
          var PAGE_PX = Math.floor(183 * PX_PER_MM) - FOOTER_RESERVE_PX - SAFETY_PX;

          var headerEl    = document.getElementById('doc-header');
          var rowsThead   = document.getElementById('rows-thead');
          var rowsTbody   = document.getElementById('rows-tbody');
          var gobdEl      = document.getElementById('doc-gobd');
          var hashTitleEl = document.getElementById('hash-title');
          var hashThead   = document.getElementById('hash-thead');
          var hashTbody   = document.getElementById('hash-tbody');
          var sigEl       = document.getElementById('doc-signatures');
          if (!headerEl || !rowsTbody) return;

          var headerH = headerEl.offsetHeight + 16;

          // Generische Tabellen-Pagination: verteilt Zeilen ueber Seiten,
          // beginnt jede neue Seite mit dem THEAD.
          function paginateTable(theadEl, tbodyEl, firstPageStartH) {
            var theadHtml = theadEl.outerHTML;
            var theadH    = theadEl.offsetHeight;
            var rows      = Array.prototype.slice.call(tbodyEl.children);
            var rowHs     = rows.map(function(r) { return r.offsetHeight; });

            // pages[i] = { rowIdxs: [...] }
            var pages = [{ rowIdxs: [] }];
            var curH  = firstPageStartH + theadH;
            for (var i = 0; i < rows.length; i++) {
              if (curH + rowHs[i] > PAGE_PX && pages[pages.length - 1].rowIdxs.length > 0) {
                pages.push({ rowIdxs: [] });
                curH = theadH;
              }
              pages[pages.length - 1].rowIdxs.push(i);
              curH += rowHs[i];
            }
            return {
              pages: pages,
              rows: rows,
              theadHtml: theadHtml,
              theadH: theadH,
              lastUsedH: curH,
              tableClassName: tbodyEl.parentElement ? tbodyEl.parentElement.className : 'list'
            };
          }

          // 1) Haupt-Tabelle paginieren (Header zaehlt nur auf Seite 1).
          var mainP = paginateTable(rowsThead, rowsTbody, headerH);

          // Pages bauen wir als Arbeits-Liste mit DOM-Inhalten auf, die spaeter
          // sequenziell ueber print-page Container ausgegeben werden.
          // Jede Page-Definition kann mehrere "Bloecke" enthalten:
          //   - { type: 'table-part', cls, theadHtml, rows: [DOMs] }
          //   - { type: 'block', el: DOM }
          var pageBuilds = [];
          for (var p = 0; p < mainP.pages.length; p++) {
            var part = {
              type: 'table-part',
              cls: mainP.tableClassName,
              theadHtml: mainP.theadHtml,
              rows: mainP.pages[p].rowIdxs.map(function(i) { return mainP.rows[i].cloneNode(true); })
            };
            pageBuilds.push({ isFirst: p === 0, blocks: [part], used: (p === 0 ? headerH : 0) + mainP.theadH });
            // Roh-used aus paginateTable nicht direkt verfuegbar pro Seite;
            // wir rechnen unten neu fuer die letzte Seite.
          }
          if (pageBuilds.length === 0) {
            pageBuilds.push({ isFirst: true, blocks: [], used: headerH });
          }

          // Tatsaechlichen Verbrauch der LETZTEN Seite neu berechnen
          (function recalcLast() {
            var last = pageBuilds[pageBuilds.length - 1];
            var used = (last.isFirst ? headerH : 0);
            last.blocks.forEach(function(b) {
              if (b.type === 'table-part') {
                used += mainP.theadH;
                b.rows.forEach(function(r) { used += r.offsetHeight || 0; });
              } else if (b.el) {
                used += (b.el.offsetHeight || 0) + 8;
              }
            });
            last.used = used;
          })();

          // 2) Trailing-Bloecke: GoBD-Hinweis, Hash-Anhang (Titel + Tabelle), Unterschriften.
          function appendBlock(el) {
            var h = el.offsetHeight + 8;
            var cur = pageBuilds[pageBuilds.length - 1];
            if (cur.used + h > PAGE_PX && cur.blocks.length > 0) {
              cur = { isFirst: false, blocks: [], used: 0 };
              pageBuilds.push(cur);
            }
            cur.blocks.push({ type: 'block', el: el });
            cur.used += h;
          }

          appendBlock(gobdEl);
          appendBlock(hashTitleEl);

          // Hash-Tabelle erneut paginieren, dabei aktuelle Restplatz-Hoehe auf der
          // letzten bereits angefangenen Seite beruecksichtigen.
          var hashStartUsed = pageBuilds[pageBuilds.length - 1].used;
          var hashP = paginateTable(hashThead, hashTbody, hashStartUsed);
          for (var hp = 0; hp < hashP.pages.length; hp++) {
            var part2 = {
              type: 'table-part',
              cls: hashP.tableClassName,
              theadHtml: hashP.theadHtml,
              rows: hashP.pages[hp].rowIdxs.map(function(i) { return hashP.rows[i].cloneNode(true); })
            };
            if (hp === 0) {
              var cur2 = pageBuilds[pageBuilds.length - 1];
              cur2.blocks.push(part2);
              // used: Tabellen-Header + Zeilen
              cur2.used += hashP.theadH;
              part2.rows.forEach(function(r) { cur2.used += r.offsetHeight || 0; });
            } else {
              pageBuilds.push({
                isFirst: false,
                blocks: [part2],
                used: hashP.theadH + part2.rows.reduce(function(s, r) { return s + (r.offsetHeight || 0); }, 0)
              });
            }
          }

          appendBlock(sigEl);

          // 3) Seiten zusammenbauen und mit Footer + "Seite x von N" versehen.
          var totalPages = pageBuilds.length;
          var container = document.createElement('div');
          for (var pp = 0; pp < totalPages; pp++) {
            var pb   = pageBuilds[pp];
            var page = document.createElement('div');
            page.className = 'print-page';
            if (pb.isFirst) page.appendChild(headerEl.cloneNode(true));

            pb.blocks.forEach(function(blk) {
              if (blk.type === 'table-part') {
                var tbl = document.createElement('table');
                tbl.className = blk.cls;
                tbl.innerHTML = blk.theadHtml + '<tbody></tbody>';
                var newTbody = tbl.querySelector('tbody');
                blk.rows.forEach(function(r) { newTbody.appendChild(r); });
                page.appendChild(tbl);
              } else if (blk.el) {
                page.appendChild(blk.el.cloneNode(true));
              }
            });

            var footer = document.createElement('div');
            footer.className = 'page-footer';
            footer.innerHTML =
              '<span>Inventur Audit-Log (GoBD) · Druck: ' + DRUCK + ' · ' + DRUCKER + '</span>' +
              '<span class="pf-page">Seite ' + (pp + 1) + ' von ' + totalPages + '</span>';
            page.appendChild(footer);

            container.appendChild(page);
          }
          document.getElementById('content-flow').remove();
          document.body.appendChild(container);
        }

        function start() {
          try { paginate(); } catch (e) { console.error('Pagination fehlgeschlagen', e); }
          setTimeout(function() { try { window.print(); } catch (e) { console.warn(e); } }, 150);
        }

        if (document.readyState === 'complete') start();
        else window.addEventListener('load', function() { setTimeout(start, 50); });
      })();
    ${'<'}/script>
    </body></html>`;
};

// ========================================
// LIFECYCLE
// ========================================

onMounted(() => {
  fetchArchivList();
  fetchEinzelliste();
  fetchKompaktliste();
  fetchLog();
  fetchPaletten();
  fetchKatalogStats();
  fetchAktuelleMeta();
  // Artikel-Katalog wird erst beim Oeffnen des Buttons "Artikel-Katalog oeffnen" geladen.

  startOfficeLivePoll();
  document.addEventListener('visibilitychange', onOfficeVisibilityChange);

  // Sofort-Update, wenn im selben Browser (anderer Tab) eine Palette
  // abgeschlossen / wieder geöffnet / gescannt wurde.
  if (typeof BroadcastChannel !== 'undefined') {
    inventurBroadcast = new BroadcastChannel('inventur-events');
    inventurBroadcast.onmessage = (ev) => {
      const type = ev?.data?.type;
      if (
        type === 'palette_abgeschlossen' ||
        type === 'palette_reopened' ||
        type === 'palette_changed' ||
        type === 'scan'
      ) {
        refreshOfficeLive();
      }
    };
  }
});

onUnmounted(() => {
  stopOfficeLivePoll();
  document.removeEventListener('visibilitychange', onOfficeVisibilityChange);
  if (inventurBroadcast) {
    inventurBroadcast.close();
    inventurBroadcast = null;
  }
});

// Stats für die EAN-Lookup-Tabelle beim Aufrufen des Tabs (lazy)
watch(activeTab, (val) => {
  if (val === 'ean-lookup') {
    fetchEanLookupStats();
    fetchKatalogStats();
  } else if (val === 'import') {
    fetchKatalogStats();
    // Katalog-Liste nur nachladen, falls sie bereits explizit eingeblendet ist.
    if (katalogVerwaltungVisible.value) {
      loadKatalogVerwaltung();
    }
  } else if (
    val === 'einzelliste' ||
    val === 'kompaktliste' ||
    val === 'paletten' ||
    val === 'log'
  ) {
    // Beim Tab-Wechsel frische Daten – sonst sieht man ggf. den Stand vom Seitenaufruf.
    refreshOfficeLive();
  }
});

// Beim Wechsel der Archiv-Auswahl alle Daten neu laden
watch(selectedArchivId, () => {
  // Drilldown-Cache leeren
  kompaktArtikelPaletten.value = {};
  fetchEinzelliste();
  fetchKompaktliste();
  fetchLog();
  fetchPaletten();
  fetchKorrekturen();
  startOfficeLivePoll();
});
</script>

<style scoped>
.inventur-office-container {
  padding: 20px;
}

.palette-row-erfassung {
  background-color: rgba(33, 150, 243, 0.12);
  font-style: italic;
}
.palette-row-erfassung td {
  border-left: 3px solid rgb(33, 150, 243);
}
.palette-row-erfassung td:not(:first-child) {
  border-left: none;
}

.audit-hash {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  word-break: break-all;
  white-space: normal;
  display: inline-block;
  max-width: 280px;
  line-height: 1.25;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.info-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

/* Plausibilitäts-Vorschau */
.plausi-card {
  background: rgba(255, 255, 255, 0.02);
}
.plausi-tile {
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  height: 100%;
}
.plausi-tile-ok      { background: rgba( 76, 175,  80, 0.10); border-color: rgba( 76, 175,  80, 0.35); }
.plausi-tile-warn    { background: rgba(255, 152,   0, 0.10); border-color: rgba(255, 152,   0, 0.35); }
.plausi-tile-danger  { background: rgba(244,  67,  54, 0.12); border-color: rgba(244,  67,  54, 0.40); }
.plausi-tile-info    { background: rgba( 33, 150, 243, 0.10); border-color: rgba( 33, 150, 243, 0.35); }
.plausi-tile-neutral { background: rgba(158, 158, 158, 0.08); border-color: rgba(158, 158, 158, 0.30); }
.plausi-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  opacity: 0.75;
}
.plausi-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  margin: 4px 0 4px;
}
.plausi-hint {
  font-size: 0.78rem;
  opacity: 0.85;
  line-height: 1.25;
}
</style>
